import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "./AuthContext";
import ConsoleShell, { Breadcrumb } from "./ConsoleShell";
import {
  Sparkles, LayoutDashboard, Library, Radio, Network, LayoutGrid, Users, FileText, ListChecks, ShieldCheck,
  CheckCircle2, MinusCircle, CircleDot, ArrowRight, ChevronRight, ChevronDown, Check,
  Download, FileDown, Filter, Sparkle, Upload, X, Play, Loader2, FileUp, Zap, AlertTriangle, ExternalLink, RefreshCw, Trash2,
} from "lucide-react";
import {
  EngagementBriefReport, FunctionViewReport,
  downloadPdf, slugify,
} from "./pdf/ReportPdf";
import { StreamBriefReport } from "./pdf/StreamBriefPdf";
import { PatientAdvisoryBriefReport, KolAdvisoryBriefReport } from "./pdf/PabReportPdf";
import { useConsoleMode, MODE_CLIENT } from "./useConsoleMode";
import { ABBREVIATIONS, ABBR_REGEX } from "./abbreviations";
import { EvidenceGradeChip, EvidenceLegend } from "./evidenceTaxonomy";
import { EvidenceIntelligenceSummary } from "./EvidenceIntelligenceSummary";
import { useAuth } from "./AuthContext";
import { DemoGuide } from "./demo/DemoGuide";

/**
 * AutoAbbr — decorates a text blob with instant hover popups around any
 * short forms found in /app/frontend/src/console/abbreviations.js.
 *
 *   <AutoAbbr text={f.interpretation} />
 *
 * Each matched token renders as a small `<span class="group">` carrying
 * the dotted underline plus a Tailwind-positioned popup that appears on
 * hover (zero delay, no native <abbr> sluggishness). The popup is a
 * dark capsule positioned above the token with a small arrow.
 */
function AutoAbbr({ text, className = "" }) {
  if (text == null || text === "") return null;
  const str = String(text);
  ABBR_REGEX.lastIndex = 0;
  const out = [];
  let last = 0;
  let key = 0;
  let m;
  while ((m = ABBR_REGEX.exec(str)) !== null) {
    if (m.index > last) out.push(<span key={`t-${key++}`}>{str.slice(last, m.index)}</span>);
    const token = m[1];
    const full = ABBREVIATIONS[token];
    out.push(
      <span
        key={`a-${key++}`}
        className="relative inline-block group cursor-help"
        data-abbr={token}
      >
        <span className="underline decoration-dotted decoration-[#A09680] underline-offset-[3px] group-hover:decoration-[#C84C21] group-hover:text-[#C84C21] transition-colors">
          {token}
        </span>
        {/* Hover popup — instant, no native-tooltip delay. Positioned
            above the token; flips below if there's not enough room at the
            top (handled with absolute positioning + a small CSS arrow). */}
        <span
          role="tooltip"
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-50 hidden group-hover:block whitespace-nowrap bg-[#0F1825] text-white text-[11px] font-normal leading-tight px-2.5 py-1.5 shadow-lg max-w-[280px] normal-case tracking-normal"
          style={{ whiteSpace: "normal", minWidth: "120px" }}
        >
          <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#FAF7F1]/70 mr-1.5">{token}</span>
          {full}
          <span
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
            style={{
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #0F1825",
            }}
          />
        </span>
      </span>
    );
    last = m.index + token.length;
  }
  if (last < str.length) out.push(<span key={`t-${key++}`}>{str.slice(last)}</span>);
  return <span className={className}>{out}</span>;
}

/* ============================================================
 * Decision-question categories (kept in UI — 1:1 by index with seed)
 * ============================================================ */
const DECISION_CATEGORIES = [
  "Access",
  "Patient Preference",
  "Medical Narrative",
  "Lifecycle Strategy",
  "Evidence Planning",
];

/* ============================================================
 * Tabs
 * ============================================================ */
const TABS = [
  { key: "overview",      label: "Engagement Brief",     hint: "Scope · decision questions · teams",                 Icon: LayoutDashboard },
  { key: "sources",       label: "Source Library",       hint: "Upload source PDFs · AI signal extraction",          Icon: Library },
  { key: "signals",       label: "Signal Intake",        hint: "Five patient evidence streams",                       Icon: Radio },
  { key: "concept-map",   label: "Concept Map",          hint: "Signal → Concept → Domain → Evidence implication",   Icon: Network },
  { key: "triangulation", label: "Triangulation View",   hint: "Where streams converge or diverge",                  Icon: Network },
  { key: "functions",     label: "Team Recommendations", hint: "Recommendations re-cast per team",                    Icon: Users },
  { key: "pab",           label: "Patient Advisory Brief", hint: "Live patient advisory board discussion guide",      Icon: ListChecks },
  { key: "kol",           label: "KOL Advisory Brief",   hint: "Pre-launch KOL board · positioning · adoption · advocacy", Icon: ListChecks },
];

// Client Workspace — 9 client-facing modules in logical engagement order.
// Source Library Status was folded into the Engagement Brief (the
// 'Where your evidence comes from' grid lives inline there) so the tab
// itself is removed from the client nav. Signal Intake is exposed to the
// client so the individual patient quotes per stream remain accessible
// behind the 5-stream summary on the Concept Map.
const CLIENT_TABS = [
  { key: "overview",      label: "Engagement Brief",                   hint: "Asset · scope · decision questions",         Icon: LayoutDashboard },
  { key: "signals",       label: "Patient Signals",                    hint: "Individual patient signals by stream",       Icon: Radio },
  { key: "concept-map",   label: "Patient Experience Concept Map",     hint: "Signal → Concept → Domain → Implication",    Icon: Network },
  { key: "triangulation", label: "Triangulation View",                 hint: "Where streams converge or diverge",          Icon: Network },
  { key: "functions",     label: "Team Recommendations",               hint: "Recommendations re-cast per team",           Icon: Users },
  { key: "pab",           label: "Patient Advisory Brief",             hint: "Live patient advisory board discussion guide", Icon: ListChecks },
  { key: "kol",           label: "KOL Advisory Brief",                 hint: "Pre-launch KOL board · positioning · adoption · advocacy", Icon: ListChecks },
];
const CLIENT_TAB_KEYS = new Set(CLIENT_TABS.map((t) => t.key));

/* ============================================================
 * Action status remap (broader, client-friendly set)
 * ============================================================ */
const ACTION_STATUS_MAP = {
  "Scoped": "Recommended",
  "In Progress": "In Progress",
  "Acted On": "Implemented",
};
const ACTION_STATUSES = ["Recommended", "Accepted", "In Progress", "Implemented", "Monitoring", "Closed"];

/* ============================================================
 * Function master list — used by Function Views
 * ============================================================ */
const FUNCTIONS = [
  { key: "Medical Affairs",      lede: "Patient-narrative priorities for MSL discussions, scientific exchange, and congress activity." },
  { key: "HEOR",                 lede: "Patient-experience inputs into value modelling, PRO / COA design, and RWE follow-up." },
  { key: "Market Access",        lede: "Access narrative grounded in patient-relevant value drivers and country-level adoption signals." },
  { key: "Regulatory Affairs",   lede: "PFDD-informed evidence shaping for label-relevant patient experience and combination indication strategy." },
  { key: "Commercial / Brand",   lede: "Messaging anchored in patient reality — autonomy, honest framing, supportive-care positioning." },
  { key: "Commercial",           lede: "Messaging anchored in patient reality — autonomy, honest framing, supportive-care positioning." },
  { key: "Patient Engagement",   lede: "Advocacy alignment, initiation-stage education, and patient-led content co-creation." },
  { key: "Leadership",           lede: "Executive synthesis: patient-voice shifts, top evidence gaps, and the recommended cross-functional actions sourced from the triangulated findings." },
];

/* ============================================================
 * Main component
 * ============================================================ */
export default function ConsoleEngagementWorkspace() {
  const { id } = useParams();
  const [eng, setEng] = useState(null);
  const [mode, setMode] = useConsoleMode();
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get("demo") === "1";
  // A guided demo always runs in the Client Workspace view (what a client would see).
  useEffect(() => { if (demoMode && mode !== MODE_CLIENT) setMode(MODE_CLIENT); }, [demoMode, mode, setMode]);
  const isClient = mode === MODE_CLIENT || demoMode;
  // Both modes land on the Engagement Brief (first sidebar entry).
  const [active, setActive] = useState("overview");
  // scrollTo lets one tab deep-link to a specific row in another tab — e.g.
  // the Triangulation View FindingCard links to its paired row on
  // Team Recommendations. Receiver clears the value after scrolling.
  const [scrollTo, setScrollTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const viewTabs = isClient ? CLIENT_TABS : TABS;
  const nav = useNavigate();
  const { user: currentUser } = useAuth();

  const refreshEng = async () => {
    try {
      const { data } = await axios.get(`${API}/console/engagements/${id}`);
      setEng(data);
    } catch { /* keep the current copy */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/console/engagements/${id}`);
        setEng(data);
      } catch (e) {
        nav(demoMode ? "/demo" : "/console/engagements", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  // Re-fetch the engagement whenever the tab changes so briefs written by a
  // background job (triangulation → PAB / KOL / domain actions) are current.
  useEffect(() => {
    if (!loading) refreshEng();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Ensure `active` is valid for the current mode's tab list. When the user
  // switches into Client Briefing View from, say, the Source Library tab, we
  // need to fall back to a tab that exists.
  useEffect(() => {
    if (isClient && !CLIENT_TAB_KEYS.has(active)) {
      setActive("overview");
    }
  }, [isClient, active]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F1]">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#5C6470]">Loading workspace…</div>
      </div>
    );
  }
  if (!eng) return null;
  const isAdminRole = ((currentUser?.role || "").toLowerCase() === "admin"
                    || (currentUser?.role || "").toLowerCase() === "operator");

  const sidebar = (
    <nav className="py-6" data-testid="ws-sidebar">
      <div className="px-6 mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mb-1.5">Engagement</div>
        <div className="font-serif text-xl text-[#0F1825] leading-tight">{eng.brand}</div>
        <div className="text-[12px] text-[#5C6470] mt-0.5">{eng.client_label}</div>
        <div className="mt-3 pt-3 border-t border-[#F0EBDC]">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#C84C21]">Operated by</div>
          <div className="text-[12px] text-[#0F1825] mt-0.5">Voice2Evidence™ methodology team</div>
        </div>
      </div>
      <div className="border-t border-[#E8E1D2]" />
      <ul className="py-2">
        {viewTabs.map((t) => {
          const isActive = active === t.key;
          const Icon = t.Icon;
          return (
            <li key={t.key}>
              <button
                onClick={() => setActive(t.key)}
                className={`w-full text-left flex items-start gap-3 px-6 py-3 border-l-2 transition-colors ${
                  isActive
                    ? "bg-[#FAF7F1] border-[#C84C21] text-[#0F1825]"
                    : "border-transparent text-[#5C6470] hover:bg-[#FAF7F1]/60 hover:text-[#0F1825]"
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon size={18} className={`mt-0.5 shrink-0 ${isActive ? "text-[#C84C21]" : "text-[#A09680]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium leading-snug flex items-center gap-2">
                    {t.label}
                  </div>
                  <div className="text-[11.5px] text-[#A09680] leading-snug mt-0.5">{t.hint}</div>
                </div>
                {isActive && <ChevronRight size={14} className="text-[#C84C21] mt-1 shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="border-t border-[#E8E1D2] mt-2 px-6 py-4">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-1.5">Engagement model</div>
        <p className="text-[11.5px] text-[#5C6470] leading-relaxed">
          {isClient
            ? "This workspace is the client-facing view of a Voice2Evidence™ services engagement. The evidence work behind it is operated by our methodology team — it is not a self-serve software subscription."
            : "The workspace is the delivery interface for a Voice2Evidence™ services engagement — operated by our methodology team. It is not a self-serve software subscription."}
        </p>
      </div>
    </nav>
  );

  const breadcrumb = (
    <Breadcrumb
      items={[
        demoMode ? { label: "Client demo", to: "/demo" } : { label: "Engagements", to: "/console/engagements" },
        { label: eng.client_label },
        { label: eng.brand },
      ]}
    />
  );

  const isLive = eng?.kind === "live";
  return (
    <ConsoleShell breadcrumb={breadcrumb} sidebar={sidebar} hideIllustrativeRibbon={isLive} liveEngagement={isLive}>
      <div className="px-6 lg:px-10 py-10 max-w-6xl" data-testid="page-engagement-workspace">
        {active === "overview" && <Overview id={id} eng={eng} setActive={setActive} isClient={isClient} />}
        {active === "sources" && <SourceLibraryTab id={id} eng={eng} setActive={setActive} isClient={isClient} />}
        {active === "signals" && <SignalsTab id={id} eng={eng} setActive={setActive} isClient={isClient} />}
        {active === "concept-map" && <ConceptMapTab id={id} isClient={isClient} />}
        {active === "triangulation" && <TriangulationTab id={id} setActive={setActive} setScrollTo={setScrollTo} isClient={isClient} onEngChanged={refreshEng} />}
        {active === "functions" && <FunctionViewsTab id={id} eng={eng} isClient={isClient} scrollTo={scrollTo} setScrollTo={setScrollTo} />}
        {active === "pab" && <PatientAdvisoryBriefTab id={id} eng={eng} isClient={isClient} />}
        {active === "kol" && <KolAdvisoryBriefTab id={id} eng={eng} isClient={isClient} />}
      </div>
      {demoMode && <DemoGuide eng={eng} setActive={setActive} />}
    </ConsoleShell>
  );
}

/* ============================================================
 * Shared atoms
 * ============================================================ */
function TabHeader({ eyebrow, title, lede, why }) {
  return (
    <header className="mb-8" data-testid="tab-header">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{eyebrow}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[#0F1825] mt-2 leading-tight tracking-tight">{title}</h1>
      {lede && <p className="mt-4 text-[#5C6470] text-[15.5px] leading-relaxed max-w-3xl">{lede}</p>}
      {why && (
        <div className="mt-5 inline-flex items-start gap-3 bg-white border border-[#E8E1D2] px-4 py-3 max-w-3xl">
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#C84C21] mt-1 shrink-0">Why it matters</span>
          <span className="text-[13.5px] text-[#0F1825] leading-relaxed">{why}</span>
        </div>
      )}
    </header>
  );
}

function ClientOutput({ children }) {
  return (
    <div className="mt-5 mb-8 flex items-start gap-3 bg-[#F0F4F1] border-l-2 border-[#3F6D55] px-4 py-3" data-testid="client-output">
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#3F6D55] mt-0.5 shrink-0 whitespace-nowrap">Client output</span>
      <span className="text-[13.5px] text-[#0F1825] leading-relaxed">{children}</span>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-1.5">{label}</div>
      <div className="text-[#0F1825] text-[14.5px] leading-snug">{value}</div>
    </div>
  );
}
function Label({ children }) { return <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-1">{children}</div>; }
function Value({ children }) { return <div className="text-[#0F1825] text-[14.5px]">{children}</div>; }
function SectionTitle({ children }) {
  return <h3 className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{children}</h3>;
}

function NextStep({ n, label, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-[#FAF7F1] hover:bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 p-5 transition-colors group"
    >
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">Stage {n}</span>
      </div>
      <div className="font-serif text-[1.15rem] text-[#0F1825] leading-snug">{label}</div>
      <div className="mt-2 text-[13px] text-[#5C6470] leading-relaxed">{desc}</div>
      <div className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] opacity-70 group-hover:opacity-100">
        Open <ArrowRight size={12} />
      </div>
    </button>
  );
}

/* ======================== ENGAGEMENT FLOW PRIMER ========================
 * Briefs the client on how the workspace is organised — the journey
 * arc and the recurring cycle of "deliver → act → close → measure
 * maturity → re-scope". Static (non-clickable) cards so this reads as a
 * briefing, not a navigation UI. Sits in the left column of the
 * Engagement Brief, under the Objective + Scope blocks, filling the
 * column whitespace.
 * ======================================================================= */
const FLOW_STAGES = [
  { key: "signals",       label: "Patient Signals",                 phase: "Evidence",   blurb: "Individual patient signals — raw voice, by stream." },
  { key: "concept-map",   label: "Patient Experience Concept Map",  phase: "Evidence",   blurb: "Signals become concepts and domains." },
  { key: "triangulation", label: "Triangulation View",              phase: "Synthesis",  blurb: "One recommendation per theme, drawn across the five streams." },
  { key: "functions",     label: "Team Recommendations",            phase: "Translation",blurb: "Recommendations re-cast for each team that has to act." },
];

function EngagementFlowPrimer() {
  return (
    <div className="bg-white border border-[#E8E1D2] p-6" data-testid="engagement-flow-primer">
      <SectionTitle>How this engagement is organised</SectionTitle>
      <p className="text-[13px] text-[#5C6470] mt-2 leading-relaxed">
        Patient voice is captured, structured, interpreted, and translated into team-specific recommendations — every recommendation closes the loop inside this engagement. The engagement is delivered as a complete deliverable; no thread is left as &quot;come back next cycle.&quot;
      </p>
      <ol className="mt-5 space-y-2.5">
        {FLOW_STAGES.map((s, i) => (
          <li
            key={s.key}
            className="flex gap-3 items-start"
            data-testid={`flow-step-${s.key}`}
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] shrink-0 w-[58px] pt-0.5">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="text-[13.5px] text-[#0F1825] font-medium leading-snug">{s.label}</span>
                <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]">{s.phase}</span>
              </div>
              <div className="mt-0.5 text-[12px] text-[#5C6470] leading-snug">{s.blurb}</div>
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-5 pt-4 border-t border-[#F0EBDC] text-[11.5px] text-[#A09680] leading-relaxed">
        Each tab carries a distinct role. Triangulation View produces the recommendations; Team Recommendations re-casts them per owning team — one theme, one recommendation, one closed loop.
      </p>
    </div>
  );
}

/* =========================== ENGAGEMENT BRIEF (was Overview) =========================== */
function Overview({ id, eng, setActive, isClient }) {
  const { user: currentUser } = useAuth();
  const isAdminRole = ((currentUser?.role || "").toLowerCase() === "admin"
                    || (currentUser?.role || "").toLowerCase() === "operator");
  const [signalCount, setSignalCount] = useState(null);
  useEffect(() => {
    axios.get(`${API}/console/engagements/${id}/signals`)
      .then((r) => setSignalCount(Object.values(r.data?.counts_by_stream || {}).reduce((a, b) => a + b, 0)))
      .catch(() => setSignalCount(0));
  }, [id]);
  return (
    <div data-testid="overview-tab">
      {/* Asset card */}
      <section className="bg-white border border-[#E8E1D2] p-7">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-serif text-3xl text-[#0F1825]">{eng.brand}</h2>
          <span className="text-[#A09680]">·</span>
          <span className="text-[#5C6470] text-[15px]"><AutoAbbr text={eng.asset} /></span>
        </div>
        <div className="mt-1 text-[#5C6470] text-[14px]"><AutoAbbr text={eng.mechanism} /></div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
          <Field label="Therapeutic area" value={eng.ta} />
          <Field label="Indication" value={eng.indication} />
          <Field label="Lifecycle phase" value={eng.lifecycle_phase} />
          <Field label="Client" value={eng.client_label} />
        </div>
      </section>

      {/* Engagement objective + primary teams + geography */}
      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <div className="bg-white border border-[#E8E1D2] p-7">
            <SectionTitle>Engagement objective</SectionTitle>
            <p className="mt-3 text-[#0F1825] leading-relaxed text-[15px]" data-testid="engagement-objective">
              <AutoAbbr text={eng.engagement_objective || `Translate patient voice on ${eng.indication || "this asset"} into evidence-ready strategy for Medical Affairs, HEOR, Market Access, and Patient Engagement.`} />
            </p>
            <div className="mt-6 pt-5 border-t border-[#F0EBDC]">
              <SectionTitle>Engagement scope</SectionTitle>
              <p className="mt-3 text-[#0F1825] leading-relaxed text-[14.5px]"><AutoAbbr text={eng.scope} /></p>
            </div>
          </div>
          {/* How this engagement is organised — fills the left-column
              whitespace beneath the objective + scope block. Client-only;
              operator has its own workflow nav at the bottom of the page. */}
          {isClient && <EngagementFlowPrimer />}
        </div>

        <aside className="col-span-12 lg:col-span-4 space-y-4">
          {eng.client_contact && (
            <div className="bg-white border border-[#E8E1D2] p-6" data-testid="engagement-client-contact">
              <Label>Client sponsor</Label>
              <div className="mt-2 text-[#0F1825]">
                <div className="font-serif text-[1.05rem] leading-tight">{eng.client_contact.name}</div>
                <div className="mt-1 text-[12.5px] text-[#5C6470] leading-snug">{eng.client_contact.title}</div>
                <div className="mt-2 text-[12px] text-[#0F1825]">{eng.client_contact.organisation}</div>
                <div className="text-[11.5px] text-[#A09680]">{eng.client_contact.location}</div>
                {eng.client_contact.summary && (
                  <div className="mt-3 pt-3 border-t border-[#F0EBDC] text-[12px] text-[#5C6470] leading-relaxed italic">
                    {eng.client_contact.summary}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="bg-white border border-[#E8E1D2] p-6">
            <Label>Status</Label>
            <Value>{eng.status}</Value>
          </div>
        </aside>
      </div>

      {/* Evidence Intelligence Summary — the Discussion section (executive
          narrative · headline findings · top-3 recommendations). Sits after the
          brief and only unlocks once the engagement holds signals. */}
      {signalCount === null ? null : signalCount > 0 ? (
        <div className="mt-6">
          <EvidenceIntelligenceSummary engagementId={id} engagement={eng} isAdmin={isAdminRole} />
        </div>
      ) : (
        <div className="mt-6 border border-dashed border-[#E8E1D2] bg-[#FAF7F1] px-6 py-4 flex items-center gap-3" data-testid="evidence-summary-locked">
          <Sparkle size={14} className="text-[#A09680] shrink-0" />
          <div className="text-[13px] text-[#5C6470]">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#A09680] mr-2">Evidence Intelligence Summary</span>
            Unlocks after signal intake — run a stream prompt or upload sources in the Source Library, then draft the executive narrative here.
          </div>
        </div>
      )}

      {/* Active decision questions removed — they were hardcoded seed text
          and no longer fit the SLE-scope-only brief. */}

      {/* Stream commissioning briefs removed from the Engagement Brief tab —
          they remain available as the canonical operator prompt definition
          for each stream and surface where they're actionable (Source
          Library's "Pre-fill from stream brief" link inside Run Prompt). */}

      {/* CLIENT: engagement flow primer — fills the left-column whitespace
          beneath the objective / scope blocks. Sets expectations for the
          journey before the reader clicks any tab. Operator view keeps
          the workflow nav at the bottom of the page. */}

      {/* OPERATOR: 'Where to go next' nav and per-stream brief PDF download
          row removed — operator navigation lives in the left sidebar; brief
          PDFs are accessible from the Source Library where they're actionable. */}
    </div>
  );
}

/* ----- helper: engagement-agnostic default prompt for any stream ------- */
// Used when an engagement has no hand-authored commissioning brief for the
// stream (every engagement created from the console). Built from the
// engagement's own metadata + the stream's source universe so the operator
// gets a runnable V2E prompt in one click and can edit before sending.
const STREAM_PROMPT_AXES = {
  L1: ["Most-discussed symptoms and which patients call most debilitating", "Daily-life, work and family burden", "Emotional and mental-health language", "Treatment experience — starting, staying on, switching, stopping", "Side-effect and tolerability language", "Information sources patients trust and misinformation patterns", "Access, cost and system-navigation frustrations", "Caregiver perspective", "Unmet needs stated in patients' own words"],
  L2: ["Validated PRO / COA instruments used and domains most affected", "Symptom burden and health-related quality-of-life scores", "Treatment preference and trade-off studies (DCE, conjoint, BWS)", "Treatment satisfaction, adherence and persistence measures", "Work productivity and activity impairment", "Caregiver burden instruments", "Differences by gender, age band, severity, treatment line"],
  L3: ["Priorities patients rank highest for a new treatment", "Acceptable trade-offs (efficacy vs safety vs convenience)", "Diagnostic journey and time-to-treatment experience", "Support needs at initiation and during long-term management", "Communication with clinicians and the care team", "Perceived gaps in the current standard of care", "Co-creation opportunities patients volunteer"],
  L4: ["Real-world treatment patterns, sequencing and switching", "Persistence, discontinuation and reasons", "Healthcare resource utilisation and hospitalisation", "Outcomes by subgroup (gender, age, comorbidity, region)", "Patient support programme uptake and impact", "Pharmacovigilance narratives with patient-experience content", "Registry and observational evidence on burden over time"],
  L5: ["Patient-experience data cited in regulatory assessments and labels", "PFDD / patient-preference information considered by regulators", "HTA appraisals — patient and carer submissions, committee conclusions", "Payer-relevant outcomes and unmet-need statements", "Endpoints regulators and HTA bodies accepted or challenged", "Geographic differences in assessment conclusions"],
};

function defaultStreamPrompt(streamCode, eng) {
  const meta = STREAM_META_BY_K[streamCode];
  if (!meta || !eng) return "";
  const lines = [];
  lines.push(`[V2E STREAM PROMPT · ${streamCode} · ${meta.name}]`);
  lines.push(`Engagement: ${eng.client_label || ""} · ${eng.brand || ""}${eng.asset ? ` (${eng.asset})` : ""}`);
  if (eng.mechanism) lines.push(`Mechanism: ${eng.mechanism}`);
  lines.push(`Indication: ${eng.indication || ""}`);
  if (eng.ta) lines.push(`Therapy area: ${eng.ta}`);
  if (eng.lifecycle_phase) lines.push(`Lifecycle phase: ${eng.lifecycle_phase}`);
  lines.push("Lookback: last 10 years, prioritise the most recent 5.");
  lines.push("");
  lines.push("OBJECTIVE");
  lines.push(
    `Conduct a comprehensive ${meta.name.toLowerCase()} sweep of the patient and caregiver experience in ${eng.indication || "this indication"}` +
    `${eng.brand ? `, with specific attention to ${eng.brand} and its comparators` : ""}. ` +
    `${eng.engagement_objective ? `Engagement objective: ${eng.engagement_objective} ` : ""}` +
    `${eng.scope ? `Scope: ${eng.scope}` : ""}`.trim(),
  );
  lines.push("");
  lines.push("SEARCH SOURCES");
  (meta.sources || []).forEach((s) => lines.push(`- ${s}`));
  lines.push("");
  lines.push("EVIDENCE AXES (extract findings on each)");
  (STREAM_PROMPT_AXES[streamCode] || []).forEach((a) => lines.push(`- ${a}`));
  lines.push("");
  lines.push("STRATIFICATION (segment every finding where the source allows)");
  ["Gender", "Age band", "Disease severity / stage", "Treatment line / experience", "Country or region", "Patient vs caregiver voice"].forEach((s) => lines.push(`- ${s}`));
  lines.push("");
  lines.push("MANDATORY V2E MAPPING");
  lines.push("Classify every finding into one V2E Theme (A Awareness & Understanding · B Access & Navigation · C Treatment Experience · D Life Impact · E Prevention, Monitoring & Communication) and one Sub-theme (A1–E3).");
  lines.push("");
  lines.push("PER-RECORD FIELDS (return every record with all fields)");
  ["headline_theme (one sentence, patient-voice framing)", "study_citation (authors, year, journal / source name)", "source_url", "study_design", "population and sample_size", "geography (ISO country codes if stated)", "key_finding with numbers where available", "polarity (positive / negative / mixed)", "evidence_grade (A–D)", "v2e_theme and v2e_subtheme", "strategic_implication for the asset"].forEach((f) => lines.push(`- ${f}`));
  lines.push("");
  lines.push("EXCLUSIONS");
  lines.push("- No paediatric-only sources unless the indication is paediatric. No non-human studies. No press releases without primary data. No fabricated citations — return only findings you can cite.");
  lines.push("");
  lines.push("OUTPUT FORMAT");
  lines.push("Return a strict JSON array of records per the schema above; no commentary outside the JSON.");
  return lines.join("\n");
}

/* ----- helper: format a stream brief as a paste-ready prompt string ----- */
function streamBriefToPromptText(streamCode, brief, eng) {
  if (!brief) return "";
  const lines = [];
  lines.push(`[V2E STREAM BRIEF · ${streamCode} · ${brief.title || ""}]`);
  if (brief.reference) lines.push(`Reference: ${brief.reference}`);
  if (eng?.client_label) lines.push(`Client: ${eng.client_label}`);
  if (brief.asset_under_evaluation) lines.push(`Asset: ${brief.asset_under_evaluation}`);
  if (brief.indication) lines.push(`Indication: ${brief.indication}`);
  if (brief.lookback_period) lines.push(`Lookback: ${brief.lookback_period}`);
  if (brief.delivery_window) lines.push(`Delivery window: ${brief.delivery_window}`);
  lines.push("");

  if (brief.objective) {
    lines.push("OBJECTIVE");
    lines.push(brief.objective);
    lines.push("");
  }

  const section = (label, items) => {
    if (!items || !items.length) return;
    lines.push(label.toUpperCase());
    items.forEach((it) => lines.push(`- ${it}`));
    lines.push("");
  };

  section("Search sources", brief.sources);
  section(brief.evidence_axes ? "Evidence axes (extract findings on each)" : "Quantify axes (extract findings on each)", brief.evidence_axes || brief.quantify_axes);
  section("Validated instruments (search and report against)", brief.validated_instruments);
  section("Stratification (segment every finding)", brief.stratification);

  if (brief.mandatory_v2e_mapping) {
    lines.push("MANDATORY V2E MAPPING");
    lines.push(brief.mandatory_v2e_mapping);
    lines.push("");
    lines.push("V2E framework (5 themes x 3 sub-themes — every finding maps to exactly one):");
    lines.push("  A · Awareness, Understanding & Information  →  A1 A2 A3");
    lines.push("  B · Help-Seeking, Diagnosis & Access        →  B1 B2 B3");
    lines.push("  C · Treatment Decision-Making & Journey     →  C1 C2 C3");
    lines.push("  D · Burden, Quality of Life & Support       →  D1 D2 D3");
    lines.push("  E · Prevention, Monitoring, Research        →  E1 E2 E3");
    lines.push("");
  }

  const perRecord = brief.per_insight_fields || brief.per_study_fields || [];
  if (perRecord.length) {
    lines.push(brief.per_study_fields ? "PER-STUDY FIELDS (return every record with all fields)" : "PER-INSIGHT FIELDS (return every record with all fields)");
    perRecord.forEach((it, i) => lines.push(`${String(i + 1).padStart(2, "0")}. ${it}`));
    lines.push("");
  }

  section("Exclusions", brief.exclusions);
  section("Output format", brief.output_format);
  section("Ethics floor (non-negotiable)", brief.ethics_floor);

  if (brief.downstream_use) {
    lines.push("DOWNSTREAM USE");
    lines.push(brief.downstream_use);
    lines.push("");
  }

  if (eng?.decision_questions?.length) {
    lines.push("DECISION QUESTIONS THIS STREAM IS ENGINEERED TO INFORM");
    eng.decision_questions.forEach((q, i) => lines.push(`Q${String(i + 1).padStart(2, "0")}. ${q}`));
    lines.push("");
  }

  lines.push("[End of brief. Return a strict JSON array per the schema above; no commentary outside the JSON.]");
  return lines.join("\n");
}

/* =========================== STREAM BRIEF PANEL ========================== */
/**
 * Inline rendering of a stream commissioning brief (any of L1..L5). Reads
 * `brief.title`, `brief.stream_label`, and the flexible per-brief sections
 * (sources / evidence_axes / validated_instruments / stratification /
 * mandatory_v2e_mapping / per_insight_fields / per_study_fields / exclusions /
 * output_format / ethics_floor / downstream_use). Sections are conditionally
 * rendered, so each brief can declare just the sections it needs.
 */
function StreamBriefPanel({ streamCode, brief, engagementId, isClient, eng }) {
  const [expanded, setExpanded] = useState(false);
  if (!brief) return null;

  const axisItems = brief.evidence_axes || brief.quantify_axes || [];
  const perInsightItems = brief.per_insight_fields || brief.per_study_fields || [];
  const perInsightLabel = brief.per_study_fields ? "Per-study fields" : "Per-insight fields";

  const Section = ({ label, items, columns = 2 }) => {
    if (!items || items.length === 0) return null;
    return (
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-3">{label} · {items.length}</div>
        <ul className={`grid grid-cols-1 ${columns === 1 ? "md:grid-cols-1" : "md:grid-cols-2"} gap-x-6 gap-y-2`}>
          {items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[13.5px] text-[#0F1825] leading-relaxed">
              <span className="font-mono text-[10.5px] text-[#A09680] mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span><AutoAbbr text={it} /></span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="bg-white border border-[#E8E1D2] p-7 mt-6"
      data-testid={`stream-brief-panel-${streamCode}`}
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{streamCode} · {brief.stream_label || "Stream brief"}</div>
          <SectionTitle>{brief.title || `${streamCode} commissioning brief`}</SectionTitle>
          <p className="mt-2 text-[13.5px] text-[#5C6470] leading-relaxed max-w-3xl">
            <AutoAbbr text={brief.objective || "Locked-scope brief issued to the commissioned stream lead."} />
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Reference</div>
          <div className="font-mono text-[11px] text-[#0F1825] mt-1">{brief.reference}</div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680] mt-3">Delivery window</div>
          <div className="text-[12px] text-[#0F1825] mt-1">{brief.delivery_window}</div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4 pt-5 border-t border-[#F0EBDC]">
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Sources</div>
          <div className="font-serif text-2xl text-[#0F1825] mt-1">{brief.sources?.length || 0}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">{brief.evidence_axes ? "Evidence axes" : "Quantify axes"}</div>
          <div className="font-serif text-2xl text-[#0F1825] mt-1">{axisItems.length}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">{brief.validated_instruments ? "Instruments" : "Strat dimensions"}</div>
          <div className="font-serif text-2xl text-[#0F1825] mt-1">{(brief.validated_instruments || brief.stratification || []).length}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">{brief.per_study_fields ? "Per-study fields" : "Per-insight fields"}</div>
          <div className="font-serif text-2xl text-[#0F1825] mt-1">{perInsightItems.length}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Lookback</div>
          <div className="font-serif text-[15px] text-[#0F1825] mt-2">{brief.lookback_period}</div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-5 inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#C84C21] hover:text-[#0F1825] transition-colors"
        data-testid={`stream-brief-${streamCode}-toggle`}
      >
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {expanded ? "Hide full brief taxonomy" : "Show full brief taxonomy"}
      </button>

      {expanded && (
        <div className="mt-6 pt-6 border-t border-[#F0EBDC] space-y-8" data-testid={`stream-brief-${streamCode}-expanded`}>
          <Section label="01 · Sources" items={brief.sources} />
          <Section label={brief.evidence_axes ? "02 · Evidence axes" : "02 · Quantification axes"} items={axisItems} />
          {brief.validated_instruments && <Section label="03 · Validated instruments" items={brief.validated_instruments} />}
          {brief.stratification && <Section label="03 · Stratification" items={brief.stratification} />}
          {brief.mandatory_v2e_mapping && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-3">04 · Mandatory V2E mapping</div>
              <p className="text-[13.5px] text-[#0F1825] leading-relaxed">{brief.mandatory_v2e_mapping}</p>
            </div>
          )}
          <Section label={`05 · ${perInsightLabel}`} items={perInsightItems} columns={1} />
          <Section label="06 · Exclusions" items={brief.exclusions} columns={1} />
          <Section label="07 · Output format" items={brief.output_format} columns={1} />
          {brief.ethics_floor?.length > 0 && (
            <div className="bg-[#EAF3EC] border-l-2 border-[#3F6D55] p-4">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#3F6D55] mb-2">Ethics floor · non-negotiable</div>
              <ul className="space-y-1.5">
                {brief.ethics_floor.map((it, i) => (
                  <li key={i} className="text-[13px] text-[#2E4A38] leading-relaxed">· {it}</li>
                ))}
              </ul>
            </div>
          )}
          {brief.downstream_use && (
            <div className="bg-[#FAF7F1] border-l-2 border-[#C84C21] p-4">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">Downstream use</div>
              <p className="text-[13px] text-[#0F1825] leading-relaxed">{brief.downstream_use}</p>
            </div>
          )}
        </div>
      )}

      {/* Operator actions are NOT duplicated here — they live in the Patient
          Signals tab (the natural place to add new signals). The Engagement
          Brief stays purely a specification document. */}
    </div>
  );
}


/* ============================================================
 * STREAM OPERATOR ACTIONS — paste prompt + upload sources
 * Two operator paths to feed signals into a stream:
 *   ① Run prompt via Perplexity Sonar (live deep-research literature sweep)
 *   ② Upload source files (PDF/PPTX/DOCX) → Gemini+Claude extraction
 * Both terminate at the same destination: db.signals for {engagement, stream}.
 * Operator-only — gated upstream by `!isClient` in StreamBriefPanel.
 * ============================================================ */
function StreamOperatorActions({ streamCode, brief, engagementId, eng, onSignalsCommitted }) {
  const [activePath, setActivePath] = useState("prompt"); // 'prompt' | 'upload'

  return (
    <div
      className="mt-6 pt-6 border-t border-[#F0EBDC]"
      data-testid={`stream-operator-actions-${streamCode}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Zap size={13} className="text-[#C84C21]" />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
          Operator actions · feed {streamCode} signals
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mb-5" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activePath === "prompt"}
          onClick={() => setActivePath("prompt")}
          className={`px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] border transition-colors ${
            activePath === "prompt"
              ? "bg-[#0F1825] text-white border-[#0F1825]"
              : "bg-white text-[#0F1825] border-[#E8E1D2] hover:border-[#0F1825]"
          }`}
          data-testid={`stream-${streamCode}-path-prompt`}
        >
          ① Run prompt · Perplexity Sonar
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePath === "upload"}
          onClick={() => setActivePath("upload")}
          className={`px-4 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] border transition-colors ${
            activePath === "upload"
              ? "bg-[#0F1825] text-white border-[#0F1825]"
              : "bg-white text-[#0F1825] border-[#E8E1D2] hover:border-[#0F1825]"
          }`}
          data-testid={`stream-${streamCode}-path-upload`}
        >
          ② Upload sources · AI extraction
        </button>
      </div>

      {activePath === "prompt" && (
        <RunPromptBox
          streamCode={streamCode}
          brief={brief}
          engagementId={engagementId}
          eng={eng}
          onSignalsCommitted={onSignalsCommitted}
        />
      )}
      {activePath === "upload" && (
        <StreamUploadBox
          streamCode={streamCode}
          engagementId={engagementId}
          onSignalsCommitted={onSignalsCommitted}
        />
      )}
    </div>
  );
}


/* ============================================================
 * RUN PROMPT BOX — operator pastes/edits a literature-review prompt and
 * fires it at the live Perplexity Sonar API. Returns a normalised V2E
 * signal list with citations and a per-signal commit checkbox.
 * ============================================================ */
const PERPLEXITY_MODELS = [
  { value: "sonar-deep-research", label: "sonar-deep-research · exhaustive · 4–8 min" },
  { value: "sonar-pro",           label: "sonar-pro · fast · 15–40s" },
  { value: "sonar-reasoning-pro", label: "sonar-reasoning-pro · reasoning-tuned · 1–3 min" },
];

const POLL_MS = 5000;
const POLL_CEILING_MS = 12 * 60 * 1000;
const RESUME_WINDOW_MS = 16 * 60 * 1000;
const STREAM_ORDER = ["L1", "L2", "L3", "L4", "L5"];

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};
const fmtRunDate = (ts) => (ts
  ? new Date(ts).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  : "");

/* Ticking "Xm YYs" since an ISO timestamp. */
function RunElapsed({ since }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <>{fmtElapsed(Date.now() - new Date(since).getTime())}</>;
}

/* Status chips shown on every stream row (collapsed or open). */
function StreamStatusChips({ streamKey, signalCount, runs }) {
  const running = runs.find((r) => r.status === "running");
  const latest = runs.find((r) => !r.superseded_at);
  const chip = (cls, testid, children) => (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 ${cls}`} data-testid={testid}>
      {children}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`stream-status-${streamKey}`}>
      {signalCount > 0
        ? chip("border-[#3F6D55]/40 bg-[#EAF3EC] text-[#2E4A38]", `stream-signals-chip-${streamKey}`, <><Check size={10} /> {signalCount} signals</>)
        : chip("border-[#E8E1D2] bg-white text-[#A09680]", `stream-signals-chip-${streamKey}`, "No signals yet")}
      {running && chip("border-[#B7791F] bg-[#FFF6E5] text-[#B7791F]", `stream-running-chip-${streamKey}`,
        <><Loader2 size={10} className="animate-spin" /> Sonar running · <RunElapsed since={running.triggered_at} /></>)}
      {!running && latest?.status === "succeeded" && !latest.committed_at && (latest.signal_count || 0) > 0
        && chip("border-[#C84C21]/50 bg-[#FCE9E1] text-[#C84C21]", `stream-review-chip-${streamKey}`, `${latest.signal_count} signals to review`)}
      {!running && latest?.status === "failed"
        && chip("border-[#C84C21]/50 bg-white text-[#C84C21]", `stream-failed-chip-${streamKey}`, "Last run failed")}
    </div>
  );
}

/* One row in the stream's run manager. */
function SonarRunRow({ run, streamKey, active, onView, onViewPrompt, onRerun, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  const isRunning = run.status === "running";
  const isFailed = run.status === "failed";
  const isOk = run.status === "succeeded";
  const cost = run.usage?.cost?.total_cost;
  const act = "font-mono text-[10px] uppercase tracking-[0.16em] transition-colors";
  const short = run.id.slice(0, 8);
  return (
    <li
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 border text-[12px] ${active ? "border-[#C84C21] bg-[#FCE9E1]" : "border-[#E8E1D2] bg-white"}`}
      data-testid={`sonar-run-row-${streamKey}-${short}`}
      aria-current={active ? "true" : undefined}
    >
      <span className="font-mono text-[10.5px] text-[#0F1825]">{fmtRunDate(run.completed_at || run.triggered_at)}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]">{(run.model_used || run.model_requested || "").replace("sonar-", "")}</span>
      {isRunning && (
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[#B7791F]">
          <Loader2 size={10} className="animate-spin" /> Running · <RunElapsed since={run.triggered_at} />
        </span>
      )}
      {isFailed && <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21]">Failed</span>}
      {isOk && <span className="text-[#0F1825]"><span className="font-medium">{run.signal_count || 0}</span> signals</span>}
      {run.committed_at && (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[#2E4A38] bg-[#EAF3EC] border border-[#3F6D55]/40 px-1.5 py-px" data-testid={`sonar-run-committed-${streamKey}-${short}`}>
          <Check size={10} /> {run.committed_count} committed
        </span>
      )}
      {run.superseded_at && <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">Superseded</span>}
      {typeof cost === "number" && <span className="text-[#A09680] font-mono text-[10.5px]">${cost.toFixed(3)}</span>}
      {isFailed && run.error && <span className="basis-full text-[11px] text-[#5C6470] leading-snug">{run.error}</span>}
      <span className="ml-auto inline-flex items-center gap-3">
        {(isOk || isRunning) && (
          <button type="button" onClick={onView} className={`${act} text-[#C84C21] hover:text-[#0F1825]`} data-testid={`sonar-run-view-${streamKey}-${short}`}>
            {active ? "Viewing" : isRunning ? "Resume" : "View"}
          </button>
        )}
        {isOk && onViewPrompt && (
          <button type="button" onClick={onViewPrompt} className={`${act} text-[#0F1825] hover:text-[#C84C21]`} data-testid={`sonar-run-prompt-${streamKey}-${short}`}>Prompt</button>
        )}
        <button type="button" onClick={onRerun} className={`${act} text-[#0F1825] hover:text-[#C84C21]`} data-testid={`sonar-run-rerun-${streamKey}-${short}`}>Re-run</button>
        {!confirm ? (
          <button type="button" onClick={() => setConfirm(true)} className={`${act} inline-flex items-center gap-1 text-[#A09680] hover:text-[#C84C21]`} data-testid={`sonar-run-delete-${streamKey}-${short}`}>
            <Trash2 size={11} /> Delete
          </button>
        ) : (
          <span className="inline-flex items-center gap-2">
            <span className="text-[11px] text-[#C84C21]">Delete this run{run.committed_at ? " (committed signals stay)" : ""}?</span>
            <button type="button" onClick={onDelete} className={`${act} bg-[#C84C21] text-white px-2 py-0.5`} data-testid={`sonar-run-delete-confirm-${streamKey}-${short}`}>Yes</button>
            <button type="button" onClick={() => setConfirm(false)} className={`${act} text-[#5C6470]`} data-testid={`sonar-run-delete-cancel-${streamKey}-${short}`}>No</button>
          </span>
        )}
      </span>
    </li>
  );
}

/* ============================================================
 * SONAR WORKBENCH — one panel per stream. Opens on whatever needs attention
 * (a run in flight → progress; an uncommitted result → review & commit;
 * otherwise → a pre-filled prompt ready to run). Manages the stream's runs.
 * ============================================================ */
function RunPromptBox({ streamCode, brief, engagementId, eng, runs, runsLoaded, streamSignalCount = 0, onRunsChanged, onSignalsCommitted }) {
  const [model, setModel] = useState("sonar-deep-research");
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState("idle"); // idle | running | succeeded | failed | stalled | committed
  const [committedRun, setCommittedRun] = useState(null); // summary of the committed run shown in the "committed" state
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState({});
  const [commitState, setCommitState] = useState({ status: "idle", message: "" });
  const [activeRun, setActiveRun] = useState(null); // { id, startedAt, model }
  const [elapsedMs, setElapsedMs] = useState(0);
  const [showRuns, setShowRuns] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const initialised = useRef(false);

  const defaultPrompt = () => (brief ? streamBriefToPromptText(streamCode, brief, eng) : defaultStreamPrompt(streamCode, eng)) || "";
  const notifyRuns = () => { if (typeof onRunsChanged === "function") onRunsChanged(); };
  const runUrl = (runId) => `${API}/console/engagements/${engagementId}/streams/${streamCode}/prompt-runs/${runId}`;

  useEffect(() => {
    if (status !== "running" || !activeRun) return undefined;
    const tick = () => setElapsedMs(Date.now() - activeRun.startedAt);
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [status, activeRun]);

  // Opening view, decided once the stream's runs are known.
  useEffect(() => {
    if (!runsLoaded || initialised.current) return;
    initialised.current = true;
    const running = runs.find((r) => r.status === "running" && r.triggered_at
      && Date.now() - new Date(r.triggered_at).getTime() < RESUME_WINDOW_MS);
    if (running) { resumeRun(running); return; }
    const review = runs.find((r) => r.status === "succeeded" && !r.committed_at && !r.superseded_at && (r.signal_count || 0) > 0);
    if (review) { openRun(review.id); return; }
    const committed = runs.find((r) => r.committed_at && !r.superseded_at);
    if (committed) { setCommittedRun(committed); setStatus("committed"); return; }
    setPrompt((p) => p || defaultPrompt());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsLoaded]);

  const resumeRun = (run) => {
    const startedAt = new Date(run.triggered_at).getTime();
    setActiveRun({ id: run.id, startedAt, model: run.model_requested });
    setStatus("running");
    setError("");
    setResult(null);
    pollRun(run.id, startedAt);
  };

  const loadResult = (run) => {
    setResult(run);
    const sel = {};
    (run.signals || []).forEach((s, i) => { sel[i] = !s.verification_status || s.verification_status === "verified"; });
    setSelected(sel);
    setExpanded({});
    setStatus("succeeded");
    setError("");
    setActiveRun(null);
    setCommitState({ status: "idle", message: "" });
  };

  const openRun = async (runId) => {
    try {
      const { data: run } = await axios.get(runUrl(runId));
      if (run.status === "running") { resumeRun(run); return; }
      if (run.status === "succeeded") { loadResult(run); return; }
      setResult(null);
      if (run.prompt) setPrompt(run.prompt);
      if (run.model_requested) setModel(run.model_requested);
      setStatus("failed");
      setError(run.error || "Run failed.");
    } catch (e) {
      setStatus("failed");
      setError(String(e?.response?.data?.detail || e?.message || "Could not load run."));
    }
  };

  const rerunFrom = async (run) => {
    try {
      const { data: full } = await axios.get(runUrl(run.id));
      if (full.prompt) setPrompt(full.prompt);
      if (full.model_requested) setModel(full.model_requested);
    } catch { /* keep the current prompt */ }
    setStatus("idle");
    setResult(null);
    setError("");
    setCommitState({ status: "idle", message: "" });
    setShowRuns(false);
  };

  const deleteRun = async (run) => {
    try {
      await axios.delete(runUrl(run.id));
      if (result?.id === run.id || activeRun?.id === run.id) startNewRun();
      notifyRuns();
      toast.success(`Run from ${fmtRunDate(run.triggered_at)} deleted`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || e?.message || "Could not delete run."));
    }
  };

  const onRun = async () => {
    if (prompt.trim().length < 40) {
      setError("Prompt is too short — please paste a full stream prompt.");
      return;
    }
    if (status === "running") return;
    setStatus("running");
    setError("");
    setResult(null);
    setSelected({});
    setShowRuns(false);
    setCommitState({ status: "idle", message: "" });
    try {
      const { data: kickoff } = await axios.post(
        `${API}/console/engagements/${engagementId}/streams/${streamCode}/run-prompt`,
        { prompt, model },
        { timeout: 60000 }
      );
      const startedAt = Date.now();
      setActiveRun({ id: kickoff.id, startedAt, model });
      notifyRuns();
      await pollRun(kickoff.id, startedAt);
    } catch (e) {
      setStatus("failed");
      setError(String(e?.response?.data?.detail || e?.message || "Run failed."));
    }
  };

  // Poll every 5 s until the run settles or the 12-min ceiling is hit (the
  // backend gives Sonar 10 min + a Claude compile pass). Ceiling → "stalled".
  const pollRun = async (runId, startedAt) => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const windowStart = Date.now();
    let transientFailures = 0;
    while (Date.now() - windowStart < POLL_CEILING_MS) {
      await wait(POLL_MS);
      try {
        const { data: run } = await axios.get(runUrl(runId), { timeout: 30000 });
        transientFailures = 0;
        if (run.status === "succeeded") {
          loadResult(run);
          notifyRuns();
          return;
        }
        if (run.status === "failed") {
          setStatus("failed");
          setError(run.error || "Run failed.");
          setActiveRun(null);
          notifyRuns();
          return;
        }
      } catch (pollErr) {
        transientFailures += 1;
        if (transientFailures > 4) {
          setStatus("failed");
          setError(`Polling failed repeatedly: ${pollErr?.message || pollErr}`);
          return;
        }
      }
    }
    setStatus("stalled");
    setError(
      `Still no result after ${fmtElapsed(Date.now() - startedAt)}. Deep-research normally finishes in 4–8 min; the backend gives up at ~16 min. Check again below, or start a new run.`
    );
  };

  const onCheckAgain = () => {
    if (!activeRun) return;
    setStatus("running");
    setError("");
    pollRun(activeRun.id, activeRun.startedAt);
  };

  const onCommit = async () => {
    if (!result?.signals?.length) return;
    const toCommit = result.signals.filter((_, i) => selected[i]);
    if (toCommit.length === 0) {
      setCommitState({ status: "failed", message: "Select at least one signal to commit." });
      return;
    }
    setCommitState({ status: "running", message: "" });
    try {
      const { data } = await axios.post(
        `${API}/console/engagements/${engagementId}/streams/${streamCode}/commit-signals`,
        { signals: toCommit, run_id: result.id }
      );
      const n = data.inserted_count;
      const dup = data.skipped_duplicate_count;
      if (n > 0) {
        toast.success(`${n} signal${n === 1 ? "" : "s"} committed to ${streamCode}${dup ? ` · ${dup} duplicate${dup === 1 ? "" : "s"} skipped` : ""}`);
        setCommitState({ status: "succeeded", message: `${n} committed · ${dup} duplicate${dup === 1 ? "" : "s"} skipped.` });
      } else {
        setCommitState({ status: "succeeded", message: `Nothing new added — all ${dup} selected signals were already in ${streamCode}.` });
      }
      if (typeof onSignalsCommitted === "function") onSignalsCommitted(data);
    } catch (e) {
      setCommitState({ status: "failed", message: String(e?.response?.data?.detail || e?.message || "Commit failed.") });
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(result?.prompt || "");
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1500);
    } catch { toast.error("Could not copy — select the text and copy manually."); }
  };
  const rerunWithThisPrompt = () => {
    if (result?.prompt) setPrompt(result.prompt);
    if (result?.model_requested) setModel(result.model_requested);
    setShowPrompt(false);
    startNewRun();
  };

  const startNewRun = () => {
    setStatus("idle");
    setResult(null);
    setSelected({});
    setError("");
    setActiveRun(null);
    setCommitState({ status: "idle", message: "" });
    setPrompt((p) => p || defaultPrompt());
  };

  const cost = result?.usage?.cost?.total_cost;
  const signalCount = result?.signals?.length || 0;
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const expandedCount = Object.values(expanded).filter(Boolean).length;
  const showComposer = status === "idle" || status === "failed";
  const setSelection = (pred) => {
    const sel = {};
    (result?.signals || []).forEach((s, i) => { sel[i] = pred(s); });
    setSelected(sel);
  };
  const setAllExpanded = (v) => {
    const ex = {};
    (result?.signals || []).forEach((_, i) => { ex[i] = v; });
    setExpanded(ex);
  };
  const progressPct = Math.min(95, Math.round((elapsedMs / (8 * 60000)) * 100));
  const visibleRuns = runs.filter((r) => showSuperseded || !r.superseded_at);
  const supersededCount = runs.length - runs.filter((r) => !r.superseded_at).length;
  const viewingId = result?.id || activeRun?.id || null;

  const commitButton = (testid) => (
    <button
      type="button"
      onClick={onCommit}
      disabled={commitState.status === "running" || selectedCount === 0 || commitState.status === "succeeded"}
      className="inline-flex items-center gap-2 bg-[#C84C21] text-white px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.18em] hover:bg-[#0F1825] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
    >
      {commitState.status === "running" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
      Commit {selectedCount} selected to {streamCode}
    </button>
  );

  return (
    <div className="bg-[#FAF7F1] p-5 space-y-4" data-testid={`run-prompt-box-${streamCode}`}>
      {runs.length > 0 && (
        <div data-testid={`sonar-runs-${streamCode}`}>
          <button
            type="button"
            onClick={() => setShowRuns((v) => !v)}
            className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-left"
            data-testid={`sonar-runs-toggle-${streamCode}`}
            aria-expanded={showRuns}
          >
            <ChevronDown size={13} className={`text-[#A09680] transition-transform ${showRuns ? "rotate-180" : ""}`} />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">Sonar runs · {visibleRuns.length}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]">
              {runs.filter((r) => r.committed_at).length} committed
              {runs.some((r) => r.status === "running") ? " · 1 running" : ""}
              {supersededCount ? ` · ${supersededCount} superseded` : ""}
            </span>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">{showRuns ? "Hide" : "Manage runs"}</span>
          </button>
          {showRuns && (
            <ul className="mt-2 space-y-1" data-testid={`sonar-runs-list-${streamCode}`}>
              {visibleRuns.map((r) => (
                <SonarRunRow
                  key={r.id}
                  run={r}
                  streamKey={streamCode}
                  active={r.id === viewingId}
                  onView={() => { setShowPrompt(false); openRun(r.id); }}
                  onViewPrompt={() => { setShowPrompt(true); openRun(r.id); }}
                  onRerun={() => rerunFrom(r)}
                  onDelete={() => deleteRun(r)}
                />
              ))}
              {supersededCount > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowSuperseded((v) => !v)}
                    className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] hover:text-[#0F1825] px-3 py-1"
                    data-testid={`sonar-runs-superseded-toggle-${streamCode}`}
                  >
                    {showSuperseded ? "Hide superseded runs" : `Show ${supersededCount} superseded run${supersededCount === 1 ? "" : "s"}`}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {showComposer && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4" data-testid={`run-composer-${streamCode}`}>
          <div>
            <div className="flex items-baseline justify-between mb-2 gap-3">
              <label className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">
                {status === "failed" ? "Edit the prompt below and re-run" : "Prompt · pre-filled for this engagement · edit before running"}
              </label>
              <button
                type="button"
                onClick={() => setPrompt(defaultPrompt())}
                className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#C84C21] hover:text-[#0F1825] transition-colors"
                data-testid={`prefill-from-brief-${streamCode}`}
              >
                Reset to V2E default ↺
              </button>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={10}
              className="w-full bg-white border border-[#E8E1D2] p-3 font-mono text-[12px] text-[#0F1825] leading-relaxed focus:outline-none focus:border-[#0F1825] resize-y"
              placeholder={`Paste your ${streamCode} prompt here…`}
              data-testid={`prompt-textarea-${streamCode}`}
            />
          </div>
          <div className="flex flex-col gap-3">
            <div>
              <label className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680] block mb-2">Model</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full bg-white border border-[#E8E1D2] p-2.5 text-[12px] text-[#0F1825] focus:outline-none focus:border-[#0F1825]"
                data-testid={`prompt-model-${streamCode}`}
              >
                {PERPLEXITY_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={onRun}
              className="inline-flex items-center justify-center gap-2 bg-[#0F1825] text-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#C84C21] transition-colors"
              data-testid={`run-prompt-btn-${streamCode}`}
            >
              <Play size={14} />
              {status === "failed" ? "Re-run via Perplexity Sonar" : "Run via Perplexity Sonar"}
            </button>
            <p className="font-mono text-[9.5px] text-[#A09680] leading-relaxed">
              Deep-research: 4–8 min, $0.10–$0.40. sonar-pro: 15–40 s, fewer signals.
              Runs continue on the server if you leave — the stream row shows progress.
            </p>
          </div>
        </div>
      )}

      {status === "committed" && committedRun && (
        <div className="bg-white border border-[#3F6D55]/40 p-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`run-committed-${streamCode}`}>
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#2E4A38]">
            <Check size={13} /> {streamCode} done · {streamSignalCount} signals in {streamCode}
          </span>
          <span className="text-[12px] text-[#5C6470]">
            Last commit {fmtRunDate(committedRun.committed_at)} from the {fmtRunDate(committedRun.completed_at || committedRun.triggered_at)} {(committedRun.model_used || "").replace("sonar-", "")} run
            {" · "}{committedRun.committed_count || 0} new
            {committedRun.committed_skipped ? ` · ${committedRun.committed_skipped} already in ${streamCode} (skipped)` : ""}
          </span>
          <span className="ml-auto inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowPrompt(true); openRun(committedRun.id); }}
              className="inline-flex items-center gap-2 border border-[#E8E1D2] text-[#0F1825] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-[#0F1825] transition-colors"
              data-testid={`run-committed-prompt-${streamCode}`}
            >
              <FileText size={12} /> View prompt
            </button>
            <button
              type="button"
              onClick={() => { setShowPrompt(false); openRun(committedRun.id); }}
              className="inline-flex items-center gap-2 border border-[#E8E1D2] text-[#0F1825] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-[#0F1825] transition-colors"
              data-testid={`run-committed-view-${streamCode}`}
            >
              View signals
            </button>
            <button
              type="button"
              onClick={startNewRun}
              className="inline-flex items-center gap-2 bg-[#0F1825] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#C84C21] transition-colors"
              data-testid={`run-committed-new-${streamCode}`}
            >
              <RefreshCw size={12} /> New run
            </button>
          </span>
        </div>
      )}

      {status === "running" && (
        <div className="bg-white border border-[#E8E1D2] p-5" data-testid={`run-running-${streamCode}`}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#B7791F]">
              <Loader2 size={14} className="animate-spin" />
              <span data-testid={`run-elapsed-${streamCode}`}>Sonar searching · {fmtElapsed(elapsedMs)}</span>
            </span>
            {activeRun?.model && (
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]">{activeRun.model}</span>
            )}
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">typically 4–8 min</span>
          </div>
          <div className="mt-3 h-1 w-full bg-[#F0EBDC] overflow-hidden">
            <div className="h-full bg-[#B7791F] transition-[width] duration-1000" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="mt-3 font-mono text-[10px] text-[#C84C21] leading-relaxed" data-testid={`run-progress-hint-${streamCode}`}>
            {elapsedMs < 60000
              ? "Sonar is searching sources…"
              : elapsedMs < 6 * 60000
                ? "Sonar sweep in progress · searching and reading sources"
                : "Claude is compiling V2E signals from the Sonar prose · nearly there"}
          </p>
          <p className="mt-1 text-[12px] text-[#5C6470] leading-relaxed">
            Safe to move on to another stream — this row keeps showing progress and the result lands here when it is done.
          </p>
        </div>
      )}

      {status === "stalled" && (
        <div className="bg-[#FFF6E5] border-l-2 border-[#B7791F] p-4 flex items-start gap-3" data-testid={`run-stalled-${streamCode}`}>
          <AlertTriangle size={14} className="text-[#B7791F] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B7791F]">Still running · no result yet</div>
            <div className="text-[12.5px] text-[#0F1825] mt-1 break-words">{error}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={onCheckAgain}
                className="inline-flex items-center gap-2 bg-[#0F1825] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#C84C21] transition-colors"
                data-testid={`run-check-again-${streamCode}`}
              >
                <RefreshCw size={12} /> Check again
              </button>
              <button
                type="button"
                onClick={startNewRun}
                className="inline-flex items-center gap-2 border border-[#E8E1D2] text-[#0F1825] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-[#0F1825] transition-colors"
                data-testid={`run-start-over-${streamCode}`}
              >
                Start a new run
              </button>
            </div>
          </div>
        </div>
      )}

      {status === "failed" && (
        <div className="bg-[#FCE9E1] border-l-2 border-[#C84C21] p-4 flex items-start gap-2" data-testid={`run-error-${streamCode}`}>
          <AlertTriangle size={14} className="text-[#C84C21] mt-0.5 shrink-0" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">Run failed</div>
            <div className="text-[12.5px] text-[#0F1825] mt-1 break-words">{error}</div>
          </div>
        </div>
      )}

      {status === "succeeded" && result && (
        <div className="space-y-3" data-testid={`run-result-${streamCode}`}>
          <div className="bg-white border border-[#E8E1D2] p-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`run-result-header-${streamCode}`}>
            <div className="min-w-0">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">
                {result.committed_at ? "Committed Sonar run" : "Sonar result · review & commit"}
              </div>
              <div className="font-serif text-[1.05rem] text-[#0F1825] leading-tight">
                {fmtRunDate(result.completed_at || result.triggered_at)} · <span className="font-mono text-[12px] text-[#5C6470]">{result.model_used}</span>
              </div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3F6D55]">
              {signalCount} signals · {result.citations?.length || 0} citations
            </span>
            {typeof cost === "number" && (
              <span className="text-[12px] text-[#5C6470]">Cost <span className="font-mono">${cost.toFixed(3)}</span></span>
            )}
            {result.discarded_count > 0 && (
              <span className="text-[12px] text-[#C84C21]">{result.discarded_count} dropped (no V2E mapping)</span>
            )}
            {result.committed_at && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[#2E4A38] bg-[#EAF3EC] border border-[#3F6D55]/40 px-1.5 py-px">
                <Check size={10} /> {result.committed_count} committed {fmtRunDate(result.committed_at)}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowPrompt((v) => !v)}
              className={`ml-auto inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                showPrompt ? "border-[#C84C21] bg-[#C84C21] text-white" : "border-[#E8E1D2] text-[#0F1825] hover:border-[#0F1825]"
              }`}
              data-testid={`run-view-prompt-${streamCode}`}
              aria-expanded={showPrompt}
            >
              <FileText size={12} /> {showPrompt ? "Hide prompt" : "View prompt"}
            </button>
            <button
              type="button"
              onClick={startNewRun}
              className="inline-flex items-center gap-2 border border-[#0F1825] text-[#0F1825] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#0F1825] hover:text-white transition-colors"
              data-testid={`run-new-${streamCode}`}
            >
              <RefreshCw size={12} /> New run
            </button>
          </div>

          {showPrompt && (
            <div className="bg-white border border-[#E8E1D2]" data-testid={`run-prompt-view-${streamCode}`}>
              <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-[#F0EBDC]">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">
                  Prompt that produced these {signalCount} signals · sent to {result.model_used || result.model_requested} with the V2E mapping system prompt
                </span>
                <span className="ml-auto inline-flex items-center gap-3">
                  <button type="button" onClick={copyPrompt} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21] hover:text-[#0F1825]" data-testid={`run-copy-prompt-${streamCode}`}>
                    {promptCopied ? "Copied ✓" : "Copy"}
                  </button>
                  <button type="button" onClick={rerunWithThisPrompt} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#0F1825] hover:text-[#C84C21]" data-testid={`run-rerun-prompt-${streamCode}`}>
                    Edit &amp; re-run
                  </button>
                </span>
              </div>
              <pre className="px-4 py-3 font-mono text-[11.5px] text-[#0F1825] leading-relaxed whitespace-pre-wrap max-h-[420px] overflow-auto" data-testid={`run-prompt-text-${streamCode}`}>
                {result.prompt || "(prompt not stored for this run)"}
              </pre>
            </div>
          )}

          {signalCount === 0 && (
            <div className="bg-white border border-[#E8E1D2] p-4 text-[12.5px] text-[#5C6470]">
              No V2E-mappable signals returned. Tighten the prompt or try a different model.
            </div>
          )}

          {signalCount > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[#E8E1D2] pb-3" data-testid={`run-select-bar-${streamCode}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0F1825]">
                {selectedCount} of {signalCount} selected
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] inline-flex items-center gap-2">
                <button type="button" onClick={() => setSelection(() => true)} className="text-[#C84C21] hover:text-[#0F1825]" data-testid={`select-all-${streamCode}`}>All</button>
                ·
                <button type="button" onClick={() => setSelection((s) => !s.verification_status || s.verification_status === "verified")} className="text-[#C84C21] hover:text-[#0F1825]" data-testid={`select-verified-${streamCode}`}>Verified only</button>
                ·
                <button type="button" onClick={() => setSelection(() => false)} className="text-[#C84C21] hover:text-[#0F1825]" data-testid={`select-none-${streamCode}`}>None</button>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] inline-flex items-center gap-2">
                <button type="button" onClick={() => setAllExpanded(expandedCount < signalCount)} className="text-[#5C6470] hover:text-[#0F1825]" data-testid={`expand-all-${streamCode}`}>
                  {expandedCount < signalCount ? "Expand all" : "Collapse all"}
                </button>
              </span>
              <div className="ml-auto">{commitButton(`commit-signals-btn-${streamCode}`)}</div>
            </div>
          )}

          {commitState.status === "succeeded" && (
            <div className="bg-[#EAF3EC] border-l-2 border-[#3F6D55] p-3 text-[12px] text-[#2E4A38]" data-testid={`commit-success-${streamCode}`}>
              ✓ {commitState.message}
            </div>
          )}
          {commitState.status === "failed" && (
            <div className="bg-[#FCE9E1] border-l-2 border-[#C84C21] p-3 text-[12px] text-[#C84C21]" data-testid={`commit-error-${streamCode}`}>
              {commitState.message}
            </div>
          )}

          <div className="space-y-1.5">
            {result.signals?.map((sig, i) => (
              <PerplexitySignalPreviewCard
                key={i}
                index={i}
                streamCode={streamCode}
                signal={sig}
                checked={!!selected[i]}
                onToggle={() => setSelected({ ...selected, [i]: !selected[i] })}
                expanded={!!expanded[i]}
                onToggleExpand={() => setExpanded({ ...expanded, [i]: !expanded[i] })}
              />
            ))}
          </div>

          {signalCount > 6 && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">
                {selectedCount} of {signalCount} selected
              </div>
              {commitButton(`commit-signals-btn-bottom-${streamCode}`)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* One preview signal from Perplexity Sonar — collapsed to a single line
   (checkbox · chips · headline) until expanded. */
function PerplexitySignalPreviewCard({ index, streamCode, signal, checked, onToggle, expanded, onToggleExpand }) {
  const isVerified = signal.verification_status === "verified";
  const label = "font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]";
  return (
    <div
      className={`bg-white border ${isVerified ? "border-[#E8E1D2]" : "border-[#F2C9B6] bg-[#FCF1EC]"}`}
      data-testid={`preview-signal-${streamCode}-${index}`}
    >
      <div className="flex items-start gap-3 p-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 w-4 h-4 accent-[#C84C21] cursor-pointer shrink-0"
          data-testid={`preview-signal-check-${streamCode}-${index}`}
        />
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex-1 min-w-0 text-left"
          data-testid={`preview-signal-toggle-${streamCode}-${index}`}
          aria-expanded={expanded}
        >
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#C84C21]">
              {signal.v2e_theme} · {(signal.v2e_subthemes || []).join(" · ")}
            </span>
            {signal.evidence_grade_normalized ? (
              <EvidenceGradeChip signal={signal} size="xs" showTrackHint testid={`preview-signal-grade-${streamCode}-${index}`} />
            ) : signal.evidence_grade ? (
              <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]" data-testid={`preview-signal-grade-${streamCode}-${index}`}>
                {signal.evidence_grade}
              </span>
            ) : null}
            <span className={`font-mono text-[9.5px] uppercase tracking-[0.22em] ${
              signal.polarity === "positive" ? "text-[#3F6D55]" :
              signal.polarity === "negative" ? "text-[#C84C21]" : "text-[#A09680]"
            }`}>
              {signal.polarity}
            </span>
            <VerificationBadge status={signal.verification_status || "unverified"} note={signal.verification_note} />
          </div>
          <div className={`text-[13px] text-[#0F1825] font-medium leading-snug ${expanded ? "" : "line-clamp-2"}`}>
            <AutoAbbr text={signal.headline_theme} />
          </div>
        </button>
        <button type="button" onClick={onToggleExpand} className="shrink-0 mt-0.5 text-[#A09680] hover:text-[#0F1825]" aria-label={expanded ? "Collapse" : "Expand"}>
          <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pl-10 space-y-2" data-testid={`preview-signal-details-${streamCode}-${index}`}>
          {signal.key_finding && (
            <p className="text-[12.5px] text-[#5C6470] leading-relaxed"><AutoAbbr text={signal.key_finding} /></p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11.5px] text-[#5C6470]">
            {signal.study_design && <div><span className={label}>Design</span> · <AutoAbbr text={signal.study_design} /></div>}
            {signal.population && <div><span className={label}>Population</span> · <AutoAbbr text={signal.population} /></div>}
            {signal.sample_size && <div><span className={label}>Sample</span> · {signal.sample_size}</div>}
            {signal.statistical_significance && <div><span className={label}>Stat sig</span> · {signal.statistical_significance}</div>}
            {signal.confidence_level && <div><span className={label}>Confidence</span> · {signal.confidence_level}</div>}
          </div>
          {signal.strategic_implication && (
            <div className="bg-[#FAF7F1] border-l-2 border-[#C84C21] p-2.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">Strategic implication</div>
              <div className="text-[12px] text-[#0F1825] leading-relaxed"><AutoAbbr text={signal.strategic_implication} /></div>
            </div>
          )}
          {signal.source_url && (
            <a
              href={signal.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[10.5px] text-[#C84C21] hover:text-[#0F1825] transition-colors break-all"
              data-testid={`preview-signal-source-${streamCode}-${index}`}
            >
              <ExternalLink size={11} /> {signal.study_citation || signal.source_url}
            </a>
          )}
          {!isVerified && signal.verification_note && (
            <div className="text-[11px] text-[#C84C21] italic">{signal.verification_note}</div>
          )}
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * STREAM UPLOAD BOX — multi-file upload (PDF/PPTX/DOCX) → backend
 * extraction (Gemini + Claude). Each file uploads and then is queued for
 * extraction; per-file progress is shown inline.
 * ============================================================ */
function StreamUploadBox({ streamCode, engagementId, onSignalsCommitted }) {
  const [files, setFiles] = useState([]); // [{ id, name, status, signalCount, error }]
  const [dragOver, setDragOver] = useState(false);

  const acceptStr = ".pdf,.pptx,.docx";

  const onPick = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const f of arr) {
      const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
      setFiles((prev) => [...prev, { id: tempId, name: f.name, status: "uploading", signalCount: 0, error: "" }]);
      try {
        const form = new FormData();
        form.append("file", f);
        const studyTitle = (f.name || "Uploaded source").replace(/\.[^.]+$/, "").slice(0, 240);
        const url =
          `${API}/console/engagements/${engagementId}/sources` +
          `?stream=${encodeURIComponent(streamCode)}` +
          `&study_title=${encodeURIComponent(studyTitle)}`;
        const { data: uploaded } = await axios.post(
          url,
          form,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
        );
        const sourceId = uploaded.id || uploaded.source_id;
        setFiles((prev) => prev.map((row) =>
          row.id === tempId ? { ...row, id: sourceId, status: "extracting" } : row
        ));
        try {
          const { data: extracted } = await axios.post(
            `${API}/console/engagements/${engagementId}/sources/${sourceId}/extract`,
            {},
            { timeout: 600000 }
          );
          setFiles((prev) => prev.map((row) =>
            row.id === sourceId
              ? { ...row, status: "extracted", signalCount: extracted.extracted_signal_count || 0 }
              : row
          ));
          if ((extracted.extracted_signal_count || 0) > 0 && typeof onSignalsCommitted === "function") {
            onSignalsCommitted();
          }
        } catch (extractErr) {
          const detail = extractErr?.response?.data?.detail || extractErr?.message || "Extraction failed.";
          setFiles((prev) => prev.map((row) =>
            row.id === sourceId ? { ...row, status: "failed", error: String(detail) } : row
          ));
        }
      } catch (uploadErr) {
        const detail = uploadErr?.response?.data?.detail || uploadErr?.message || "Upload failed.";
        setFiles((prev) => prev.map((row) =>
          row.id === tempId ? { ...row, status: "failed", error: String(detail) } : row
        ));
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onPick(e.dataTransfer.files);
  };

  return (
    <div className="bg-[#FAF7F1] border border-[#E8E1D2] p-5" data-testid={`upload-box-${streamCode}`}>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`block cursor-pointer text-center border-2 border-dashed p-8 transition-colors ${
          dragOver ? "border-[#C84C21] bg-[#FCE9E1]" : "border-[#E8E1D2] bg-white hover:border-[#0F1825]"
        }`}
      >
        <FileUp size={28} className="mx-auto text-[#C84C21]" />
        <div className="mt-3 text-[13.5px] text-[#0F1825]">Drag &amp; drop or click to upload</div>
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#A09680] mt-1">
          {streamCode} · PDF · PPTX · DOCX · multiple at once
        </div>
        <input
          type="file"
          multiple
          accept={acceptStr}
          onChange={(e) => onPick(e.target.files)}
          className="hidden"
          data-testid={`upload-input-${streamCode}`}
        />
      </label>

      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`upload-list-${streamCode}`}>
          {files.map((f, idx) => (
            <div key={`${f.id}-${idx}`} className="bg-white border border-[#E8E1D2] p-3 flex items-center gap-3" data-testid={`upload-row-${streamCode}-${idx}`}>
              <FileText size={14} className="text-[#0F1825] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-[#0F1825] truncate">{f.name}</div>
                {f.status === "failed" && f.error && (
                  <div className="text-[11px] text-[#C84C21] mt-0.5 break-words">{f.error}</div>
                )}
              </div>
              <UploadStatusBadge status={f.status} signalCount={f.signalCount} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadStatusBadge({ status, signalCount }) {
  if (status === "uploading") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21]">
        <Loader2 size={11} className="animate-spin" /> Uploading
      </span>
    );
  }
  if (status === "extracting") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21]">
        <Loader2 size={11} className="animate-spin" /> AI extracting
      </span>
    );
  }
  if (status === "extracted") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#3F6D55]">
        <Check size={11} /> {signalCount} signals
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21]">
        <AlertTriangle size={11} /> Failed
      </span>
    );
  }
  return null;
}

/* ============================================================
 * Reusable PDF download button (async, dynamic doc build, error-safe)
 * ============================================================ */
function PdfDownloadButton({ label, filename, buildDoc, Icon = Download, testid, small = false }) {
  const [busy, setBusy] = useState(false);
  const onClick = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await downloadPdf(buildDoc(), filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF generation failed", err);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  if (small) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#0F1825] font-mono text-[9.5px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors disabled:opacity-50"
        data-testid={testid}
      >
        <Icon size={11} className="text-[#C84C21]" />
        {busy ? "Generating…" : label}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#0F1825] font-mono text-[11px] uppercase tracking-[0.16em] px-4 py-2.5 transition-colors disabled:opacity-50"
      data-testid={testid}
    >
      <Icon size={14} className="text-[#C84C21]" />
      {busy ? "Generating…" : label}
    </button>
  );
}

function Block({ n, title, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{n}</span>
        <h3 className="font-serif text-[1.35rem] text-[#0F1825]">{title}</h3>
      </div>
      {children}
    </section>
  );
}
function Empty({ children }) {
  return <div className="bg-white border border-dashed border-[#E8E1D2] p-5 text-[13px] text-[#A09680]">{children}</div>;
}

/* =========================== SOURCE LIBRARY =========================== */
// PentaVoice™ stream metadata — single source of truth for stream labels,
// the comprehensive internal source taxonomy per bucket, and the value each
// stream contributes. Rendered by StreamMetaTable in both Source Library and
// Patient Signals.
const STREAM_META = [
  {
    k: "L1",
    name: "Digital & Social Listening",
    sources: [
      "Digital & social listening",
      "Patient communities",
      "Forums",
      "Blogs",
      "Advocacy group material",
      "Caregiver communities",
      "Patient-facing digital discussions",
    ],
    contributes: "Early lived-experience signals",
    sourceLibraryExample: "Social listening study report",
  },
  {
    k: "L2",
    name: "PROs & Surveys",
    sources: [
      "Published PRO / COA literature",
      "Validated instruments",
      "Patient surveys",
      "Preference studies",
      "Discrete choice experiments",
      "Trial exit surveys",
      "QoL and symptom-burden measures",
    ],
    contributes: "Structured patient-reported evidence",
    sourceLibraryExample: "PRO study, preference study, or service publication",
  },
  {
    k: "L3",
    name: "Patient Advisory",
    sources: [
      "Patient advisory boards",
      "Interviews",
      "Focus groups",
      "Co-creation panels",
      "Patient journey mapping",
      "Caregiver advisory",
      "Community organisation input",
    ],
    contributes: "Direct context and prioritisation",
    sourceLibraryExample: "Advisory board, focus group, or journey-mapping report",
  },
  {
    k: "L4",
    name: "RWD & RWE",
    sources: [
      "RWD / RWE publications",
      "Claims",
      "EHR",
      "Registries",
      "Patient support program data",
      "Patient assistance program data",
      "Medical information queries",
      "Call-centre insights",
      "Pharmacovigilance narratives",
      "Wearables",
      "Remote monitoring",
      "Observational studies",
    ],
    contributes: "Real-world burden, behaviour, and outcomes",
    sourceLibraryExample: "RWE study, registry pull, PSP / PAP data, or PV narrative report",
  },
  {
    k: "L5",
    name: "Regulatory & HTA Evidence",
    sources: [
      "Regulatory documents",
      "PFDD materials",
      "HTA assessments",
      "Payer-relevant evidence",
      "P&T testimony (where available)",
      "Labels, assessment reports, public decision records",
    ],
    contributes: "Decision-context alignment",
    sourceLibraryExample: "Regulatory, HTA, or P&T evidence document",
  },
];

const STREAM_META_BY_K = Object.fromEntries(STREAM_META.map((s) => [s.k, s]));

/**
 * Unified stream-metadata table. Replaces both the prior
 * "StreamContributionsTable" and the bottom tile-row stream selector — same
 * data, one component.
 *
 * @param {object} props
 * @param {boolean} props.interactive    — rows are clickable (selector mode)
 * @param {string=} props.activeKey      — highlight a row
 * @param {(k:string)=>void=} props.onSelect — row click handler
 * @param {object=} props.signalCounts   — { L1: 12, L2: 5, ... } (operator)
 * @param {boolean=} props.hideCounts    — explicitly suppress counts (client)
 */
function StreamMetaTable({ interactive = false, activeKey, onSelect, signalCounts, hideCounts }) {
  const showCounts = !hideCounts && signalCounts && Object.keys(signalCounts).length > 0;
  const Row = interactive ? "button" : "div";
  return (
    <section
      className="bg-white border border-[#E8E1D2] mb-5"
      data-testid="stream-meta-table"
    >
      <div className="px-5 py-3 border-b border-[#E8E1D2] flex items-baseline justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">The five PentaVoice™ streams</div>
        <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]">Sources covered · what each stream contributes</div>
      </div>
      <div>
        {STREAM_META.map((s, idx) => {
          const isActive = interactive && activeKey === s.k;
          const count = signalCounts?.[s.k] ?? 0;
          return (
            <Row
              key={s.k}
              type={interactive ? "button" : undefined}
              onClick={interactive && onSelect ? () => onSelect(s.k) : undefined}
              className={`w-full text-left grid grid-cols-12 gap-x-4 px-5 py-3.5 transition-colors ${
                idx < STREAM_META.length - 1 ? "border-b border-[#F0EBDC]" : ""
              } ${
                isActive ? "bg-[#FAF7F1] border-l-2 border-l-[#C84C21]" : interactive ? "border-l-2 border-l-transparent hover:bg-[#FAF7F1]/60" : ""
              }`}
              data-testid={`stream-row-${s.k}`}
            >
              {/* L code */}
              <div className="col-span-1 pt-1">
                <span className={`font-mono text-[10px] uppercase tracking-[0.22em] ${isActive ? "text-[#C84C21]" : "text-[#C84C21]"}`}>{s.k}</span>
              </div>
              {/* Stream name + sources caption */}
              <div className="col-span-12 md:col-span-7 -mt-0.5">
                <div className="text-[14.5px] text-[#0F1825] font-medium leading-tight">{s.name}</div>
                <div className="mt-1 text-[11.5px] text-[#5C6470] leading-snug">
                  {s.sources.join(" · ")}
                </div>
              </div>
              {/* What it contributes + (optional) count */}
              <div className="col-span-12 md:col-span-4 flex flex-col md:items-end gap-1.5 -mt-0.5">
                <div className="text-[12.5px] text-[#5C6470] leading-snug md:text-right">{s.contributes}</div>
                {showCounts && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]">
                    {count} <span className="text-[#A09680]">{count === 1 ? "signal" : "signals"}</span>
                  </span>
                )}
                {interactive && (
                  <ChevronRight size={12} className={`hidden md:block ${isActive ? "text-[#C84C21]" : "text-[#A09680]"}`} />
                )}
              </div>
            </Row>
          );
        })}
      </div>
      {/* Cross-cutting note — Congress / Scientific Narrative */}
      <div className="border-t border-[#E8E1D2] bg-[#FAF7F1]/60 px-5 py-2.5 flex items-baseline gap-3" data-testid="stream-cross-cutting-note">
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] shrink-0">Cross-cutting</span>
        <span className="text-[11.5px] text-[#5C6470] leading-snug">
          Congress abstracts and scientific narratives are cross-cutting evidence — tagged under <span className="font-mono text-[10px] text-[#C84C21]">L4</span> or <span className="font-mono text-[10px] text-[#C84C21]">L5</span> depending on content. They are not a separate bucket.
        </span>
      </div>
    </section>
  );
}

function SourceLibraryTab({ id, eng, setActive, isClient }) {
  const [data, setData] = useState({ sources: [], by_stream: {} });
  const [signalsByStream, setSignalsByStream] = useState({ L1: 0, L2: 0, L3: 0, L4: 0, L5: 0 });
  const [signalsLoaded, setSignalsLoaded] = useState(false);
  const [activityByStream, setActivityByStream] = useState({}); // per-stream provenance + latest-at
  const [composerStream, setComposerStream] = useState(null); // operator only — upload form open for this stream
  const [openStream, setOpenStream] = useState(null); // operator only — the one expanded stream (accordion)
  const [runsByStream, setRunsByStream] = useState(null); // null until loaded — every Sonar run per stream (summary)

  const loadRuns = async () => {
    try {
      const { data: d } = await axios.get(`${API}/console/engagements/${id}/prompt-runs`);
      const by = { L1: [], L2: [], L3: [], L4: [], L5: [] };
      (d?.runs || []).forEach((r) => { if (by[r.stream]) by[r.stream].push(r); });
      setRunsByStream(by);
    } catch { /* non-critical */ }
  };
  const reload = async () => {
    const [src, sig] = await Promise.all([
      axios.get(`${API}/console/engagements/${id}/sources`),
      axios.get(`${API}/console/engagements/${id}/signals`),
    ]);
    setData(src.data);
    const counts = { L1: 0, L2: 0, L3: 0, L4: 0, L5: 0 };
    const blank = () => ({ sonarCount: 0, uploadCount: 0, pasteCount: 0, latestAt: null });
    const activity = { L1: blank(), L2: blank(), L3: blank(), L4: blank(), L5: blank() };
    (sig.data?.signals || []).forEach((s) => {
      if (counts[s.stream] === undefined) return;
      counts[s.stream] += 1;
      const a = activity[s.stream];
      const via = s.ingested_via || "seed";
      if (via === "perplexity_sonar") a.sonarCount += 1;
      else if (via === "signal_extraction") a.uploadCount += 1;
      else a.pasteCount += 1;
      const ts = s.ingested_at || s.captured_at;
      if (ts && (!a.latestAt || ts > a.latestAt)) a.latestAt = ts;
    });
    setSignalsByStream(counts);
    setActivityByStream(activity);
    setSignalsLoaded(true);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); loadRuns(); }, [id]);

  const anyRunning = !!runsByStream && Object.values(runsByStream).some((rs) => rs.some((r) => r.status === "running"));
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(loadRuns, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRunning, id]);

  // Open the stream that needs attention first: a run in flight, else the
  // first stream with nothing committed yet, else L1.
  useEffect(() => {
    if (openStream !== null || !runsByStream || !signalsLoaded) return;
    const running = STREAM_ORDER.find((k) => (runsByStream[k] || []).some((r) => r.status === "running"));
    const empty = STREAM_ORDER.find((k) => !(signalsByStream[k] > 0));
    setOpenStream(running || empty || "L1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsByStream, signalsLoaded]);

  const scrollToStream = (k) => {
    setTimeout(() => document.getElementById(`source-stream-anchor-${k}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };
  const jumpTo = (k) => { setOpenStream(k); scrollToStream(k); };
  const advanceFrom = (k) => {
    const next = STREAM_ORDER[STREAM_ORDER.indexOf(k) + 1] || null;
    setOpenStream(next);
    if (next) scrollToStream(next);
    else toast.success("All five streams have committed signals.");
  };

  const totalUploaded = data.sources.length;
  const totalExtracted = data.sources
    .filter((s) => s.extraction_status === "extracted")
    .reduce((a, s) => a + (s.extracted_signal_count || 0), 0);
  const totalSignals = Object.values(signalsByStream).reduce((a, n) => a + n, 0);

  return (
    <div data-testid="sources-tab">
      {isClient ? (
        <>
          <TabHeader
            eyebrow="Section · Source Library Status"
            title="Where your evidence comes from."
            lede="A status view of the five PentaVoice™ streams contributing evidence to this engagement — and how many patient signals each stream has surfaced for review."
          />
          <ClientOutput>
            A simple status view of the five PentaVoice™ streams contributing evidence to this engagement.
          </ClientOutput>
        </>
      ) : (
        <div className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">Stage 02 of 06 · Source Library</div>
          <h2 className="font-serif text-[1.6rem] text-[#0F1825] leading-tight">Evidence per stream.</h2>
          <p className="mt-1 text-[12.5px] text-[#5C6470]">Work each stream top to bottom: run the prompt once, review, commit — the next stream opens automatically.</p>
        </div>
      )}

      {isClient && <ClientSourceStatusGrid engagementId={id} />}

      {!isClient && (
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 mb-5 pb-4 border-b border-[#E8E1D2]" data-testid="sources-summary">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">Total signals</span>
            <span className="font-serif text-[1.4rem] text-[#0F1825] font-medium leading-none">{totalSignals}</span>
          </div>
          <div className="hidden md:block w-px h-5 bg-[#E8E1D2]" />
          <div className="flex items-baseline gap-2 flex-wrap">
            {STREAM_ORDER.map((k) => {
              const n = signalsByStream[k] || 0;
              return (
                <button
                  type="button"
                  key={k}
                  onClick={() => jumpTo(k)}
                  className={`inline-flex items-baseline gap-1.5 font-mono text-[11px] px-2 py-0.5 border transition-colors hover:border-[#0F1825] ${
                    openStream === k ? "border-[#0F1825] bg-[#0F1825] text-white" :
                    n > 0 ? "border-[#C84C21]/40 bg-[#FCE9E1] text-[#0F1825]" : "border-[#E8E1D2] bg-white text-[#A09680]"
                  }`}
                  data-testid={`sources-stream-chip-${k}`}
                  title={`Open ${k}`}
                >
                  <span className="uppercase tracking-[0.16em]">{k}</span>
                  <span className="font-medium">{n}</span>
                </button>
              );
            })}
          </div>
          <div className="hidden md:block w-px h-5 bg-[#E8E1D2]" />
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">Source files</span>
            <span className="font-mono text-[12.5px] text-[#0F1825]">{totalUploaded}</span>
            {totalUploaded > 0 && (
              <span className="text-[#A09680] text-[11px]">({totalExtracted} extracted)</span>
            )}
          </div>
        </div>
      )}

      {!isClient && (
      <div className="space-y-3">
        {STREAM_META.map((s) => {
          const items = data.by_stream?.[s.k] || [];
          const ex = s.sourceLibraryExample;
          const streamRuns = runsByStream?.[s.k] || [];
          const open = openStream === s.k;
          return (
            <section
              key={s.k}
              id={`source-stream-anchor-${s.k}`}
              className={`bg-white border scroll-mt-32 transition-colors ${open ? "border-[#0F1825]" : "border-[#E8E1D2]"}`}
              data-testid={`source-stream-${s.k}`}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => setOpenStream(open ? null : s.k)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenStream(open ? null : s.k); } }}
                className="w-full text-left px-6 py-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:bg-[#FAF7F1] transition-colors"
                data-testid={`source-stream-toggle-${s.k}`}
                aria-expanded={open}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 min-w-0 flex-wrap">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">{s.k}</span>
                    <span className="text-[#A09680]">·</span>
                    <h3 className="font-serif text-[1.15rem] text-[#0F1825] truncate">{s.name}</h3>
                    <span className="text-[#A09680] text-[11px] hidden md:inline">·</span>
                    <span className="text-[12.5px] text-[#5C6470] truncate hidden md:inline">{s.contributes}</span>
                  </div>
                  {open && (
                    <p className="mt-1.5 text-[11.5px] text-[#A09680] leading-snug">
                      <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#5C6470]">Sources covered ·</span>{" "}
                      {(s.sources || []).slice(0, 6).join(" · ")}
                      {(s.sources || []).length > 6 && " · …"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <StreamStatusChips streamKey={s.k} signalCount={signalsByStream[s.k] || 0} runs={streamRuns} />
                  {open && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setComposerStream(composerStream === s.k ? null : s.k); }}
                      className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] text-white font-mono text-[10.5px] uppercase tracking-[0.16em] px-3 py-2 transition-colors"
                      data-testid={`source-upload-cta-${s.k}`}
                    >
                      <Upload size={12} />
                      Upload source report
                    </button>
                  )}
                  <ChevronDown size={16} className={`text-[#A09680] transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
              </div>

              {open && (
                <div className="border-t border-[#E8E1D2]" data-testid={`source-stream-body-${s.k}`}>
                  <RunPromptBox
                    streamCode={s.k}
                    brief={eng?.stream_briefs?.[s.k] || null}
                    engagementId={id}
                    eng={eng}
                    runs={streamRuns}
                    runsLoaded={!!runsByStream}
                    streamSignalCount={signalsByStream[s.k] || 0}
                    onRunsChanged={loadRuns}
                    onSignalsCommitted={(res) => { reload(); loadRuns(); if (res?.inserted_count > 0) advanceFrom(s.k); }}
                  />

                  {composerStream === s.k && (
                    <SourceComposer
                      engagementId={id}
                      stream={s.k}
                      example={ex}
                      onClose={() => setComposerStream(null)}
                      onUploaded={async () => { setComposerStream(null); await reload(); }}
                    />
                  )}

                  <div className="p-4 border-t border-[#F0EBDC]">
                    {items.length === 0 ? (
                      <StreamActivityLine
                        streamKey={s.k}
                        signalCount={signalsByStream[s.k] || 0}
                        activity={activityByStream[s.k] || {}}
                      />
                    ) : (
                      <ul className="space-y-3">
                        {items.map((src) => (
                          <SourceCard
                            key={src.id}
                            engagementId={id}
                            source={src}
                            onChange={reload}
                            onJumpToSignals={() => setActive && setActive("signals")}
                            isClient={isClient}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
      )}
    </div>
  );
}

// ClientSourceStatusGrid — the simplified 5-stream status view shown to
// clients. Replaces the operator-only per-source table for client mode.
// Status taxonomy per the product spec: Included · Under review ·
// Evidence gap identified · Optional follow-up · Not available. The
// component fetches signal counts per stream so the status is driven by
// real engagement coverage data, not by raw uploads.
function ClientSourceStatusGrid({ engagementId, compact }) {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/console/engagements/${engagementId}/signals`);
      setCounts(data.counts_by_stream || {});
    })();
  }, [engagementId]);

  const STREAM_STATUS_META = {
    "Included":                { wrap: "border-[#3F6D55]/50 bg-[#F0F4F1]",           dot: "bg-[#3F6D55]", label: "text-[#3F6D55]" },
    "Under review":            { wrap: "border-[#E8D08A] bg-[#FDF7E6]",              dot: "bg-[#9A6B1A]", label: "text-[#9A6B1A]" },
    "Evidence gap identified": { wrap: "border-[#C84C21]/40 bg-[#C84C21]/[0.04]",    dot: "bg-[#C84C21]", label: "text-[#C84C21]" },
    "Optional follow-up":      { wrap: "border-[#A09680]/40 bg-white",               dot: "bg-[#A09680]", label: "text-[#5C6470]" },
    "Not available":           { wrap: "border-dashed border-[#C8C0AE] bg-white",    dot: "bg-[#C8C0AE]", label: "text-[#A09680]" },
  };
  // Status derivation: ≥3 signals → Included; 1-2 → Under review;
  // 0 → Not available. Future engagements can override per stream.
  const statusFor = (k) => {
    const c = counts?.[k] ?? 0;
    if (c >= 3) return "Included";
    if (c >= 1) return "Under review";
    return "Not available";
  };
  return (
    <div className={compact ? "" : "mb-8"} data-testid="client-source-status">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {STREAM_META.map((s) => {
          const status = statusFor(s.k);
          const c = counts?.[s.k] ?? 0;
          const meta = STREAM_STATUS_META[status];
          return (
            <div
              key={s.k}
              className={`border ${meta.wrap} p-4 flex flex-col`}
              data-testid={`client-stream-status-${s.k}`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{s.k}</span>
                <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-medium ${meta.label}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {status}
                </span>
              </div>
              <div className="text-[13px] text-[#0F1825] font-medium leading-tight mb-1">{s.name}</div>
              <div className="text-[11.5px] text-[#5C6470] leading-snug flex-1">{s.contributes}</div>
              {c > 0 && (
                <div className="mt-3 pt-2 border-t border-[#F0EBDC] text-[11px] text-[#5C6470]">
                  <span className="font-medium text-[#0F1825]">{c}</span> patient {c === 1 ? "signal" : "signals"} contributing
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className={`text-[11.5px] text-[#A09680] leading-relaxed ${compact ? "mt-3" : "mt-4"}`}>
        Status taxonomy · Included · Under review · Evidence gap identified · Optional follow-up · Not available.
        {compact ? "" : " Voice2Evidence selects and operates the streams relevant to your engagement; not every stream is activated in every cycle."}
      </div>
    </div>
  );
}

function SummaryCell({ label, value, suffix }) {
  return (
    <div className="bg-white border border-[#E8E1D2] p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-3xl text-[#0F1825]">{value}</span>
        {suffix && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">{suffix}</span>}
      </div>
    </div>
  );
}

/* ======================== PATIENT EXPERIENCE CONCEPT MAP ========================
 * The cross-walk view. Maps the five PentaVoice(TM) source-native taxonomies
 * (L1 SML buckets, L2 PRO instruments, L3 advisory themes, L4 RWE topic
 * codes, L5 reg / HTA concepts) onto the V2E patient-experience domain
 * framework (Symptom / Functional / QoL / Treatment burden / Access /
 * Caregiver / Preference). This is the methodological IP — the cross-walk
 * is what lets heterogeneous source taxonomies speak in one shared
 * language so triangulation downstream can operate on a single concept set.
 * ============================================================================ */

// =============================================================================
// V2E Framework — the proprietary patient-experience taxonomy.
//
// 5 L1 themes × 3 L2 sub-themes = 15 sub-themes. Fixed at both levels for
// v1.0. L3 concepts under each sub-theme are engagement-configurable (carried
// in the seed signals' theme_tags + raw_text). Mirrors /api/console/framework
// returned from /app/backend/v2e_framework.py — keep them in sync.
// =============================================================================
const V2E_FRAMEWORK_VERSION = "1.0";

const V2E_THEMES = [
  {
    key: "A",
    label: "Awareness, Understanding & Information",
    short_label: "Awareness & Information",
    accent: "#3F6D55",
    subthemes: [
      { key: "A1", label: "Awareness & initial discovery" },
      { key: "A2", label: "Understanding & perceptions" },
      { key: "A3", label: "Information sources & trust" },
    ],
  },
  {
    key: "B",
    label: "Help-Seeking, Diagnosis & Access",
    short_label: "Help-Seeking & Access",
    accent: "#3D5A8C",
    subthemes: [
      { key: "B1", label: "Help-seeking & diagnostic journey" },
      { key: "B2", label: "Access & affordability" },
      { key: "B3", label: "System navigation & practical barriers" },
    ],
  },
  {
    key: "C",
    label: "Treatment Decision-Making & Journey",
    short_label: "Treatment & Journey",
    accent: "#C84C21",
    subthemes: [
      { key: "C1", label: "Treatment choices & decision-making" },
      { key: "C2", label: "Treatment experience & perceived effects" },
      { key: "C3", label: "Tolerability, modification & discontinuation" },
    ],
  },
  {
    key: "D",
    label: "Burden, Quality of Life & Support",
    short_label: "Burden, QoL & Support",
    accent: "#3A8C9A",
    subthemes: [
      { key: "D1", label: "Impact on daily life, roles & activities" },
      { key: "D2", label: "Emotional well-being & mental health" },
      { key: "D3", label: "Social support, caregiving & community" },
    ],
  },
  {
    key: "E",
    label: "Prevention, Monitoring, Research & System Experience",
    short_label: "Prevention, Monitoring & System",
    accent: "#9A6B1A",
    subthemes: [
      { key: "E1", label: "Prevention, risk & testing/screening" },
      { key: "E2", label: "Monitoring, follow-up & long-term management" },
      { key: "E3", label: "Healthcare communication, trust & advocacy" },
    ],
  },
];

// Flat list of all 15 sub-themes (in framework order) — used for matrix rows.
const V2E_SUBTHEMES = V2E_THEMES.flatMap((t) =>
  t.subthemes.map((s) => ({
    key: s.key,
    label: s.label,
    theme_key: t.key,
    theme_label: t.label,
    theme_short_label: t.short_label,
    theme_accent: t.accent,
  }))
);

const V2E_SUBTHEME_LOOKUP = Object.fromEntries(V2E_SUBTHEMES.map((s) => [s.key, s]));
const V2E_THEME_LOOKUP = Object.fromEntries(V2E_THEMES.map((t) => [t.key, t]));

// -----------------------------------------------------------------------------
// Heuristic mapper: signal → V2E sub-theme keys. Inferred from headline_theme
// + raw_text. Theme tags excluded (they carry abstract concept handles that
// don't indicate L2 placement). An explicit `v2e_subthemes` array on the seed
// entry overrides the heuristic.
// -----------------------------------------------------------------------------
const SUBTHEME_PATTERNS = {
  A1: /awareness|never heard of|initial discovery|first heard|disease awareness/i,
  A2: /understand\w*|perception|belief|confusion|mis[- ]?attribut|mis[- ]?label|eligib(?:le|ility)|biomarker (?:eligibility|confusion|understand)|patient understanding|health literacy|comprehen/i,
  A3: /information source|misinformation|trust\w*|hcp educat(?:ion|ed)|patient educat\w*|conversation guide/i,

  B1: /diagnos\w*|diagnostic delay|diagnostic journey|referral|help[- ]?seek|specialist|cornea specialist|symptom onset|months to diagnos|time to diagnos/i,
  B2: /afford|cost|out[- ]of[- ]pocket|copay|coverage|payer|hta|financial toxicity|affordability|patient assistance|reimburs|insurance|tier[- ]2|tier[- ]3/i,
  B3: /system navigation|practical barrier|geograph|site of care|site[- ]of[- ]care|community oncology|community clinic|tertiary|chair[- ]time|travel|logistics|workday|workdays|workflow|throughput|capacity|country[- ]specific|uneven adoption|nice ta|cadth|nmpa/i,

  C1: /preference|attribute|trade[- ]off|durab|mode of administration|sc vs iv|oral vs (?:injection|depot)|subcutaneous|combination expansion|treatment choice|treatment decision|patient[- ]preference (?:study|protocol|sub[- ]?study)|ich e22/i,
  C2: /treatment experience|perceived effect|hot[- ]flash|hot[- ]flush|side[- ]effect|cardiovascular safety|cv safety|cv signal|differential|qlq[- ]?pr25|fact[- ]?p|fact[- ]?l|nei[- ]?vfq|osdi|wpai|eq[- ]?5d|ir[- ]?ae|toxicity|treatment burden|burden score/i,
  C3: /toleranc|tolerabil|adheren|persistence|discontinuation|drop[- ]out|drop[- ]?off|missed dose|set an alarm|modification|switching|stretch\w*|completion rate|early discontinuation/i,

  D1: /impact on daily life|daily life|roles? & activities|role function|activities of daily living|adl|functional vision|functional impact|reading|screen[- ]time|driving|return[- ]to[- ]function|employment|work productivity|work impairment|fine[- ]detail/i,
  D2: /emotional well|mental health|anxiety|fear|distress|depress|sense of self|intimacy|sexual function|vision[- ]?anxiety|psychosocial|hopeful|frustrat/i,
  D3: /caregiv|caregiver|family|household|spouse|partner|loved one|social support|community|advocacy partnership|advocacy partner|patient[- ]?community|patient[- ]?network|zero|ustoo|focus group/i,

  E1: /prevention|risk\b|testing|screening|biomarker test|companion diagnos|msi[- ]?h|dmmr|under[- ]coded|under[- ]coding|under[- ]recognis|epidemiology|prevalence|under[- ]measure|early detection/i,
  E2: /monitor|follow[- ]up|long[- ]?term management|endpoint|pivotal|protocol|trial design|corvita|phase 3|sub[- ]?study|registry|rwe|real[- ]?world|longitudinal|next[- ]cycle/i,
  E3: /healthcare communication|hcp communication|hcp.{0,30}(framework|materials|conversation)|advocacy|trust|provider trust|patient[- ]hcp|scientific exchange|sciexch|conversation framework|payer narrative/i,
};

function inferSubthemesForSignal(s) {
  if (Array.isArray(s.v2e_subthemes) && s.v2e_subthemes.length > 0) return s.v2e_subthemes;
  const haystack = [s.headline_theme || "", s.raw_text || ""].join(" ");
  const hits = Object.entries(SUBTHEME_PATTERNS)
    .filter(([, re]) => re.test(haystack))
    .map(([k]) => k);
  return hits.length > 0 ? hits : ["C2"]; // C2 is the modal clinical-signal bucket
}

// Heuristic mapper from the legacy 7-domain scheme has been replaced by
// inferSubthemesForSignal above (V2E Framework).

// Display label for a signal in the matrix cell — uses the aggregated
// headline_theme when available, otherwise the first theme tag.
function signalShortLabel(s) {
  if (s.headline_theme) return s.headline_theme;
  if (s.theme_tags && s.theme_tags.length > 0) return s.theme_tags.join(" · ");
  return (s.raw_text || "").slice(0, 80) + "…";
}

// -----------------------------------------------------------------------------
// Sentiment — the direction the signal/finding points in.
//
// Three states only — matches the L1 SML sentiment_distribution vocabulary so
// the whole workspace speaks one language end-to-end (Patient Signals → Concept
// Map → Triangulation → Outputs by Team).
//
//   positive — patient-beneficial / asset-favourable / improvement (green)
//   negative — patient burden / barrier / unfavourable             (red)
//   neutral  — mixed direction OR neutral reference / regulatory   (white)
//
// Sentiment is inferred client-side from existing seed fields (sentiment_distribution
// for L1 SML; sentiment + keyword cues for L2-L5) so it stays in sync with the
// underlying data. An explicit `polarity` field on a seed entry overrides.
// -----------------------------------------------------------------------------
const POSITIVE_PATTERNS = /(favour\w*|advantage|differentiator|superior|restored?|restoring|reduces?|reduction|reduced|improve\w*|prefer\w*|easier|time saved|hours back|chair[- ]time reduction|throughput up|once[- ]daily|durab|single[- ]administration|hopeful|positive|decentralis\w*|shifted to|fewer|shorter|faster|higher adheren\w*|recommend\w* relugolix|recommend\w* the asset|cited (?:as )?support\w*|supported by|supportive of|acceptance of|accept\w* structured patient)/i;
const NEGATIVE_PATTERNS = /(burden|barrier|delay|gap|toxicity|financial toxicity|missed dose|adherence drop|drop[- ]?out|impair|decrement|distress|anxiety|fear|concern\w*|lost workdays?|cancelled|out[- ]of[- ]pocket|costly|impossible|never mentioned|under[- ]measure|under[- ]specify|under[- ]reported|under[- ]quantified|under[- ]coded|frustrated|inequit|unequal|sting|fiddly|exhaust\w*|missing layer|asymmetric|uneven|opaque|confusion|adherence|persistence dropped|adherence dropped)/i;

function getPolarity(s) {
  // Explicit `polarity` field on the seed entry is authoritative. Includes
  // a back-compat path for any rows that still carry the legacy 4-state
  // values (advantage/burden/mixed/reference).
  if (s.polarity) {
    const map = { positive: "positive", negative: "negative", neutral: "neutral",
                  advantage: "positive", burden: "negative", mixed: "neutral", reference: "neutral" };
    const v = map[s.polarity];
    if (v) return v;
  }
  // L1 SML: sentiment_distribution is authoritative.
  if (s.sentiment_distribution) {
    const pos = s.sentiment_distribution.positive ?? 0;
    const neg = s.sentiment_distribution.negative ?? 0;
    if (pos - neg >= 15) return "positive";
    if (neg - pos >= 15) return "negative";
    return "neutral";
  }
  // For L2-L5 the haystack is the narrative only — theme_tags carry abstract
  // concept labels (e.g. 'Treatment burden') that don't indicate direction.
  const hay = `${s.headline_theme || ""} ${s.raw_text || ""}`;
  const pos = POSITIVE_PATTERNS.test(hay);
  const neg = NEGATIVE_PATTERNS.test(hay);
  const sent = (s.sentiment || "").toLowerCase();
  if (sent === "reference") {
    if (pos && !neg) return "positive";
    if (neg && !pos) return "negative";
    return "neutral";
  }
  if (["positive", "hopeful"].includes(sent)) return "positive";
  if (["negative", "frustrated", "concerned", "anxious", "fearful"].includes(sent)) return "negative";
  if (["mixed", "constructive"].includes(sent)) {
    if (pos && !neg) return "positive";
    if (neg && !pos) return "negative";
    return "neutral";
  }
  if (sent === "quantitative") {
    if (pos && !neg) return "positive";
    if (neg && !pos) return "negative";
    return "neutral";
  }
  return "neutral";
}

const POLARITY_META = {
  positive: { label: "Positive", dot: "bg-[#3F6D55]",                         legend: "Patient-beneficial / asset-favourable signal" },
  negative: { label: "Negative", dot: "bg-[#B83A28]",                         legend: "Patient burden / barrier / unfavourable signal" },
  neutral:  { label: "Neutral",  dot: "bg-white border border-[#5C6470]",     legend: "Mixed / trade-off / neutral reference signal" },
};

// A small colored dot — directional cue without competing with the statement
// for attention. Neutral uses a visible slate border so the white dot reads
// against white card backgrounds.
function PolarityDot({ p }) {
  const meta = POLARITY_META[p] || POLARITY_META.neutral;
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${meta.dot}`}
      title={meta.label}
      data-testid={`polarity-${p}`}
      data-polarity={p}
    />
  );
}

function dominantCellPolarity(signals) {
  if (!signals.length) return null;
  const counts = { positive: 0, negative: 0, neutral: 0 };
  signals.forEach((s) => { counts[getPolarity(s)] += 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

const CELL_BORDER_BY_POLARITY = {
  positive: "border-l-2 border-l-[#3F6D55]/60",
  negative: "border-l-2 border-l-[#B83A28]/60",
  neutral:  "border-l-2 border-l-[#5C6470]/40",
};

// Stream-specific accent for the matrix cell pill.
const STREAM_CELL_ACCENT = {
  L1: "border-[#E8E1D2] bg-white",
  L2: "border-[#E8E1D2] bg-white",
  L3: "border-[#E8E1D2] bg-white",
  L4: "border-[#E8E1D2] bg-white",
  L5: "border-[#E8E1D2] bg-white",
};

function ConceptMapTab({ id, isClient }) {
  const [signals, setSignals] = useState([]);
  const [findings, setFindings] = useState([]);
  const [chainOpen, setChainOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, f] = await Promise.all([
        axios.get(`${API}/console/engagements/${id}/signals`),
        axios.get(`${API}/console/engagements/${id}/findings`),
      ]);
      setSignals(s.data.signals || []);
      setFindings(f.data.findings || []);
    })();
  }, [id]);

  // Build the matrix: subtheme_key → stream → list of signals.
  const matrix = useMemo(() => {
    const m = {};
    V2E_SUBTHEMES.forEach((s) => {
      m[s.key] = { L1: [], L2: [], L3: [], L4: [], L5: [] };
    });
    signals.forEach((sig) => {
      const subthemes = inferSubthemesForSignal(sig);
      subthemes.forEach((stkey) => {
        if (m[stkey] && m[stkey][sig.stream]) m[stkey][sig.stream].push(sig);
      });
    });
    return m;
  }, [signals]);

  // Per-stream unique signal counts — shown in the column headers so the
  // operator sees how many DISTINCT signals feed the cross-walk. A signal
  // mapped to 2-3 sub-themes (e.g. fatigue → D1+D2) appears in multiple
  // cells by design; this column total counts each signal once.
  const streamUniqueCounts = useMemo(() => {
    const counts = { L1: 0, L2: 0, L3: 0, L4: 0, L5: 0 };
    signals.forEach((sig) => {
      if (counts[sig.stream] !== undefined) counts[sig.stream] += 1;
    });
    return counts;
  }, [signals]);

  // Map findings to their sub-theme — every finding now carries an explicit
  // v2e_subtheme field. Used for the per-row "triangulated implications" count.
  const findingsBySubtheme = useMemo(() => {
    const buckets = {};
    V2E_SUBTHEMES.forEach((s) => { buckets[s.key] = []; });
    findings.forEach((f) => {
      const key = f.v2e_subtheme && buckets[f.v2e_subtheme] ? f.v2e_subtheme : null;
      if (key) buckets[key].push(f);
    });
    return buckets;
  }, [findings]);

  const STREAMS = ["L1", "L2", "L3", "L4", "L5"];
  const STREAM_BY_KEY = Object.fromEntries(STREAM_META.map((s) => [s.k, s]));

  return (
    <div data-testid="concept-map-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Patient Experience Concept Map" : "Stage 04 of 06 · Concept Map"}
        title="The cross-walk — every stream mapped onto the V2E Framework."
        lede="Each of the five PentaVoice™ streams speaks its own native taxonomy. The Concept Map cross-walks them onto the V2E Framework — 5 themes × 3 sub-themes — so triangulation downstream operates on one shared concept set."
      />

      {/* V2E Framework credentialing chip — names the proprietary
          framework so the prospect sees the IP spine in one glance. */}
      <div className="mb-4 inline-flex items-center gap-2 border border-[#E8E1D2] bg-white px-3 py-1.5 text-[10.5px] font-mono uppercase tracking-[0.14em] text-[#5C6470]" data-testid="v2e-framework-chip">
        <span className="text-[#C84C21] font-medium">V2E Framework</span>
        <span className="text-[#A09680]">·</span>
        <span>5 themes</span>
        <span className="text-[#A09680]">·</span>
        <span>15 sub-themes</span>
      </div>

      {/* The chain — collapsible. Default compact: a single-line strip with
          dots + "How to read this" affordance. Expands to show the full chain
          prose + per-dot definitions. Keeps the matrix above-the-fold. */}
      <div className="bg-white border border-[#E8E1D2] mb-4" data-testid="concept-chain">
        <button
          type="button"
          onClick={() => setChainOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-[#FAF7F1]/50 transition-colors"
          data-testid="concept-chain-toggle"
          aria-expanded={chainOpen}
        >
          <div className="flex items-center gap-2 flex-wrap text-[12px] text-[#0F1825]" data-testid="polarity-legend">
            <span className="text-[#A09680] uppercase tracking-[0.12em] font-mono text-[10px]">How to read this</span>
            <span className="text-[#A09680]">—</span>
            {(["positive", "negative", "neutral"]).map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5" data-testid={`polarity-${p}`}>
                <PolarityDot p={p} />
                <span>{POLARITY_META[p].label}</span>
              </span>
            ))}
            <span className="text-[#A09680] ml-1">· empty cell = evidence gap</span>
          </div>
          <ChevronDown size={14} className={`text-[#5C6470] transition-transform shrink-0 ${chainOpen ? "rotate-180" : ""}`} />
        </button>
        {chainOpen && (
          <div className="px-4 pb-4 pt-1 border-t border-[#F0EBDC]" data-testid="polarity-legend-detail">
            <div className="text-[12px] text-[#A09680] font-medium mb-2">The chain — what the Concept Map does</div>
            <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#0F1825]">
              <span className="font-medium">Stream-native finding</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">V2E concept</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">V2E domain</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">Direction</span>
            </div>
            <p className="mt-2 text-[12px] text-[#5C6470] leading-relaxed">
              Each stream-native finding is mapped to the V2E patient-experience domain it sits in, with a directional tag — turning five heterogeneous taxonomies into one shared concept set. <span className="text-[#0F1825]">Evidence implications are produced in the next stage</span> (<span className="font-medium">Triangulation View</span>), where mapped findings within each domain are converged across streams. Empty cells flag an <span className="text-[#0F1825]">evidence gap</span> — a sub-theme where this engagement did not surface evidence from that stream.
            </p>
            <div className="mt-3 pt-3 border-t border-[#F0EBDC]">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680] mb-2">Direction key</div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-[#5C6470]">
                {(["positive", "negative", "neutral"]).map((p) => (
                  <span key={p} className="inline-flex items-center gap-2">
                    <PolarityDot p={p} />
                    <span className="text-[#0F1825]">{POLARITY_META[p].label}</span>
                    <span>· {POLARITY_META[p].legend}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cross-walk matrix — consistent spacing. table-fixed + equal-width
          stream columns; uniform p-3 cell padding; populated cells and
          evidence-gap placeholders share the same min-height so rows are
          visually rectangular; inner finding cards share a fixed gap so
          the vertical rhythm reads cleanly across the whole grid. */}
      <div className="bg-white border border-[#E8E1D2] overflow-auto max-h-[78vh]" data-testid="cross-walk-table">
        <table className="w-full table-fixed text-[12.5px]">
          <colgroup>
            <col style={{ width: "16%" }} />
            <col style={{ width: "16.8%" }} />
            <col style={{ width: "16.8%" }} />
            <col style={{ width: "16.8%" }} />
            <col style={{ width: "16.8%" }} />
            <col style={{ width: "16.8%" }} />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#FAF7F1]">
              <th className="text-left px-3 py-2.5 text-[11.5px] text-[#5C6470] font-medium border-b border-[#E8E1D2] bg-[#FAF7F1]">V2E Sub-theme</th>
              {STREAMS.map((k) => (
                <th key={k} className="text-left px-3 py-2.5 text-[11.5px] text-[#C84C21] font-medium border-b border-l border-[#E8E1D2] bg-[#FAF7F1]">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span>{k}</span>
                    <span className="font-normal text-[10px] text-[#A09680]">{STREAM_BY_KEY[k]?.name}</span>
                    <span
                      className="font-mono text-[9.5px] text-[#5C6470] bg-white border border-[#E8E1D2] px-1.5 py-[1px] rounded-sm"
                      title={`${streamUniqueCounts[k] || 0} unique signal${streamUniqueCounts[k] === 1 ? "" : "s"} feeding ${k}. A multi-domain signal appears in more than one row by design.`}
                      data-testid={`crosswalk-stream-count-${k}`}
                    >
                      {streamUniqueCounts[k] || 0}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {V2E_THEMES.map((theme) => (
              <React.Fragment key={theme.key}>
                {/* L1 theme banner row — coloured strip that groups the 3
                    sub-theme rows beneath it. */}
                <tr data-testid={`concept-theme-${theme.key}`}>
                  <td colSpan={6} className="p-0">
                    <div
                      className="flex items-baseline gap-2 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.16em] text-white"
                      style={{ backgroundColor: theme.accent }}
                    >
                      <span className="text-[12px] font-semibold">{theme.key}</span>
                      <span className="opacity-90">·</span>
                      <span className="font-semibold tracking-[0.12em]">{theme.label}</span>
                    </div>
                  </td>
                </tr>
                {theme.subthemes.map((sub) => {
                  const findingsThisSub = findingsBySubtheme[sub.key] || [];
                  return (
                    <tr key={sub.key} className="border-b border-[#F0EBDC] last:border-b-0 align-top" data-testid={`concept-subtheme-${sub.key}`}>
                      {/* Sub-theme label cell — same padding as the stream
                          cells so the row reads as a single horizontal band. */}
                      <td className="p-3 align-top bg-[#FAF7F1]/30" style={{ borderLeft: `3px solid ${theme.accent}` }}>
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className="font-mono text-[10px] text-[#A09680] shrink-0">{sub.key}</span>
                          <span className="font-serif text-[13px] text-[#0F1825] leading-tight">{sub.label}</span>
                        </div>
                        {findingsThisSub.length > 0 && (
                          <div className="mt-2 text-[10.5px] text-[#A09680]">
                            {findingsThisSub.length} triangulated {findingsThisSub.length === 1 ? "insight" : "insights"}
                          </div>
                        )}
                      </td>

                      {/* 5 stream cells — uniform p-3 padding. Both
                          populated cells and Evidence-gap placeholders use
                          the same min-height so the row's vertical rhythm
                          is visually rectangular regardless of content. */}
                      {STREAMS.map((k) => {
                        const cellSignals = matrix[sub.key][k];
                        return (
                          <td key={k} className="p-3 align-top border-l border-[#F0EBDC]" data-testid={`crosswalk-cell-${sub.key}-${k}`}>
                            {cellSignals.length === 0 ? (
                              <div
                                className="border border-dashed border-[#C8C0AE] bg-[#FAF7F1]/30 px-2.5 py-2 text-[10.5px] text-[#A09680] italic min-h-[44px] flex items-center justify-center"
                                title="No evidence captured for this sub-theme from this stream in the current cycle"
                              >
                                Evidence gap
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {cellSignals.slice(0, 3).map((s) => {
                                  const pol = getPolarity(s);
                                  return (
                                    <div
                                      key={s.id}
                                      className={`border ${STREAM_CELL_ACCENT[k]} ${CELL_BORDER_BY_POLARITY[pol]} px-2.5 py-2 text-[12px] text-[#0F1825] leading-snug`}
                                      data-testid={`crosswalk-signal-${s.id}`}
                                      data-polarity={pol}
                                    >
                                      <span className="flex items-start gap-2">
                                        <span className="pt-[3px] shrink-0"><PolarityDot p={pol} /></span>
                                        <span className="min-w-0"><AutoAbbr text={signalShortLabel(s)} /></span>
                                      </span>
                                    </div>
                                  );
                                })}
                                {cellSignals.length > 3 && (
                                  <div className="text-[10px] text-[#A09680] italic pt-0.5">+ {cellSignals.length - 3} more</div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourceComposer({ engagementId, stream, example, onClose, onUploaded }) {
  const [title, setTitle] = useState("");
  const [methodologist, setMethodologist] = useState("");
  // Multi-file: an array of File objects, processed sequentially on submit
  // so progress is observable per file.
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [err, setErr] = useState("");

  const allowedMime = new Set([
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ]);
  const allowedExt = /\.(pdf|pptx?|docx?)$/i;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!title.trim()) { setErr("Study title is required."); return; }
    if (!files.length) { setErr("Attach one or more PDF / PPTX / DOCX files."); return; }
    for (const f of files) {
      if (!(allowedMime.has(f.type) || allowedExt.test(f.name))) {
        setErr(`Unsupported file type: ${f.name}. Only PDF, PPTX, or DOCX accepted.`); return;
      }
      if (f.size > 25 * 1024 * 1024) { setErr(`${f.name} exceeds the 25 MB limit.`); return; }
    }

    setBusy(true);
    setProgress({ done: 0, total: files.length, current: "" });
    try {
      // Upload sequentially so each file's source record + extraction
      // can be polled independently in the source list below.
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress({ done: i, total: files.length, current: f.name });
        const perFileTitle = files.length === 1 ? title.trim() : `${title.trim()} · ${f.name}`;
        const form = new FormData();
        form.append("file", f, f.name);
        const url = `${API}/console/engagements/${engagementId}/sources?stream=${encodeURIComponent(stream)}&study_title=${encodeURIComponent(perFileTitle)}${methodologist.trim() ? `&methodologist=${encodeURIComponent(methodologist.trim())}` : ""}`;
        await axios.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setProgress({ done: files.length, total: files.length, current: "" });
      await onUploaded();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="border-b border-[#E8E1D2] bg-[#FAF7F1] px-6 py-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start"
      data-testid={`source-composer-${stream}`}
    >
      <div className="lg:col-span-5">
        <Label>Study title</Label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`e.g. ${example}`}
          className="mt-1.5 w-full bg-white border border-[#E8E1D2] focus:border-[#C84C21] outline-none px-3 py-2 text-[13.5px] text-[#0F1825]"
          data-testid={`source-title-${stream}`}
        />
      </div>
      <div className="lg:col-span-4">
        <Label>Methodologist (optional)</Label>
        <input
          type="text"
          value={methodologist}
          onChange={(e) => setMethodologist(e.target.value)}
          placeholder="e.g. Dr. Aparna Rao"
          className="mt-1.5 w-full bg-white border border-[#E8E1D2] focus:border-[#C84C21] outline-none px-3 py-2 text-[13.5px] text-[#0F1825]"
          data-testid={`source-methodologist-${stream}`}
        />
      </div>
      <div className="lg:col-span-3">
        <Label>PDF · PPTX · DOCX (25 MB max each)</Label>
        <input
          type="file"
          multiple
          accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx,application/vnd.ms-powerpoint,.ppt,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/msword,.doc"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="mt-1.5 w-full bg-white border border-[#E8E1D2] focus:border-[#C84C21] outline-none px-2 py-1.5 text-[12px] text-[#0F1825] file:mr-2 file:bg-[#FAF7F1] file:border-0 file:px-2 file:py-1 file:text-[10.5px] file:uppercase file:tracking-[0.14em] file:text-[#C84C21]"
          data-testid={`source-file-${stream}`}
        />
        {files.length > 0 && (
          <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#5C6470]">
            {files.length} {files.length === 1 ? "file" : "files"} selected
          </div>
        )}
      </div>
      <div className="lg:col-span-12 flex flex-wrap gap-2 items-center">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] text-white font-mono text-[10.5px] uppercase tracking-[0.16em] px-3.5 py-2 transition-colors disabled:opacity-50"
          data-testid={`source-submit-${stream}`}
        >
          <Upload size={12} />
          {busy
            ? (progress.total > 1 ? `Uploading ${progress.done + 1}/${progress.total}…` : "Uploading…")
            : files.length > 1 ? `Add ${files.length} files` : "Add to library"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-[#5C6470] hover:text-[#0F1825] font-mono text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 transition-colors"
          data-testid={`source-cancel-${stream}`}
        >
          <X size={12} /> Cancel
        </button>
        {busy && progress.current && (
          <span className="text-[11px] font-mono text-[#5C6470] ml-2 truncate max-w-md">{progress.current}</span>
        )}
        {err && <span className="text-[11.5px] text-[#C84C21] ml-2" data-testid={`source-composer-error-${stream}`}>{err}</span>}
      </div>
    </form>
  );
}

// StreamActivityLine — compact one-row status surfaced when a stream has no
// uploaded source FILES (but may have signals from Sonar runs or manual paste).
// Replaces the misleading "No sources uploaded" empty-state.
function StreamActivityLine({ streamKey, signalCount, activity }) {
  const a = activity || {};
  const totalActivity = (a.sonarCount || 0) + (a.uploadCount || 0) + (a.pasteCount || 0);

  // Nothing yet for this stream — minimalist hint, no upload-only wording.
  if (totalActivity === 0 && signalCount === 0) {
    return (
      <div className="px-2 py-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#A09680]" data-testid={`stream-activity-empty-${streamKey}`}>
        No activity yet · run a prompt or upload a report to seed {streamKey}
      </div>
    );
  }

  // Format a relative "X min ago" for the latest activity timestamp.
  let agoLabel = "";
  if (a.latestAt) {
    const ms = Date.now() - new Date(a.latestAt).getTime();
    if (ms < 60_000) agoLabel = "just now";
    else if (ms < 3600_000) agoLabel = `${Math.round(ms / 60_000)} min ago`;
    else if (ms < 86_400_000) agoLabel = `${Math.round(ms / 3600_000)} h ago`;
    else agoLabel = `${Math.round(ms / 86_400_000)} d ago`;
  }

  // Build provenance breakdown chips
  const chips = [];
  if (a.sonarCount > 0)  chips.push({ label: "Sonar prompt", n: a.sonarCount,  cls: "text-[#C84C21] border-[#C84C21]/40 bg-[#FCE9E1]" });
  if (a.uploadCount > 0) chips.push({ label: "Document",     n: a.uploadCount, cls: "text-[#0F1825] border-[#0F1825]/30 bg-white" });
  if (a.pasteCount > 0)  chips.push({ label: "Curated",      n: a.pasteCount,  cls: "text-[#5C6470] border-[#A09680]/40 bg-[#FAF7F1]" });

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-2 py-2" data-testid={`stream-activity-${streamKey}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">Signals ·</span>
        <span className="font-serif text-[1.05rem] text-[#0F1825] font-medium leading-none">{signalCount}</span>
      </div>
      {chips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {chips.map((c) => (
            <span key={c.label} className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 ${c.cls}`}>
              <span>{c.label}</span>
              <span className="font-medium">{c.n}</span>
            </span>
          ))}
        </div>
      )}
      {agoLabel && (
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">
          Last activity · {agoLabel}
        </span>
      )}
    </div>
  );
}


function SourceCard({ engagementId, source, onChange, onJumpToSignals, isClient }) {
  const s = source;
  const [busy, setBusy] = useState(false);
  // Live phase polled from /sources while extraction is in flight.
  // null when not in flight; string in {"queued","parsing","vision","reasoning","validating","completed","failed"} otherwise.
  const [livePhase, setLivePhase] = useState(null);
  const isExtracted = s.extraction_status === "extracted";

  // Polling loop. Active only while busy=true. Fetches the source list, finds
  // this source's phase, and stops when the backend reports completed/failed.
  useEffect(() => {
    if (!busy) return;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/console/engagements/${engagementId}/sources`);
        const me = (r.data?.sources || []).find((x) => x.id === s.id);
        if (me && !cancelled) {
          setLivePhase(me.extraction_phase || "queued");
          if (me.extraction_status === "extracted" || me.extraction_status === "failed") {
            return;  // stop polling — onExtract's await will resolve any moment
          }
        }
      } catch { /* swallow — keep polling */ }
      if (!cancelled) timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 800);  // small initial delay so the POST has time to write "queued"
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [busy, engagementId, s.id]);

  const onExtract = async () => {
    setBusy(true);
    setLivePhase("queued");
    try {
      // Real LLM extraction — Gemini 3.1 Pro vision pass + Claude Sonnet 4.6
      // reasoning. Typical runtime is 30-90s for a 20-30 slide deck.
      await axios.post(`${API}/console/engagements/${engagementId}/sources/${s.id}/extract`);
      await onChange();
    } catch (err) {
      console.error("Extraction failed:", err?.response?.data?.detail || err);
    } finally {
      setBusy(false);
      setLivePhase(null);
    }
  };

  const onView = () => {
    const url = `${API}/console/engagements/${engagementId}/sources/${s.id}/file?v=${encodeURIComponent(s.uploaded_pdf_file_id || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onRemove = async () => {
    if (!window.confirm(`Remove "${s.study_title}" from the source library?`)) return;
    await axios.delete(`${API}/console/engagements/${engagementId}/sources/${s.id}`);
    await onChange();
  };

  return (
    <li className="bg-white border border-[#E8E1D2] p-4" data-testid={`source-card-${s.id}`}>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-7">
          <div className="flex items-baseline gap-2 flex-wrap">
            <FileText size={13} className="text-[#C84C21] shrink-0" />
            <span className="text-[14.5px] text-[#0F1825] font-medium">{s.study_title}</span>
          </div>
          <div className="mt-1 text-[12px] text-[#5C6470] leading-relaxed">
            {s.uploaded_pdf_filename} · {formatBytes(s.uploaded_pdf_size)}
            {s.methodologist ? <> · {s.methodologist}</> : null}
          </div>
          {/* In client view we hide the "Uploaded by amit.d@…" auditing line — it's operational metadata. */}
          {!isClient && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">
              Uploaded {s.uploaded_pdf_at?.slice(0, 10)} by {s.uploaded_pdf_by}
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-5 flex flex-col md:items-end gap-2">
          {/* Phase stepper (in-flight) OR extraction badge (static). Operator only. */}
          {!isClient && busy ? (
            <ExtractionPhaseStepper phase={livePhase} />
          ) : !isClient ? (
            <ExtractionBadge status={s.extraction_status} count={s.extracted_signal_count} busy={busy} />
          ) : null}
          {/* Client view: if extracted, show a soft signals-linked summary. */}
          {isClient && isExtracted && (
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] border border-[#C84C21] text-[#C84C21] bg-[#C84C21]/10 px-2 py-1 inline-flex items-center gap-1.5">
              <Sparkle size={10} />
              {s.extracted_signal_count} linked signals
            </span>
          )}

          <div className="flex flex-wrap gap-1.5 md:justify-end">
            {/* Operator-only: Run extraction trigger */}
            {!isClient && !isExtracted && (
              <button
                type="button"
                onClick={onExtract}
                disabled={busy}
                className="inline-flex items-center gap-1.5 bg-[#C84C21] hover:bg-[#0F1825] text-white font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors disabled:opacity-50"
                data-testid={`source-extract-${s.id}`}
              >
                <Sparkle size={11} />
                {busy ? "Extracting…" : "Run signal extraction"}
              </button>
            )}

            {/* Shared: View signals (deep link to Patient Signals tab) — visible once extracted */}
            {isExtracted && (
              <button
                type="button"
                onClick={onJumpToSignals}
                className="inline-flex items-center gap-1.5 bg-[#C84C21]/10 border border-[#C84C21] text-[#C84C21] hover:bg-[#C84C21] hover:text-white font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors"
                data-testid={`source-view-signals-${s.id}`}
              >
                {isClient ? "View linked signals" : "View signals"} <ArrowRight size={11} />
              </button>
            )}

            {/* Shared: View / download the source PDF */}
            <button
              type="button"
              onClick={onView}
              className="inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#0F1825] font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors"
              data-testid={`source-view-file-${s.id}`}
            >
              <Download size={11} className="text-[#C84C21]" /> {isClient ? "Open study" : "View PDF"}
            </button>

            {/* Operator-only: Remove */}
            {!isClient && (
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#5C6470] font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors"
                data-testid={`source-remove-${s.id}`}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function ExtractionBadge({ status, count, busy }) {
  if (busy) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] border border-[#A09680] text-[#A09680] bg-white px-2 py-1 inline-flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[#A09680] animate-pulse" />
        AI extracting…
      </span>
    );
  }
  if (status === "extracted") {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] border border-[#C84C21] text-[#C84C21] bg-[#C84C21]/10 px-2 py-1 inline-flex items-center gap-1.5">
        <Sparkle size={10} />
        Signals extracted · {count}
      </span>
    );
  }
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.16em] border border-[#A09680] text-[#A09680] bg-white px-2 py-1 inline-flex items-center gap-1.5">
      AI extraction pending
    </span>
  );
}

/**
 * Live phase stepper rendered during an in-flight extraction. Reads the
 * backend-written `extraction_phase` field via polling. Shows three
 * discrete stages — Parse, Vision, Reason — with the active stage marked
 * by a pulsing dot, completed stages with a check, and pending stages with
 * a soft outline. Validation collapses into the Reason stage to keep the
 * stepper to three visible steps.
 */
const PHASE_ORDER = ["parsing", "vision", "reasoning", "validating", "completed"];
const PHASE_STEPS = [
  { key: "parse",  label: "Parse",  hint: "Reading text + tables + notes", matchAt: 0 },
  { key: "vision", label: "Vision", hint: "Gemini reads charts + images",  matchAt: 1 },
  { key: "reason", label: "Reason", hint: "Claude maps to V2E themes",     matchAt: 2 },
];

function ExtractionPhaseStepper({ phase }) {
  // Translate the live phase into a numeric stage index for the stepper.
  // queued -> -1 (nothing yet); parsing -> 0; vision -> 1; reasoning/validating -> 2; completed -> 3.
  const idx = PHASE_ORDER.indexOf(phase);
  const stage = phase === "queued" || idx < 0
    ? -1
    : (phase === "validating" ? 2 : Math.min(idx, 3));

  return (
    <div
      className="w-full md:w-auto md:min-w-[320px] bg-white border border-[#E8E1D2] px-3 py-2.5"
      data-testid="extraction-phase-stepper"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#C84C21]">
          AI extraction · live
        </span>
        <span className="font-mono text-[9.5px] text-[#A09680]">
          {stage >= 3 ? "done" : stage < 0 ? "queued…" : `${stage + 1} of 3`}
        </span>
      </div>
      <ol className="mt-2 flex items-center gap-1">
        {PHASE_STEPS.map((step, i) => {
          const done = stage > step.matchAt;
          const active = stage === step.matchAt;
          const pending = stage < step.matchAt;
          return (
            <li key={step.key} className="flex items-center gap-1 flex-1" data-testid={`phase-${step.key}`}>
              <span
                className={`relative flex items-center justify-center w-4 h-4 shrink-0 rounded-full border ${
                  done   ? "bg-[#C84C21] border-[#C84C21]"
                  : active ? "border-[#C84C21] bg-white"
                  : "border-[#E8E1D2] bg-white"
                }`}
              >
                {done && <Check size={9} className="text-white" />}
                {active && <span className="w-1.5 h-1.5 rounded-full bg-[#C84C21] animate-pulse" />}
                {pending && <span className="w-1 h-1 rounded-full bg-[#E8E1D2]" />}
              </span>
              <span
                className={`text-[10.5px] font-mono uppercase tracking-[0.14em] ${
                  done || active ? "text-[#0F1825]" : "text-[#A09680]"
                }`}
              >
                {step.label}
              </span>
              {i < PHASE_STEPS.length - 1 && (
                <span
                  className={`flex-1 h-px ${
                    done ? "bg-[#C84C21]" : "bg-[#E8E1D2]"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-1.5 text-[10.5px] text-[#5C6470] leading-snug">
        {stage < 0 && "Queued — server picking up the job…"}
        {stage === 0 && PHASE_STEPS[0].hint}
        {stage === 1 && PHASE_STEPS[1].hint}
        {stage === 2 && PHASE_STEPS[2].hint}
        {stage >= 3 && "Done — refreshing signals…"}
      </div>
    </div>
  );
}

/* =========================== SIGNALS =========================== */
const SIGNAL_STATUS_MAP = {
  "New": "AI-surfaced",
  "Triaged": "Expert-reviewed",
  "Validated": "Expert-reviewed",
  "Linked to Recommendation": "Evidence-linked",
};
const SIGNAL_STATUSES = ["AI-surfaced", "Expert-reviewed", "Evidence-linked", "Excluded / low confidence"];

/**
 * Compact stream selector used as the LEFT PANE in Patient Signals.
 * Five rows, vertical. Active row highlights and expands to show the
 * source-coverage taxonomy. Click any row to swap the right-pane signals.
 */
function StreamSidebarSelector({ active, onSelect, signalCounts, hideCounts }) {
  return (
    <nav className="bg-white border border-[#E8E1D2]" data-testid="stream-sidebar-selector">
      <div className="px-5 py-4 border-b border-[#E8E1D2]">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">The five PentaVoice™ streams</div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680]">Select a stream</div>
      </div>
      <ul>
        {STREAM_META.map((s, idx) => {
          const isActive = active === s.k;
          const count = signalCounts?.[s.k] ?? 0;
          const showCount = !hideCounts && signalCounts && Object.keys(signalCounts).length > 0;
          return (
            <li
              key={s.k}
              className={idx < STREAM_META.length - 1 ? "border-b border-[#F0EBDC]" : ""}
            >
              <button
                type="button"
                onClick={() => onSelect(s.k)}
                className={`w-full text-left px-5 py-4 border-l-2 transition-colors ${
                  isActive
                    ? "bg-[#FAF7F1] border-l-[#C84C21]"
                    : "border-l-transparent hover:bg-[#FAF7F1]/60"
                }`}
                data-testid={`stream-row-${s.k}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2.5 min-w-0">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21] shrink-0">{s.k}</span>
                    <span className="text-[16px] text-[#0F1825] font-medium leading-tight truncate">{s.name}</span>
                  </div>
                  {showCount && (
                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#5C6470] shrink-0">{count}</span>
                  )}
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#5C6470] leading-snug">{s.contributes}</div>
                {isActive && (
                  <div className="mt-3 pt-3 border-t border-[#F0EBDC] text-[12px] text-[#5C6470] leading-relaxed">
                    <div className="font-mono uppercase tracking-[0.18em] text-[#A09680] text-[10px] mb-1.5">Sources covered</div>
                    {s.sources.join(" · ")}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {/* Cross-cutting note kept compact in the sidebar */}
      <div className="border-t border-[#E8E1D2] bg-[#FAF7F1]/60 px-5 py-3" data-testid="stream-cross-cutting-note">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mb-1">Cross-cutting</div>
        <div className="text-[11.5px] text-[#5C6470] leading-snug">
          Congress abstracts and scientific narratives are cross-cutting evidence — tagged under <span className="font-mono text-[11px] text-[#C84C21]">L4</span> or <span className="font-mono text-[11px] text-[#C84C21]">L5</span> depending on content.
        </div>
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PeerReviewedExtractPanel — operator-only action panel on Signal Intake.
// Triggers POST /api/console/engagements/{id}/extract-peer-reviewed with a
// user-selected subset of streams, then shows per-stream inserted / skipped
// counts. Dual-lane runs alongside Perplexity Sonar; every returned signal
// carries ingested_via=peer_reviewed_extractor + verification_status=verified.
// ─────────────────────────────────────────────────────────────────────────────
function PeerReviewedExtractPanel({ engagementId, onCompleted }) {
  const ALL_STREAMS = ["L1", "L2", "L3", "L4", "L5"];
  const [selected, setSelected] = useState(new Set(ALL_STREAMS));
  const [status, setStatus] = useState("idle"); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const toggleStream = (k) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const run = async () => {
    if (selected.size === 0) return;
    setStatus("running");
    setError(null);
    setResult(null);
    try {
      const streams = ALL_STREAMS.filter((s) => selected.has(s)).join(",");
      const { data } = await axios.post(
        `${API}/console/engagements/${engagementId}/extract-peer-reviewed`,
        null,
        { params: { streams } }
      );
      setResult(data);
      setStatus("done");
      if (onCompleted) onCompleted();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Request failed");
      setStatus("error");
    }
  };

  const isRunning = status === "running";
  const perStream = result?.per_stream || {};

  return (
    <div
      className="bg-white border border-[#E8E1D2] p-5 mb-5"
      data-testid="peer-reviewed-extract-panel"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">
            Peer-Reviewed Extraction · Dual Lane
          </div>
          <p className="text-[13px] text-[#0F1825] leading-relaxed max-w-[62ch]">
            Pulls scholarly evidence from Semantic Scholar, Europe PMC, and (for L4 / L5) ClinicalTrials.gov across every V2E sub-theme. Runs alongside Perplexity Sonar; deduped against existing signals per (stream, source URL, headline).
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={isRunning || selected.size === 0}
          className={`inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.16em] px-3.5 py-2 transition-colors border ${
            isRunning || selected.size === 0
              ? "bg-white border-[#E8E1D2] text-[#A09680] cursor-not-allowed"
              : "bg-[#0F1825] border-[#0F1825] text-white hover:bg-[#C84C21] hover:border-[#C84C21]"
          }`}
          data-testid="peer-reviewed-extract-run"
        >
          {isRunning ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
          {isRunning ? "Extracting…" : "Add peer-reviewed signals"}
        </button>
      </div>

      {/* Stream picker */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">Streams</span>
        {ALL_STREAMS.map((k) => {
          const active = selected.has(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleStream(k)}
              disabled={isRunning}
              className={`font-mono text-[10.5px] uppercase tracking-[0.16em] border px-2.5 py-1 transition-colors ${
                active
                  ? "bg-[#C84C21] border-[#C84C21] text-white"
                  : "bg-white border-[#E8E1D2] text-[#5C6470] hover:border-[#C84C21]/60"
              } ${isRunning ? "opacity-60 cursor-not-allowed" : ""}`}
              data-testid={`peer-reviewed-extract-stream-${k}`}
            >
              {active ? <Check size={10} className="inline mr-1 -mt-0.5" /> : null}
              {k}
            </button>
          );
        })}
      </div>

      {/* Result summary */}
      {status === "done" && result && (
        <div
          className="mt-4 border-t border-[#F0EBDC] pt-3"
          data-testid="peer-reviewed-extract-result"
        >
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3F6D55]">
              Completed · {result.total_inserted} signals ingested
            </span>
            {ALL_STREAMS.filter((k) => perStream[k]).map((k) => {
              const st = perStream[k] || { inserted: 0, skipped: 0 };
              return (
                <span
                  key={k}
                  className="inline-flex items-baseline gap-1.5 font-mono text-[11px] px-2 py-0.5 border border-[#E8E1D2] bg-[#FAF7F1] text-[#0F1825]"
                  data-testid={`peer-reviewed-extract-result-${k}`}
                >
                  <span className="uppercase tracking-[0.16em] text-[#C84C21]">{k}</span>
                  <span className="font-medium">+{st.inserted}</span>
                  {st.skipped > 0 && <span className="text-[#A09680]">· {st.skipped} dupe</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {status === "error" && (
        <div
          className="mt-4 border border-[#B83A28] bg-[#FCE9E1] p-3 text-[12px] text-[#B83A28]"
          data-testid="peer-reviewed-extract-error"
        >
          <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
          {error}
        </div>
      )}
    </div>
  );
}

function SignalsTab({ id, eng, setActive, isClient }) {
  const [data, setData] = useState(null);
  const [stream, setStream] = useState("L1");
  const [statusFilter, setStatusFilter] = useState("All");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/console/engagements/${id}/signals`, { params: { stream } });
      setData(data);
    })();
  }, [id, stream, refreshKey]);

  const totalCount = useMemo(() => {
    if (!data?.counts_by_stream) return 0;
    return Object.values(data.counts_by_stream).reduce((a, b) => a + b, 0);
  }, [data]);

  const filteredSignals = useMemo(() => {
    const signals = data?.signals || [];
    // In Client Briefing View we only surface evidence-linked signals — the
    // ones that actually contributed to findings. The AI-surfaced / Excluded
    // states are operational.
    if (isClient) {
      return signals.filter((s) => SIGNAL_STATUS_MAP[s.status] === "Evidence-linked");
    }
    if (statusFilter === "All") return signals;
    return signals.filter((s) => (SIGNAL_STATUS_MAP[s.status] || "AI-surfaced") === statusFilter);
  }, [data, statusFilter, isClient]);

  return (
    <div data-testid="signals-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Patient Signals" : "Stage 03 of 06 · Signal Intake"}
        title={isClient ? "Aggregated patient-experience findings, by stream." : "Five streams. One patient voice."}
        lede={isClient
          ? "Each finding here is the aggregated patient-experience output of a Voice2Evidence study in that stream — not an individual quote. For social listening: a theme bucket with sentiment distribution and volume. For PROs: an instrument-level metric. For advisory, regulatory / HTA, and RWE: their native unit of evidence. Representative verbatims are shown only to illustrate the underlying voice."
          : "The reviewed patient-signal library for this engagement — tagged by source, cohort, theme, review status, and strategic relevance. Signals shown here are AI-extracted from source reports uploaded in the Source Library, then reviewed by senior methodologists."}
      />

      <ClientOutput>
        {isClient
          ? "A curated, source-traceable view of the patient signals behind every finding in this engagement."
          : "A source-indexed, expert-reviewed patient-signal library across five PentaVoice™ evidence streams — the raw material for triangulation, evidence mapping, and strategy translation."}
      </ClientOutput>

      {/* Intake summary — operator only; client view's curated framing covers this */}
      {!isClient && (
        <div className="bg-white border border-[#E8E1D2] p-5 mb-5" data-testid="signal-intake-summary">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">Signal Intake Summary</div>
          <p className="text-[14px] text-[#0F1825] leading-relaxed">
            <span className="font-medium">{totalCount}</span> patient-relevant signals captured across five evidence streams. Signals are tagged by source, cohort, theme, review status, and strategic relevance. Not all signals advance to triangulation — discipline at intake protects the integrity of every downstream finding.
          </p>
        </div>
      )}

      {/* Peer-reviewed dual-lane extractor — operator only */}
      {!isClient && (
        <PeerReviewedExtractPanel
          engagementId={id}
          onCompleted={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* Evidence credibility legend — always visible above signal cards so
          every chip in the grid has a nearby key. Two-track by design:
          GRADE (peer-reviewed streams) + qualitative credibility (L1). */}
      <div className="mb-5" data-testid="signals-evidence-legend">
        <EvidenceLegend />
      </div>

      {/* Two-pane layout: stream selector (left) · signal cards (right).
          Left pane is the constant anchor (5 fixed streams) and is given
          more breathing room + larger type so it reads as the primary
          navigation. Right pane is the scrollable workhorse — slightly
          quieter typography because content volume varies. */}
      <div className="grid grid-cols-12 gap-6" data-testid="signals-twopane">
        {/* LEFT — stream selector */}
        <aside className="col-span-12 md:col-span-5 lg:col-span-4">
          <div className="md:sticky md:top-28 space-y-3">
            <StreamSidebarSelector
              active={stream}
              onSelect={setStream}
              signalCounts={data?.counts_by_stream}
              hideCounts={isClient}
            />
            {/* Status filter chips — operator only */}
            {!isClient && (
              <div className="bg-white border border-[#E8E1D2] p-3" data-testid="signal-status-filter">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-2">Filter by review status</div>
                <div className="flex flex-wrap gap-1.5">
                  {["All", ...SIGNAL_STATUSES].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`font-mono text-[9.5px] uppercase tracking-[0.14em] border px-2 py-0.5 transition-colors ${
                        statusFilter === s
                          ? "bg-[#0F1825] border-[#0F1825] text-white"
                          : "bg-white border-[#E8E1D2] text-[#5C6470] hover:border-[#C84C21]/60 hover:text-[#0F1825]"
                      }`}
                      data-testid={`signal-status-${s.toLowerCase().replace(/[\s/]+/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* RIGHT — signal cards for the active stream */}
        <section className="col-span-12 md:col-span-7 lg:col-span-8" data-testid="signals-list">
          {/* Active-stream header — typography mirrors the left-pane row
              (same sans, same size, same L-code treatment) so the two
              panes read as one continuous selection state instead of two
              competing visual systems. */}
          <div className="flex items-baseline justify-between mb-3 pb-3 border-b border-[#E8E1D2]">
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] shrink-0">{stream}</span>
                <span className="text-[14px] text-[#0F1825] font-medium leading-tight truncate">{STREAM_META_BY_K[stream]?.name}</span>
              </div>
              <div className="mt-1 text-[11.5px] text-[#5C6470] leading-snug">{STREAM_META_BY_K[stream]?.contributes}</div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] shrink-0 ml-3" data-testid="signals-count-line">
              {filteredSignals.length} {filteredSignals.length === 1 ? "signal" : "signals"}
            </span>
          </div>

          {/* Signal Intake is read-only — adding signals (Run Prompt or
              Upload sources) happens in the Source Library tab. Keeping the
              two surfaces separate keeps "feed evidence in" and "review
              evidence out" mentally distinct. */}

          {filteredSignals.length === 0 ? (
            <Empty>{isClient ? "No evidence-linked findings surfaced for this stream yet." : "No signals match this filter."}</Empty>
          ) : (
            <div className="space-y-3">
              {filteredSignals.map((s) => (
                <AggregatedFindingCard key={s.id} s={s} isClient={isClient} streamKey={stream} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SignalStatusBadge({ raw }) {
  const mapped = SIGNAL_STATUS_MAP[raw] || "AI-surfaced";
  const styles = {
    "AI-surfaced": "bg-white border-[#A09680] text-[#5C6470]",
    "Expert-reviewed": "bg-[#FAF7F1] border-[#5C6470] text-[#0F1825]",
    "Evidence-linked": "bg-[#C84C21]/10 border-[#C84C21] text-[#C84C21]",
    "Excluded / low confidence": "bg-white border-[#E8E1D2] text-[#A09680]",
  };
  return <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 ${styles[mapped]}`}>{mapped}</span>;
}

// VerificationBadge — small affordance showing whether a signal's citation
// was cross-checked against the Perplexity retrieved-URL set.
//   verified   — green check, URL matches a Perplexity citation
//   unverified — red warning, URL is not in the Perplexity citation set
//                (operator should manually verify before relying on it)
//   seeded     — neutral, original curated content (the 7 L2 seeds etc.)
function VerificationBadge({ status, note, compact }) {
  const map = {
    verified:   { label: "Verified citation",   cls: "text-[#3F6D55] bg-[#EAF3EC] border-[#CFE2D2]", icon: "✓" },
    unverified: { label: "Unverified · review", cls: "text-[#C84C21] bg-[#FBE7DF] border-[#F2C9B6]", icon: "⚠" },
    seeded:     { label: "Curated · seeded",    cls: "text-[#5C6470] bg-[#FAF7F1] border-[#E8E1D2]", icon: "◆" },
  };
  const m = map[status] || map.seeded;
  return (
    <span
      title={note || m.label}
      className={`inline-flex items-center gap-1 font-mono ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-[0.16em] border px-1.5 py-0.5 ${m.cls}`}
      data-testid={`verification-badge-${status || "seeded"}`}
    >
      <span aria-hidden>{m.icon}</span>
      {!compact && m.label}
    </span>
  );
}

// AggregatedFindingCard — renders a single L1-L5 aggregated finding in
// each stream's native unit. When the seed entry carries the new fields
// (headline_theme, domain, sentiment_distribution, volume, metric), the
// card renders the full aggregated-finding layout. Otherwise it falls
// back to a compact quote-card layout so legacy entries still display.
function AggregatedFindingCard({ s, isClient, streamKey }) {
  const hasAggregate =
    s.headline_theme || s.domain || s.sentiment_distribution || s.volume || s.metric;
  // V2E published-evidence + AI-extracted schema (preferred reference fields).
  // Falls back to legacy `source` / `source_type` / `date_captured` for older seeds.
  const referenceText = s.source_summary || s.source_title;
  const verbatim = s.raw_text || s.representative_quote;
  const polarityClass = s.polarity === "positive"
    ? "text-[#3F6D55] bg-[#EAF3EC] border-[#CFE2D2]"
    : s.polarity === "negative"
    ? "text-[#C84C21] bg-[#FBE7DF] border-[#F2C9B6]"
    : "text-[#5C6470] bg-[#FAF7F1] border-[#E8E1D2]";
  const cegClass = s.confirmed_emerging_gap === "Confirmed"
    ? "text-[#3F6D55] bg-[#EAF3EC] border-[#CFE2D2]"
    : s.confirmed_emerging_gap === "Gap"
    ? "text-[#9A6B1A] bg-[#FDF7E6] border-[#E8D08A]"
    : "text-[#3D5A8C] bg-[#E7EEF8] border-[#C9D5E7]";

  return (
    <article
      className="bg-white border border-[#E8E1D2] p-5 hover:border-[#C84C21]/50 transition-colors"
      data-testid={`signal-${s.id}`}
    >
      {/* Header — headline theme (aggregate) or theme tags (legacy) */}
      {hasAggregate && s.headline_theme && (
        <div className="mb-2">
          <div className="flex items-baseline justify-between gap-3">
            <h4 className="text-[15px] text-[#0F1825] font-medium leading-snug"><AutoAbbr text={s.headline_theme} /></h4>
            <div className="flex items-center gap-1.5 shrink-0">
              {!isClient && <VerificationBadge status={s.verification_status || "seeded"} note={s.verification_note} />}
              {!isClient && <SignalStatusBadge raw={s.status} />}
            </div>
          </div>
          {s.domain && (
            <div className="mt-1 text-[11.5px] text-[#A09680]">
              <span className="text-[#5C6470]">Domain · </span><AutoAbbr text={s.domain} />
            </div>
          )}
        </div>
      )}

      {/* Interpretation — methodologist explanation. Published-evidence +
          AI-extracted signals carry this; legacy seeds don't. */}
      {s.interpretation && (
        <p className="mt-2 text-[#0F1825] text-[13.5px] leading-relaxed">
          <AutoAbbr text={s.interpretation} />
        </p>
      )}

      {/* Source / type / date strip (legacy seeds with `source` field) */}
      {(s.source || s.source_type || s.date_captured) && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-2.5 mb-2.5">
          {s.source && <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#0F1825] font-medium">{s.source}</span>}
          {s.source && s.source_type && <span className="text-[#A09680] text-[10px]">·</span>}
          {s.source_type && <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#5C6470]">{s.source_type}</span>}
          {s.date_captured && (
            <>
              <span className="text-[#A09680] text-[10px]">·</span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#5C6470]">{s.date_captured}</span>
            </>
          )}
          {!isClient && !hasAggregate && (
            <span className="ml-auto"><SignalStatusBadge raw={s.status} /></span>
          )}
        </div>
      )}

      {/* Stream-native quantitative row — sentiment distribution, volume,
          metric. Renders only when the seed entry has aggregate data. */}
      {hasAggregate && (s.sentiment_distribution || s.volume || s.metric) && (
        <div className="bg-[#FAF7F1]/50 border border-[#F0EBDC] px-3 py-2.5 mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px]">
          {s.sentiment_distribution && (
            <SentimentBar dist={s.sentiment_distribution} />
          )}
          {s.volume && (
            <span><span className="text-[#A09680]">{streamKey === "L1" ? "Volume · " : "n = "}</span><span className="text-[#0F1825] font-medium">{s.volume}</span></span>
          )}
          {s.metric && (
            <span className="text-[#0F1825]">{s.metric}</span>
          )}
        </div>
      )}

      {/* Verbatim — illustrative only when aggregate; primary when legacy.
          Picks up both legacy `raw_text` and AI-extracted `representative_quote`. */}
      {verbatim && (
        <div className={hasAggregate ? "border-l-2 border-[#C84C21]/40 pl-3 py-1 mt-3" : ""}>
          {hasAggregate && (
            <div className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#A09680] mb-1">
              Representative verbatim · illustrative
            </div>
          )}
          <p className="text-[#0F1825] leading-relaxed text-[13.5px] italic">
            {hasAggregate ? `“${verbatim}”` : verbatim}
          </p>
        </div>
      )}

      {/* Reference + evidence-grade strip — citation visibility for
          published-evidence + AI-extracted signals. The Reference text
          is rendered as a link to an explicit `source_url` when present,
          otherwise to a Google Scholar search of the reference string. */}
      {(referenceText || s.evidence_grade) && (
        <div className="mt-3 pt-2.5 border-t border-[#F0EBDC] grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-1.5 text-[11.5px] leading-snug">
          {referenceText && (
            <div className="md:col-span-8">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] mr-2">Reference</span>
              <a
                href={s.source_url || `https://scholar.google.com/scholar?q=${encodeURIComponent(referenceText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0F1825] underline decoration-[#C84C21]/40 decoration-1 underline-offset-2 hover:decoration-[#C84C21] hover:text-[#C84C21] transition-colors"
                data-testid={`signal-${s.id}-reference-link`}
              >
                {referenceText}
                <span className="inline-block ml-1 text-[#A09680]">↗</span>
              </a>
            </div>
          )}
          {s.evidence_grade && (
            <div className="md:col-span-4 md:text-right flex md:justify-end items-center gap-2 flex-wrap">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]">Evidence credibility</span>
              {s.evidence_grade_normalized ? (
                <EvidenceGradeChip signal={s} size="xs" showTrackHint testid={`signal-${s.id}-grade-chip`} />
              ) : (
                <span className="text-[#0F1825] font-mono text-[10.5px]">{s.evidence_grade}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI-extraction chips — archetype · geography · Confirmed/Emerging/Gap · polarity */}
      {(s.archetype || s.geography || s.confirmed_emerging_gap || (s.polarity && hasAggregate) || s.discussion_frequency) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10.5px]">
          {s.polarity && hasAggregate && (
            <span className={`font-mono uppercase tracking-[0.14em] border px-2 py-0.5 text-[9.5px] ${polarityClass}`}>
              Polarity · {s.polarity}
            </span>
          )}
          {s.confirmed_emerging_gap && (
            <span className={`font-mono uppercase tracking-[0.14em] border px-2 py-0.5 text-[9.5px] ${cegClass}`}>
              {s.confirmed_emerging_gap}
            </span>
          )}
          {s.archetype && s.archetype !== "Unstratified" && (
            <span className="font-mono uppercase tracking-[0.14em] border border-[#E8E1D2] bg-white text-[#5C6470] px-2 py-0.5 text-[9.5px]">
              {s.archetype}
            </span>
          )}
          {s.geography && (
            <span className="font-mono uppercase tracking-[0.14em] border border-[#E8E1D2] bg-white text-[#5C6470] px-2 py-0.5 text-[9.5px]">
              {s.geography}
            </span>
          )}
          {s.discussion_frequency && (
            <span className="font-mono uppercase tracking-[0.14em] border border-[#E8E1D2] bg-white text-[#5C6470] px-2 py-0.5 text-[9.5px]">
              {s.discussion_frequency}
            </span>
          )}
        </div>
      )}

      {/* Footer — cohort + sentiment + tags (legacy compatibility) */}
      {(s.sub_cohort || (!hasAggregate && s.sentiment) || (s.theme_tags?.length > 0)) && (
        <div className="mt-3 pt-2.5 border-t border-[#F0EBDC] flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px]">
          {s.sub_cohort && (
            <span><span className="text-[#A09680] font-mono uppercase tracking-[0.12em] text-[9.5px]">Cohort </span><span className="text-[#0F1825]">{s.sub_cohort}</span></span>
          )}
          {!hasAggregate && s.sentiment && (
            <span><span className="text-[#A09680] font-mono uppercase tracking-[0.12em] text-[9.5px]">Sentiment </span><span className="text-[#0F1825]">{s.sentiment}</span></span>
          )}
          <span className="flex flex-wrap gap-1.5 ml-auto">
            {s.theme_tags?.map((t) => (
              <span key={t} className="bg-[#FAF7F1] border border-[#E8E1D2] px-2 py-0.5 text-[#0F1825] font-mono text-[9.5px] uppercase tracking-[0.12em]">{t}</span>
            ))}
          </span>
        </div>
      )}
    </article>
  );
}

// SentimentBar — compact horizontal sentiment % bar for L1 SML aggregated
// findings. Mirrors the sentiment distribution typically reported on a
// MarksMan social media listening poster (positive / neutral / negative %).
function SentimentBar({ dist }) {
  const pos = dist.positive ?? dist.Positive ?? 0;
  const neu = dist.neutral ?? dist.Neutral ?? 0;
  const neg = dist.negative ?? dist.Negative ?? 0;
  const total = pos + neu + neg || 1;
  const fmt = (n) => `${Math.round((n / total) * 100)}%`;
  return (
    <span className="inline-flex items-center gap-2" data-testid="sentiment-bar">
      <span className="text-[#A09680]">Sentiment ·</span>
      <span className="inline-flex h-1.5 w-32 overflow-hidden bg-[#F0EBDC]">
        <span style={{ width: fmt(pos) }} className="bg-[#3F6D55]" />
        <span style={{ width: fmt(neu) }} className="bg-[#A09680]" />
        <span style={{ width: fmt(neg) }} className="bg-[#C84C21]" />
      </span>
      <span className="text-[#3F6D55] font-medium">{fmt(pos)}</span>
      <span className="text-[#A09680]">{fmt(neu)}</span>
      <span className="text-[#C84C21] font-medium">{fmt(neg)}</span>
    </span>
  );
}

/* ====================== TRIANGULATION ====================== */
function TriangulationTab({ id, setActive, setScrollTo, isClient, onEngChanged }) {
  const [findings, setFindings] = useState([]);
  const [themeCoverage, setThemeCoverage] = useState({});
  const [chainOpen, setChainOpen] = useState(false);
  const [activateState, setActivateState] = useState({ status: "idle", message: "", lastResult: null });
  // Domains the user has expanded. Default: first 2 domains with content open
  // so the demo flow shows substance, but the rest is collapsed to save scroll.
  const [openDomains, setOpenDomains] = useState(new Set());

  const reload = async () => {
    const [findingsRes, engRes] = await Promise.all([
      axios.get(`${API}/console/engagements/${id}/findings`),
      axios.get(`${API}/console/engagements/${id}`),
    ]);
    setFindings(findingsRes.data.findings || []);
    setThemeCoverage(engRes.data.theme_coverage || {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [id]);

  // Resume a triangulation run still in flight (page refresh / re-opened tab).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/console/engagements/${id}/triangulation-runs`);
        const latest = (data?.runs || [])[0];
        if (!cancelled && latest?.status === "running") {
          setActivateState({ status: "running", message: `Triangulation started ${fmtRunDate(latest.triggered_at)} is still running — resuming progress.`, lastResult: null });
          pollTriangulation(latest.id);
        }
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll every 5 s for up to 20 min (a 400-signal engagement takes ~9 min:
  // one Claude call per sub-theme, then domain actions, then the PAB).
  const pollTriangulation = async (runId) => {
    try {
      const deadline = Date.now() + 20 * 60 * 1000;
      let finalRun = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: poll } = await axios.get(`${API}/console/engagements/${id}/triangulation-runs/${runId}`);
        if (poll.status === "succeeded" || poll.status === "failed") {
          finalRun = poll;
          break;
        }
      }
      if (!finalRun) {
        throw new Error("Triangulation is taking longer than 20 minutes. The engine may still be running — refresh in a few minutes.");
      }
      if (finalRun.status === "failed") {
        throw new Error(finalRun.error || "Triangulation failed without a specific error message.");
      }
      await reload();
      if (typeof onEngChanged === "function") onEngChanged();
      setActivateState({
        status: "succeeded",
        message: `Triangulated ${finalRun.total_signals} signals → ${finalRun.findings_written} V2E findings · ${finalRun.confirmed_count} confirmed · ${finalRun.emerging_count} emerging · ${finalRun.single_source_count} single-source · ${finalRun.gap_count} gap · ${finalRun.synth_succeeded}/${finalRun.synth_attempted} Claude narratives synthesised · Patient Advisory Brief: ${finalRun.pab_synth_succeeded || 0}/${finalRun.pab_synth_attempted || 0} themes.`,
        lastResult: finalRun,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Triangulation failed.";
      setActivateState({ status: "failed", message: String(detail), lastResult: null });
    }
  };

  const onActivate = async () => {
    if (activateState.status === "running") return;
    setActivateState({ status: "running", message: "Starting triangulation — Claude Sonnet 4.6 synthesises one Triangulated Insight per V2E sub-theme, then the domain actions and the Patient Advisory Brief. This takes 5–10 minutes.", lastResult: null });
    try {
      const { data: kickoff } = await axios.post(`${API}/console/engagements/${id}/triangulate`, {});
      const runId = kickoff?.id;
      if (!runId) {
        throw new Error("Triangulation run did not return an id.");
      }
      await pollTriangulation(runId);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Triangulation failed.";
      setActivateState({ status: "failed", message: String(detail), lastResult: null });
    }
  };

  // Group findings by V2E L1 theme — every finding now carries an explicit
  // v2e_subtheme field. We bucket by parent theme (A-E) and sub-bucket by
  // sub-theme inside each theme section.
  const byTheme = useMemo(() => {
    const b = {};
    V2E_THEMES.forEach((t) => { b[t.key] = []; });
    findings.forEach((f) => {
      const sub = f.v2e_subtheme && V2E_SUBTHEME_LOOKUP[f.v2e_subtheme];
      if (sub) b[sub.theme_key].push(f);
    });
    return b;
  }, [findings]);

  // All theme sections are collapsed by default — the buyer opens them one
  // at a time. Keeps the page compact and lets the V2E theme spine
  // (A · Awareness, B · Help-Seeking, C · Treatment, D · Burden, E · Prevention)
  // act as a clean menu of the engagement's scope.

  const toggleDomain = (key) => {
    setOpenDomains((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div data-testid="triangulation-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Triangulation View" : "Stage 05 of 06 · Triangulation View"}
        title="Where the streams converge — and what the convergence means."
        lede="The synthesis view. Each mapped finding from the Concept Map is converged across streams here, producing a Triangulated Insight for each V2E sub-theme — the interpretation of what the evidence actually says. The strategic recommendation that follows each insight lives one tab forward on Team Recommendations, paired 1-to-1 with the patient theme it answers."
      />

      {/* V2E Framework credentialing chip — counts themes addressed (covered
          by findings OR by a meaningful-absence card). */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="triangulation-framework-chip-row">
        <div className="inline-flex items-center gap-2 border border-[#E8E1D2] bg-white px-3 py-1.5 text-[10.5px] font-mono uppercase tracking-[0.14em] text-[#5C6470]" data-testid="triangulation-framework-chip">
          <span className="text-[#C84C21] font-medium">V2E Framework</span>
          <span className="text-[#A09680]">·</span>
          <span>{findings.length} insights</span>
          <span className="text-[#A09680]">·</span>
          <span>5 of 5 themes addressed{Object.keys(themeCoverage).length > 0 ? ` · ${Object.keys(themeCoverage).length} via meaningful absence` : ""}</span>
        </div>
        {!isClient && (
          <button
            type="button"
            onClick={onActivate}
            disabled={activateState.status === "running"}
            className="inline-flex items-center gap-2 bg-[#C84C21] hover:bg-[#0F1825] text-white font-mono text-[10.5px] uppercase tracking-[0.18em] px-3 py-2 transition-colors disabled:opacity-50"
            data-testid="activate-triangulation-btn"
            title="Aggregate the live signal store into V2E findings (one per sub-theme). Pure rule-based computation — re-runnable any time without LLM cost."
          >
            {activateState.status === "running" ? <Loader2 size={12} className="animate-spin" /> : <Network size={12} />}
            {findings.length === 0 ? "▷ Activate triangulation" : "↻ Re-activate triangulation"}
          </button>
        )}
      </div>
      {!isClient && activateState.status === "running" && (
        <div className="mb-3 bg-[#FAF7F1] border-l-2 border-[#A09680] p-3 text-[12px] text-[#5C6470] flex items-start gap-2" data-testid="triangulation-activate-running">
          <Loader2 className="animate-spin shrink-0 mt-[1px]" size={12} />
          <span className="leading-relaxed">{activateState.message}</span>
        </div>
      )}
      {!isClient && activateState.status === "succeeded" && (
        <div className="mb-3 bg-[#EAF3EC] border-l-2 border-[#3F6D55] p-3 text-[12px] text-[#2E4A38]" data-testid="triangulation-activate-success">
          ✓ {activateState.message}
        </div>
      )}
      {!isClient && activateState.status === "failed" && (
        <div className="mb-3 bg-[#FCE9E1] border-l-2 border-[#C84C21] p-3 text-[12px] text-[#C84C21]" data-testid="triangulation-activate-error">
          {activateState.message}
        </div>
      )}

      {/* How to read this — collapsible. Just a clean affordance: the
          strength chip and the dot are self-evident on each finding, so the
          legend doesn't need to repeat them. The expanded view explains what
          the Triangulation View *does*. */}
      <div className="bg-white border border-[#E8E1D2] mb-4" data-testid="triangulation-chain">
        <button
          type="button"
          onClick={() => setChainOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-[#FAF7F1]/50 transition-colors"
          data-testid="triangulation-chain-toggle"
          aria-expanded={chainOpen}
        >
          <span className="text-[#A09680] uppercase tracking-[0.12em] font-mono text-[10px]" data-testid="polarity-legend">How to read this</span>
          <ChevronDown size={14} className={`text-[#5C6470] transition-transform shrink-0 ${chainOpen ? "rotate-180" : ""}`} />
        </button>
        {chainOpen && (
          <div className="px-4 pb-4 pt-1 border-t border-[#F0EBDC]" data-testid="triangulation-chain-detail">
            <div className="text-[12px] text-[#A09680] font-medium mb-2">The chain — what the Triangulation View does</div>
            <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#0F1825]">
              <span className="font-medium">Mapped findings</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">Cross-stream convergence</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">Triangulated Insight</span>
              <span className="text-[#5C6470]">(interpretation only — the synthesis)</span>
              <ArrowRight size={13} className="text-[#C84C21]" />
              <span className="font-medium">Paired on Team Recommendations</span>
              <span className="text-[#5C6470]">(theme ↔ recommendation)</span>
            </div>
            <p className="mt-2 text-[12px] text-[#5C6470] leading-relaxed">
              The Triangulation View shows what the streams say together — the converged patient theme and the interpretation of why the streams agree. The team-tagged strategic recommendation that follows from each insight lives on <span className="font-medium">Team Recommendations</span>, in the horizontal paired table that correlates every theme with the recommendation it produced. One theme, one recommendation, one closed loop — split across two tabs so the synthesis and the action stay legible.
            </p>
          </div>
        )}
      </div>

      {/* Findings grouped by V2E L1 theme, sub-grouped by L2 sub-theme.
          Collapsible per-theme sections to keep scroll short. Default: first
          2 themes with content open. */}
      <div className="space-y-3" data-testid="triangulation-domains">
        {V2E_THEMES.map((t) => {
          const ts = byTheme[t.key] || [];
          const isOpen = openDomains.has(t.key);
          const isEmpty = ts.length === 0;
          const coverage = themeCoverage[t.key] || null;
          // Empty theme without coverage data: still render the header in a
          // muted state so coverage is visible — but with a generic note.
          const mix = { Confirmed: 0, Gap: 0, Emerging: 0 };
          ts.forEach((f) => { if (mix[f.confidence] !== undefined) mix[f.confidence]++; });
          // Sub-group findings by sub-theme inside this theme, preserving
          // the canonical sub-theme order (so A1 → A2 → A3).
          const bySub = {};
          t.subthemes.forEach((s) => { bySub[s.key] = []; });
          ts.forEach((f) => {
            if (bySub[f.v2e_subtheme]) bySub[f.v2e_subtheme].push(f);
          });
          return (
            <section
              key={t.key}
              className={`border ${isEmpty ? "border-dashed border-[#C8C0AE] bg-[#FAF7F1]/30" : "bg-white border-[#E8E1D2]"}`}
              data-testid={`triangulation-domain-${t.key}`}
              data-empty={isEmpty ? "true" : "false"}
            >
              <button
                type="button"
                onClick={() => toggleDomain(t.key)}
                className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-[#FAF7F1]/50 transition-colors"
                data-testid={`triangulation-domain-toggle-${t.key}`}
                aria-expanded={isOpen}
                style={{ borderLeft: `4px solid ${isEmpty ? "#C8C0AE" : t.accent}` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-[11px] font-semibold" style={{ color: isEmpty ? "#A09680" : t.accent }}>{t.key}</span>
                  <span className={`font-serif text-[15px] ${isEmpty ? "text-[#5C6470]" : "text-[#0F1825]"}`}>{t.label}</span>
                  {isEmpty ? (
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#A09680]">
                      {coverage ? "addressed via meaningful absence" : "no insight in this engagement"}
                    </span>
                  ) : (
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#A09680]">{ts.length} insight{ts.length === 1 ? "" : "s"}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {mix.Confirmed > 0 && <span className="font-mono text-[10px] text-[#3F6D55]">{mix.Confirmed} strong</span>}
                  {mix.Gap > 0 && <span className="font-mono text-[10px] text-[#C84C21]">{mix.Gap} conditional</span>}
                  {mix.Emerging > 0 && <span className="font-mono text-[10px] text-[#5C6470]">{mix.Emerging} watch</span>}
                  <ChevronDown size={14} className={`text-[#5C6470] transition-transform ml-1 ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && isEmpty && (
                <div className="border-t border-[#F0EBDC] px-4 py-4" data-testid={`triangulation-absence-${t.key}`}>
                  {coverage ? (
                    <AbsenceCard theme={t} coverage={coverage} setActive={setActive} />
                  ) : (
                    <div className="text-[12.5px] text-[#5C6470] italic leading-relaxed">
                      No Triangulated Insight surfaced under this theme in the current engagement&apos;s evidence base. Coverage commentary was not specified at engagement design.
                    </div>
                  )}
                </div>
              )}
              {isOpen && !isEmpty && (
                <div className="border-t border-[#F0EBDC]">
                  {t.subthemes.filter((s) => bySub[s.key].length > 0).map((s) => (
                    <div key={s.key} className="border-b border-[#F0EBDC] last:border-b-0">
                      <div className="bg-[#FAF7F1]/40 px-4 py-1.5 flex items-baseline gap-2" data-testid={`triangulation-subtheme-${s.key}`}>
                        <span className="font-mono text-[10px] text-[#A09680]">{s.key}</span>
                        <span className="text-[12px] text-[#0F1825]">{s.label}</span>
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680] ml-1">{bySub[s.key].length}</span>
                      </div>
                      <div className="divide-y divide-[#F0EBDC]">
                        {bySub[s.key].map((f) => (
                          <FindingCard key={f.id} f={f} setActive={setActive} setScrollTo={setScrollTo} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// AbsenceCard — rendered inside an empty Theme section. Same visual shape as
// FindingCard's Triangulated Insight panel so it reads as a real insight, not
// a placeholder. Carries an interpretation of why this theme is silent + a
// strategic recommendation with a strength label.
function AbsenceCard({ theme, coverage, setActive }) {
  return (
    <article data-testid={`absence-${theme.key}`}>
      <div className="bg-white border border-[#F0EBDC] p-3 mb-3">
        <div className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#A09680] mb-2">Triangulated Insight — meaningful absence</div>
        {coverage.interpretation && (
          <div className="mb-2.5">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#5C6470] mb-1">Interpretation</div>
            <p className="text-[#0F1825] leading-relaxed text-[13.5px]">{coverage.interpretation}</p>
          </div>
        )}
        {coverage.strategic_recommendation && (
          <div>
            <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#5C6470]">Strategic recommendation</span>
              <ConfidenceBadge value={coverage.confidence || "Emerging"} />
            </div>
            <p className="text-[#0F1825] leading-relaxed text-[13.5px]">{coverage.strategic_recommendation}</p>
          </div>
        )}
      </div>
      {setActive && (
        <button
          type="button"
          onClick={() => setActive("functions")}
          className="inline-flex items-center gap-1.5 text-[11.5px] text-[#C84C21] hover:text-[#0F1825] transition-colors group"
          data-testid={`absence-link-to-functions-${theme.key}`}
        >
          <span className="italic">See Team Recommendations</span>
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
    </article>
  );
}

function FindingCard({ f, setActive, setScrollTo }) {
  const pol = getPolarity(f);
  // Strip inline `[N]` citation markers from the headline — references live
  // exclusively on Team Recommendations. Triangulation View is a synthesis
  // scan surface: no citation clutter, no dangling markers.
  const themeClean = (f.theme || "").replace(/\s*\[\d+(?:\s*,\s*\d+)*\]/g, "").replace(/\s{2,}/g, " ").trim();

  return (
    <article className="px-4 py-4" data-testid={`finding-${f.id}`} data-polarity={pol}>
      {/* Minimal card — just the headline finding. Interpretation lives on
          Team Recommendations (LEFT cell, under the theme) so the paired
          table balances: theme + synthesis on the left, recommendation on
          the right. One theme, one recommendation, one closed loop. */}
      <div className="min-w-0 mb-3 flex items-start gap-3">
        <h3 className="font-serif text-[15.5px] text-[#0F1825] leading-snug flex-1"><AutoAbbr text={themeClean} /></h3>
        <ConfidenceBadge value={f.confidence} />
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11.5px]">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680]">Supporting</span>
          {f.supporting_streams?.map((s) => (
            <span key={s} className="font-mono text-[10px] uppercase tracking-[0.14em] bg-[#FAF7F1] border border-[#C84C21]/30 text-[#C84C21] px-1.5 py-[1px]">{s}</span>
          ))}
        </div>
        {f.contesting_streams?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680]">Contesting</span>
            {f.contesting_streams.map((s) => (
              <span key={s} className="font-mono text-[10px] uppercase tracking-[0.14em] bg-white border border-[#E8E1D2] text-[#5C6470] px-1.5 py-[1px]">{s}</span>
            ))}
          </div>
        )}
        <PolarityDot p={pol} />
        {setActive && (
          <button
            type="button"
            onClick={() => {
              // Deep-link directly to this finding's paired row on the
              // Team Recommendations tab. The receiver clears scrollTo
              // after scrolling so subsequent navigations behave normally.
              if (setScrollTo) setScrollTo(f.id);
              setActive("functions");
            }}
            className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[#C84C21] hover:text-[#0F1825] transition-colors group"
            data-testid={`finding-link-to-functions-${f.id}`}
          >
            <span className="italic">See synthesis + recommendation on Team Recommendations</span>
            <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}
      </div>
    </article>
  );
}

function ConfidenceCard({ tone, Icon, title, count, desc }) {
  const tones = {
    confirmed: { wrap: "border-[#C84C21]/40 bg-[#C84C21]/[0.04]", icon: "text-[#C84C21]" },
    gap: { wrap: "border-[#5C6470]/30", icon: "text-[#5C6470]" },
    emerging: { wrap: "border-[#A09680]/40", icon: "text-[#A09680]" },
  };
  const t = tones[tone];
  return (
    <div className={`bg-white border ${t.wrap} p-5`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={18} className={t.icon} />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0F1825] font-medium">{title}</span>
        </div>
        <span className="font-serif text-2xl text-[#0F1825]">{count}</span>
      </div>
      <p className="text-[12.5px] text-[#5C6470] leading-relaxed">{desc}</p>
    </div>
  );
}

function ConfidenceBadge({ value }) {
  // Renders the RECOMMENDATION STRENGTH chip — green (Strong), orange
  // (Conditional), white-with-border (Watch). The strength badge belongs to
  // the recommendation, not to the overall finding. Canonical DB values stay
  // as 'Confirmed' / 'Gap' / 'Emerging' for back-compat; rendered labels and
  // colors flip here.
  //
  //   Confirmed → Strong recommendation       — implement now
  //   Gap       → Conditional recommendation  — commission the named evidence first
  //   Emerging  → Watch recommendation        — track via the named mechanism
  const styles = {
    Confirmed: "bg-[#3F6D55] text-white border-[#3F6D55]",
    Gap:       "bg-[#C84C21] text-white border-[#C84C21]",
    Emerging:  "bg-white text-[#5C6470] border-[#5C6470]",
  };
  const labels = { Confirmed: "Strong", Gap: "Conditional", Emerging: "Watch" };
  const cls = styles[value] || styles.Emerging;
  const label = labels[value] || value;
  return (
    <span className={`shrink-0 inline-flex items-center justify-center text-[10.5px] font-mono uppercase tracking-[0.14em] border ${cls} px-2 py-[3px] leading-none`} data-testid={`confidence-${value}`}>{label}</span>
  );
}

/* =========================== EVIDENCE MAP =========================== */
// Recommended next-step taxonomy per the engagement-workspace product spec.
// Tone classes drive the traffic-light pill colour:
//   act        → teal (decisive)
//   investigate→ amber (work needed)
//   watch      → slate (passive monitor)
const NEXT_STEPS = {
  "Use in medical narrative":          { tone: "act",         test: "use-in-medical-narrative" },
  "Add to HEOR evidence plan":         { tone: "act",         test: "add-to-heor-evidence-plan" },
  "Map to PRO / COA strategy":         { tone: "act",         test: "map-to-pro-coa-strategy" },
  "Explore through advisory board":    { tone: "investigate", test: "explore-through-advisory-board" },
  "Review RWE source":                 { tone: "investigate", test: "review-rwe-source" },
  "Consider payer / HTA relevance":    { tone: "investigate", test: "consider-payer-hta-relevance" },
  "Monitor":                           { tone: "watch",       test: "monitor" },
};
const NEXT_STEP_OPTIONS = Object.keys(NEXT_STEPS);

// Heuristic: pick a recommended next step per topic based on the dominant cell mix.
function nextStepForRow(topic, streamConfMap) {
  const vals = Object.values(streamConfMap);
  const confirmed = vals.filter((v) => v === "Confirmed").length;
  const gaps = vals.filter((v) => v === "Gap").length;
  const emerging = vals.filter((v) => v === "Emerging").length;
  if (confirmed >= 4) return "Use in medical narrative";
  if (gaps >= 3 && /combination|adoption|neoadjuvant|drop|burden|adherence/i.test(topic)) return "Map to PRO / COA strategy";
  if (gaps >= 3) return "Explore through advisory board";
  if (emerging >= 2) return "Monitor";
  if (/financial|access|affordab|hta|payer/i.test(topic)) return "Consider payer / HTA relevance";
  if (/rwe|real-world|registry/i.test(topic)) return "Review RWE source";
  return "Add to HEOR evidence plan";
}

// Priority class per row — High / Medium / Low, drives visual emphasis.
function priorityForRow(streamConfMap) {
  const vals = Object.values(streamConfMap);
  const gaps = vals.filter((v) => v === "Gap").length;
  const confirmed = vals.filter((v) => v === "Confirmed").length;
  if (gaps >= 3) return "High";
  if (gaps >= 1 && confirmed < 3) return "Medium";
  return "Low";
}

function PriorityBadge({ value }) {
  // Distinct from confidence colours — uses deep ink for High, amber for
  // Medium, sage for Low so it never visually collides with the heat-map
  // Confirmed (rust) / Gap (gray) / Emerging (outline-amber) cells.
  // Fixed min-width + centered layout so High / Medium / Low align
  // vertically in stacks (reference panel + map first column).
  const styles = {
    High:   { wrap: "bg-[#1F2937] text-white border-[#1F2937]",      dot: "bg-white" },
    Medium: { wrap: "bg-[#FDF7E6] text-[#9A6B1A] border-[#E8D08A]",  dot: "bg-[#9A6B1A]" },
    Low:    { wrap: "bg-[#F0F4F1] text-[#3F6D55] border-[#3F6D55]/40", dot: "bg-[#3F6D55]" },
  };
  const s = styles[value] || styles.Low;
  return (
    <span
      className={`inline-flex items-center justify-center gap-1.5 text-[11px] tracking-normal border ${s.wrap} px-2.5 py-1 shrink-0 min-w-[78px]`}
      data-testid={`priority-${value.toLowerCase()}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      <span className="leading-none">{value}</span>
    </span>
  );
}

function NextStepBadge({ value }) {
  const meta = NEXT_STEPS[value] || { tone: "watch", test: "unknown" };
  // Semantic action-type palette — distinct from the rust heat-map cells
  // and the priority badges. Teal = decisive "do this", amber = "work
  // needed", slate = "passive monitor". Reads as a traffic light.
  const tones = {
    act:         "bg-[#0E5A55] text-white border-[#0E5A55]",
    investigate: "bg-[#FDF7E6] text-[#8A5A12] border-[#E8D08A]",
    watch:       "bg-white text-[#5C6470] border-[#C8C0AE]",
  };
  return (
    <span
      className={`inline-flex items-center text-[12px] tracking-normal border ${tones[meta.tone]} px-2.5 py-1 leading-snug`}
      data-testid={`nextstep-${meta.test}`}
    >
      {value}
    </span>
  );
}

// Reference row used in the supporting panel BELOW the map — pairs the
// priority badge with its plain-English meaning AND the count of topics in
// that priority. Symmetric to the Confidence DefinitionBlock so the two
// reference panels read together.
function PriorityRefRow({ tone, label, desc, count }) {
  return (
    <div className="flex items-start gap-3" data-testid={`priority-ref-${tone.toLowerCase()}`}>
      <PriorityBadge value={tone} />
      <div className="flex-1">
        <div className="text-[13px] text-[#0F1825] font-medium">
          {label}
          <span className="ml-2 text-[11.5px] text-[#A09680] font-normal">· {count} {count === 1 ? "topic" : "topics"}</span>
        </div>
        <div className="text-[12px] text-[#5C6470] leading-snug mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

function GapMapTab({ id, isClient }) {
  // Evidence Maturity — outcome scorecard. Shows the recommendations the
  // engagement produced and where they are: To do / In progress / Done.
  // Single hero number (% complete). Single sortable list. Side panel
  // surfaces the open recommendations as the recommended scope for the
  // the engagement-close view. No cell-level matrix here — the matrix abstraction
  // belongs upstream (Triangulation), not downstream of action execution.
  const [actions, setActions] = useState([]);
  const [findings, setFindings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");

  useEffect(() => {
    (async () => {
      const [a, f] = await Promise.all([
        axios.get(`${API}/console/engagements/${id}/actions`),
        axios.get(`${API}/console/engagements/${id}/findings`),
      ]);
      setActions(a.data.actions || []);
      setFindings(f.data.findings || []);
    })();
  }, [id]);

  // Bucket the broad client-facing statuses (Recommended / Accepted /
  // In Progress / Implemented / Monitoring / Closed) into the three
  // outcome buckets the maturity tab cares about:
  //   To do        = Recommended, Accepted
  //   In progress  = In Progress, Monitoring
  //   Done         = Implemented, Closed
  const bucketFor = (status) => {
    const client = ACTION_STATUS_MAP[status] || status;
    if (client === "Implemented" || client === "Closed") return "Done";
    if (client === "In Progress" || client === "Monitoring") return "In progress";
    return "To do";
  };

  // Derive a priority for each action from its underlying finding's
  // confidence. Confirmed → High, Emerging → Medium, Gap → Medium, else Low.
  const findingForAction = (a) => findings.find((f) => f.theme === a.insight) || null;
  const priorityFor = (a) => {
    const f = findingForAction(a);
    if (!f) return "Medium";
    if (f.confidence === "Confirmed") return "High";
    if (f.confidence === "Gap") return "Medium";
    return "Low";
  };

  const enriched = useMemo(() => {
    return actions.map((a) => ({
      raw: a,
      bucket: bucketFor(a.status),
      priority: priorityFor(a),
      clientStatus: ACTION_STATUS_MAP[a.status] || a.status,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, findings]);

  const counts = useMemo(() => {
    const c = { "To do": 0, "In progress": 0, "Done": 0 };
    enriched.forEach((e) => { c[e.bucket] += 1; });
    return c;
  }, [enriched]);

  const total = enriched.length;
  const pctDone = total === 0 ? 0 : Math.round((counts["Done"] / total) * 100);

  const filtered = useMemo(() => {
    return enriched.filter((e) =>
      (statusFilter === "All" || e.bucket === statusFilter) &&
      (priorityFilter === "All" || e.priority === priorityFilter)
    );
  }, [enriched, statusFilter, priorityFilter]);

  // 'Open' = the recommendations still to do or in progress. They
  // populate the close-of-loop panel directly.
  const openRecommendations = useMemo(() =>
    enriched.filter((e) => e.bucket !== "Done"),
    [enriched]
  );

  // Emerging findings without an action attached yet — these are the
  // potential NEW recommendations the owner team should triage.
  const emergingWithoutAction = useMemo(() => {
    const themesWithAction = new Set(actions.map((a) => a.insight));
    return findings.filter(
      (f) => f.confidence === "Emerging" && !themesWithAction.has(f.theme)
    );
  }, [findings, actions]);

  return (
    <div data-testid="evidence-maturity-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Evidence Maturity" : "Stage 08 of 09 · Evidence Maturity"}
        title="Where this engagement landed — and the recommended close-of-loop on every open thread."
        lede="The closed-engagement view. Every triangulated insight carries a recommendation with a strength label: Strong recommendations for the team to implement, Conditional recommendations with V2E&apos;s naming of what evidence to commission, and Watch recommendations with V2E&apos;s naming of how to confirm or track. Nothing is left as &apos;come back next cycle&apos; — the loop closes inside this deliverable."
      />

      <ClientOutput>
        A live scorecard of recommendation status — and the recommended close-of-loop on every Conditional and Watch thread.
      </ClientOutput>

      {/* Maturity hero — single big number that drives the page. */}
      <div
        className="bg-white border border-[#E8E1D2] p-6 mb-3 grid grid-cols-1 md:grid-cols-4 gap-6"
        data-testid="maturity-hero"
      >
        <div className="md:col-span-2 border-r border-[#F0EBDC] pr-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">Recommendations</div>
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-[3rem] text-[#0F1825] leading-none" data-testid="maturity-percentage">
              {pctDone}%
            </span>
            <span className="text-[13px] text-[#5C6470]">complete</span>
          </div>
          <div className="mt-2 text-[12.5px] text-[#5C6470]">
            <span className="font-medium text-[#0F1825]">{counts["Done"]}</span> of <span className="font-medium text-[#0F1825]">{total}</span> recommendations implemented as of engagement close.
          </div>
        </div>
        <MaturityCount tone="confirmed" label="Done"        count={counts["Done"]}        note="Implemented · loop closed." />
        <MaturityCount tone="emerging"  label="In progress" count={counts["In progress"]} note="In motion · being delivered." />
      </div>

      {/* Breakdown strip + filters glued to top of the table */}
      <div
        className="bg-white border border-[#E8E1D2] border-b-0 px-4 py-3 flex flex-wrap items-center gap-4"
        data-testid="maturity-filters"
      >
        <FilterChipGroup label="Status"   options={["All", "To do", "In progress", "Done"]} value={statusFilter}   onChange={setStatusFilter}   idPrefix="status" />
        <FilterChipGroup label="Priority" options={["All", "High", "Medium", "Low"]}        value={priorityFilter} onChange={setPriorityFilter} idPrefix="priority" />
        <span className="ml-auto text-[11.5px] text-[#A09680]">
          {filtered.length} of {total} {total === 1 ? "recommendation" : "recommendations"}
        </span>
      </div>

      {/* Recommendation status table */}
      <div className="bg-white border border-[#E8E1D2] overflow-x-auto">
        <table className="min-w-full text-[14px]" data-testid="maturity-table">
          <thead>
            <tr className="bg-[#FAF7F1]">
              <th className="text-left p-4 text-[12px] text-[#5C6470] font-medium border-b border-[#E8E1D2] w-[8rem]">Priority</th>
              <th className="text-left p-4 text-[12px] text-[#5C6470] font-medium border-b border-l border-[#E8E1D2]">Recommendation</th>
              <th className="text-left p-4 text-[12px] text-[#5C6470] font-medium border-b border-l border-[#E8E1D2] w-[12rem]">Owner</th>
              <th className="text-left p-4 text-[12px] text-[#5C6470] font-medium border-b border-l border-[#E8E1D2] w-[10rem]">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={4} className="p-6"><Empty>No recommendations match these filters.</Empty></td></tr>
            ) : (
              filtered.map((e, i) => (
                <tr key={e.raw.id || i} className="border-b border-[#F0EBDC] last:border-b-0 hover:bg-[#FAF7F1]/40 transition-colors" data-testid={`maturity-row-${i}`}>
                  <td className="p-4 align-top">
                    <PriorityBadge value={e.priority} />
                  </td>
                  <td className="p-4 align-top border-l border-[#F0EBDC]">
                    <div className="text-[14px] text-[#0F1825] leading-snug">{e.raw.recommendation}</div>
                    <div className="mt-1.5 text-[11.5px] text-[#A09680] leading-snug">{e.raw.insight}</div>
                  </td>
                  <td className="p-4 align-top border-l border-[#F0EBDC] text-[13px] text-[#0F1825]">
                    <div className="font-medium">{e.raw.owner_function}</div>
                    {e.raw.owner_name && (
                      <div className="text-[11.5px] text-[#5C6470] mt-0.5">{e.raw.owner_name}</div>
                    )}
                  </td>
                  <td className="p-4 align-top border-l border-[#F0EBDC]">
                    <BucketPill bucket={e.bucket} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11.5px] text-[#A09680] leading-relaxed">
        Recommendations are drawn from triangulated insights. Status reflects implementation state at engagement close. The Action Tracker carries the full execution detail.
      </p>

      {/* Recommended close-of-loop panel — open recommendations the team can
          carry to implementation, plus directional findings with V2E's
          recommendation on how to confirm or watch them. No "next V2E cycle"
          implication — every thread closes inside this deliverable. */}
      <section className="mt-10" data-testid="close-of-loop">
        <div className="bg-white border border-[#E8E1D2] p-6">
          <div className="flex items-baseline justify-between gap-4 mb-4 pb-4 border-b border-[#F0EBDC]">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">Recommended close-of-loop</div>
              <h3 className="font-serif text-[1.4rem] text-[#0F1825] leading-snug">For the team to carry forward</h3>
            </div>
            <span className="text-[11.5px] text-[#A09680] shrink-0">
              {openRecommendations.length} open recommendations · {emergingWithoutAction.length} watch
            </span>
          </div>
          {openRecommendations.length === 0 && emergingWithoutAction.length === 0 ? (
            <Empty>Every recommendation from this engagement is implemented and no watch signals are unassigned — the loop is fully closed at engagement handover.</Empty>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <div className="text-[12px] text-[#A09680] font-medium mb-2">Open recommendations the team should carry to implementation</div>
                {openRecommendations.length === 0 ? (
                  <div className="text-[12.5px] text-[#A09680] italic">No open recommendations.</div>
                ) : (
                  <ul className="space-y-2.5">
                    {openRecommendations.map((e, i) => (
                      <li key={i} className="border border-[#E8E1D2] p-3" data-testid={`scope-open-${i}`}>
                        <div className="flex items-start gap-2.5">
                          <PriorityBadge value={e.priority} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-[#0F1825] leading-snug">{e.raw.recommendation}</div>
                            <div className="mt-1 text-[11px] text-[#A09680]">{e.raw.owner_function} · {e.bucket}</div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[12px] text-[#A09680] font-medium mb-2">Watch recommendations — V2E names how to confirm or track</div>
                {emergingWithoutAction.length === 0 ? (
                  <div className="text-[12.5px] text-[#A09680] italic">No watch recommendations unassigned.</div>
                ) : (
                  <ul className="space-y-2.5">
                    {emergingWithoutAction.map((f) => (
                      <li key={f.id} className="border border-[#A09680]/30 bg-[#FAF7F1]/40 p-3" data-testid={`scope-emerging-${f.id}`}>
                        <div className="text-[13px] text-[#0F1825] leading-snug">{f.theme}</div>
                        <div className="mt-1 text-[11px] text-[#A09680]">Watch · {f.owners?.join(" · ") || "Unassigned"}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <p className="mt-5 text-[11.5px] text-[#A09680] leading-relaxed">
            Each recommendation above names the close-of-loop action the team should take after engagement handover — operationalise via the owning function, commission the named follow-on evidence, or build the watch-mechanism into the team&apos;s own programme. The engagement deliverable is complete.
          </p>
        </div>
      </section>
    </div>
  );
}

// BucketPill — visualises the three outcome buckets (To do, In progress,
// Done) the Evidence Maturity tab uses on the right-hand column of the
// recommendation table.
function BucketPill({ bucket }) {
  const tones = {
    "Done":        "bg-[#0E5A55] text-white border-[#0E5A55]",
    "In progress": "bg-[#FDF7E6] text-[#8A5A12] border-[#E8D08A]",
    "To do":       "bg-white text-[#5C6470] border-[#C8C0AE]",
  };
  const dotByBucket = {
    "Done":        "bg-white",
    "In progress": "bg-[#8A5A12]",
    "To do":       "bg-[#A09680]",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[12px] tracking-normal border ${tones[bucket]} px-2.5 py-1 leading-snug`}
      data-testid={`bucket-${bucket.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotByBucket[bucket]}`} />
      {bucket}
    </span>
  );
}

// ActionStatusGroup — renders a labelled cluster of the granular statuses
// that roll up to a single Evidence Maturity bucket. The bucket name is
// the cluster label; each granular state sits as its own chip beneath it.
// Same data, two abstraction levels — Action Tracker (granular) ↔
// Evidence Maturity (rolled-up).
function ActionStatusGroup({ bucket, statuses, statusFilter, setStatusFilter }) {
  const bucketStyles = {
    "To do":       { dot: "bg-[#A09680]",  label: "text-[#5C6470]" },
    "In progress": { dot: "bg-[#8A5A12]",  label: "text-[#8A5A12]" },
    "Done":        { dot: "bg-[#0E5A55]",  label: "text-[#0E5A55]" },
  };
  const s = bucketStyles[bucket];
  return (
    <div className="flex items-center gap-2" data-testid={`status-group-${bucket.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${s.label} mr-1`}>
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {bucket}
      </span>
      {statuses.map((st) => (
        <button
          key={st}
          onClick={() => setStatusFilter(st)}
          className={`text-[11.5px] tracking-normal border px-2 py-0.5 transition-colors ${
            statusFilter === st
              ? "bg-[#0F1825] border-[#0F1825] text-white"
              : "bg-white border-[#E8E1D2] text-[#5C6470] hover:border-[#C84C21]/60"
          }`}
          data-testid={`action-status-${st.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {st}
        </button>
      ))}
    </div>
  );
}


function FilterChipGroup({ label, options, value, onChange, idPrefix }) {
  // Slugify the option for the testid so values like 'To address' produce
  // 'filter-conf-to-address' rather than 'filter-conf-to address'.
  const slug = (o) => String(o).toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11.5px] text-[#A09680] mr-1">{label}</span>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`text-[11.5px] border px-2 py-0.5 transition-colors ${
            value === o
              ? "bg-[#0F1825] border-[#0F1825] text-white"
              : "bg-white border-[#E8E1D2] text-[#5C6470] hover:border-[#C84C21]/60"
          }`}
          data-testid={`filter-${idPrefix}-${slug(o)}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

// Hero count tile used in the Evidence Maturity hero — three side-by-side
// counts ('established / developing / to address' on small viewports;
// shows just two on the right of the % cell on wide viewports).
function MaturityCount({ tone, label, count, note }) {
  const tones = {
    confirmed: { swatch: "bg-[#C84C21]", text: "text-[#C84C21]" },
    emerging:  { swatch: "bg-transparent border border-[#A09680]/50", text: "text-[#A09680]" },
    gap:       { swatch: "bg-[#5C6470]/15 border border-[#5C6470]/30", text: "text-[#0F1825]" },
  };
  const t = tones[tone] || tones.confirmed;
  return (
    <div data-testid={`maturity-count-${tone}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-block w-3 h-3 ${t.swatch}`} />
        <span className={`text-[12px] font-medium ${t.text}`}>{label}</span>
      </div>
      <div className="font-serif text-[2rem] text-[#0F1825] leading-none">{count}</div>
      <div className="mt-1 text-[11.5px] text-[#5C6470] leading-snug">{note}</div>
    </div>
  );
}

function GapCell({ value }) {
  // Evidence Maturity vocabulary:
  //   Confirmed (DB) → 'Established' — evidence is in place, done
  //   Emerging  (DB) → 'Developing'  — signal exists, still maturing
  //   Gap       (DB) → 'To address'  — open ground requiring work
  //   (empty)        → 'Not in scope'— outside this cycle's scope
  // Canonical DB values are unchanged so Triangulation View
  // (Supported/Gap/Emerging) and downstream analytics still work.
  const styles = {
    Confirmed:    "bg-[#C84C21] text-white",
    Gap:          "bg-[#5C6470]/15 text-[#0F1825]",
    Emerging:     "bg-transparent border border-[#A09680]/40 text-[#A09680]",
    NotAssessed:  "bg-transparent border border-dashed border-[#C8C0AE] text-[#A09680]",
  };
  const v = value || "NotAssessed";
  const display =
    v === "Confirmed" ? "Established"
    : v === "Gap" ? "To address"
    : v === "Emerging" ? "Developing"
    : "Not in scope";
  return <span className={`inline-block w-full py-2 text-[12px] tracking-normal ${styles[v]}`}>{display}</span>;
}

function DefinitionBlock({ tone, Icon, title, children }) {
  const tones = {
    confirmed:   { wrap: "border-[#C84C21]/30 bg-[#C84C21]/[0.03]", icon: "text-[#C84C21]" },
    gap:         { wrap: "border-[#5C6470]/25", icon: "text-[#5C6470]" },
    emerging:    { wrap: "border-[#A09680]/40", icon: "text-[#A09680]" },
    notassessed: { wrap: "border-dashed border-[#C8C0AE]", icon: "text-[#A09680]" },
  };
  const t = tones[tone] || tones.notassessed;
  return (
    <div className={`border ${t.wrap} p-4`} data-testid={`gap-def-${tone}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} className={t.icon} />
        <span className="text-[13px] text-[#0F1825] font-medium">{title}</span>
      </div>
      <p className="text-[12.5px] text-[#5C6470] leading-relaxed">{children}</p>
    </div>
  );
}

function LegendSwatch({ tone, label }) {
  const tones = {
    confirmed:   "bg-[#C84C21] border-[#C84C21]",
    gap:         "bg-[#5C6470]/15 border-[#5C6470]/30",
    emerging:    "bg-transparent border-[#A09680]/40",
    notassessed: "bg-transparent border-dashed border-[#C8C0AE]",
  };
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-block w-4 h-4 border ${tones[tone]}`} />
      <span className="text-[#5C6470] text-[11.5px]">{label}</span>
    </span>
  );
}

/* ====================== TEAM RECOMMENDATIONS (HERO) ====================== */

// ── Citation helpers ─────────────────────────────────────────────────────
// The synthesis engine embeds numeric citations (e.g. "the 44% gap [2]") in
// the interpretation and strategic recommendation, and returns a parallel
// `references` array on the finding: [{ n, source_url, source_summary,
// study_citation, quote_snippet, verified, verification_note, ... }].
// <CitedText> replaces every [n] token with a hoverable superscript that
// scrolls to its entry in the <ReferencesList> at the foot of the LEFT cell.

const VERIFY_PALETTE = {
  true:  { chip: "bg-[#EAF3EC] text-[#2E4A38] border-[#B6D2BC]",   dot: "#3F6D55", label: "Verified" },
  false: { chip: "bg-[#FDECE4] text-[#8C3A12] border-[#E8A985]",   dot: "#C84C21", label: "Unsupported" },
  null:  { chip: "bg-[#FAF7F1] text-[#5C6470] border-[#E8E1D2]",   dot: "#A09680", label: "Pending audit" },
};

// Peer-reviewed enrichment status → chip palette. Independent of the
// Claude-audit verdict — this is 'does the paper exist and is it retracted?'
// rather than 'does the paper actually support this claim?'.
const ENRICH_PALETTE = {
  verified:   { chip: "bg-[#EAF3EC] text-[#2E4A38] border-[#B6D2BC]", label: "Peer-reviewed" },
  not_found:  { chip: "bg-[#FDF1E6] text-[#8C3A12] border-[#E8C19A]", label: "DOI not resolved" },
  retracted:  { chip: "bg-[#FCE2E2] text-[#8B1A1A] border-[#E8A0A0]", label: "RETRACTED" },
  skipped:    { chip: "bg-[#FAF7F1] text-[#A09680] border-[#E8E1D2]", label: "Patient voice" },
};

function EnrichmentChip({ status, testid }) {
  if (!status) return null;
  const p = ENRICH_PALETTE[status];
  if (!p) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] px-1.5 py-[1px] border ${p.chip}`}
      data-testid={testid}
    >
      {p.label}
    </span>
  );
}

function RefVerificationBadge({ verified, testid }) {
  const p = VERIFY_PALETTE[verified === null || verified === undefined ? "null" : String(!!verified)];
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] px-1.5 py-[1px] border ${p.chip}`}
      data-testid={testid}
      title={p.label}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: p.dot }} />
      {p.label}
    </span>
  );
}

function CitedText({ text, referenceIds, testid }) {
  // Splits the text on [n] tokens and renders each as a clickable superscript
  // linking to the reference entry. Non-citation runs pass through <AutoAbbr>
  // for medical short-form tooltips. When no references, falls back to plain.
  if (!text) return null;
  const parts = String(text).split(/(\[\d+\])/g);
  return (
    <span data-testid={testid}>
      {parts.map((chunk, i) => {
        const m = chunk.match(/^\[(\d+)\]$/);
        if (m) {
          const n = parseInt(m[1], 10);
          const anchor = referenceIds ? `${referenceIds}-ref-${n}` : null;
          return (
            <a
              key={i}
              href={anchor ? `#${anchor}` : undefined}
              onClick={(ev) => {
                if (!anchor) return;
                ev.preventDefault();
                const el = document.getElementById(anchor);
                if (el) {
                  el.scrollIntoView({ block: "center", behavior: "smooth" });
                  el.classList.add("bg-[#FDF1E6]");
                  setTimeout(() => el.classList.remove("bg-[#FDF1E6]"), 1400);
                }
              }}
              className="align-super text-[10px] text-[#C84C21] hover:text-[#0F1825] font-mono no-underline mx-[1px]"
              data-testid={`citation-${referenceIds}-${n}`}
            >
              [{n}]
            </a>
          );
        }
        return <AutoAbbr key={i} text={chunk} />;
      })}
    </span>
  );
}

function ReferencesList({ references, containerId, testid }) {
  if (!references || references.length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-[#F0EBDC]" data-testid={testid}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#A09680] mb-2">
        References
      </div>
      <ol className="space-y-2">
        {references.map((r) => {
          // Prefer the peer-reviewed canonical title (from Crossref) over the
          // Perplexity-supplied study_citation when the DOI resolved. The DOI
          // link gives the reader a one-click way to open the paper on the
          // publisher site.
          const useCanonical = r.enrichment_status === "verified" && r.canonical_title;
          const titleText = useCanonical ? r.canonical_title : r.study_citation;
          const yearParts = useCanonical
            ? [r.canonical_journal, r.canonical_year].filter(Boolean).join(", ")
            : null;
          const openHref = r.doi
            ? `https://doi.org/${r.doi}`
            : (r.open_access_url || r.source_url || null);
          return (
            <li
              key={r.n}
              id={containerId ? `${containerId}-ref-${r.n}` : undefined}
              className="flex gap-2 items-start text-[11.5px] leading-relaxed text-[#5C6470] transition-colors duration-500"
              data-testid={`reference-${containerId}-${r.n}`}
            >
              <span className="font-mono text-[10px] text-[#C84C21] shrink-0 min-w-[16px]">[{r.n}]</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                  {r.stream && (
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] bg-white border border-[#C84C21]/30 text-[#C84C21] px-1.5 py-[1px]">
                      {r.stream}
                    </span>
                  )}
                  <RefVerificationBadge verified={r.verified} testid={`reference-verify-${containerId}-${r.n}`} />
                  <EnrichmentChip status={r.enrichment_status} testid={`reference-enrich-${containerId}-${r.n}`} />
                  {r.citation_count != null && r.enrichment_status === "verified" && (
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#5C6470] bg-white border border-[#E8E1D2] px-1.5 py-[1px]" title="Semantic Scholar citation count">
                      Cited {r.citation_count}×
                    </span>
                  )}
                  {r.influential_citations != null && r.influential_citations > 0 && (
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#8C3A12] bg-[#FDF1E6] border border-[#E8C19A] px-1.5 py-[1px]" title="Semantic Scholar influential-citation count">
                      {r.influential_citations} influential
                    </span>
                  )}
                </div>
                {titleText && (
                  <div className="text-[#0F1825] text-[12px] mb-0.5">
                    {openHref ? (
                      <a href={openHref} target="_blank" rel="noopener noreferrer" className="hover:text-[#C84C21] underline decoration-[#C84C21]/30 hover:decoration-[#C84C21]">
                        {titleText}
                      </a>
                    ) : titleText}
                  </div>
                )}
                {yearParts && (
                  <div className="text-[11px] text-[#A09680] italic mb-0.5">{yearParts}</div>
                )}
                {r.quote_snippet && (
                  <div className="italic text-[11px] text-[#5C6470] mb-0.5">&ldquo;{r.quote_snippet}&rdquo;</div>
                )}
                {r.claim_summary && (
                  <div className="text-[10.5px] text-[#A09680]">Supports: {r.claim_summary}</div>
                )}
                {r.verified === false && r.verification_note && (
                  <div className="mt-1 text-[10.5px] text-[#8C3A12] italic">Audit note: {r.verification_note}</div>
                )}
                {r.enrichment_status === "retracted" && (
                  <div className="mt-1 text-[10.5px] text-[#8B1A1A] font-medium">
                    Retraction: {r.retraction_reason || "flagged in Crossref"}
                  </div>
                )}
                {r.doi && (
                  <div className="mt-0.5 text-[10.5px] font-mono text-[#A09680]" data-testid={`reference-doi-${containerId}-${r.n}`}>
                    DOI · <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noopener noreferrer" className="text-[#C84C21] hover:text-[#0F1825]">{r.doi}</a>
                    {r.resolved_via && r.resolved_via !== "doi_in_text" && (
                      <span className="ml-1 text-[#A09680] italic">· {r.resolved_via}</span>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function VerificationStatusChip({ status }) {
  const map = {
    verified:            { bg: "bg-[#EAF3EC]", fg: "text-[#2E4A38]", border: "border-[#B6D2BC]", label: "All refs verified" },
    partially_verified:  { bg: "bg-[#FDF1E6]", fg: "text-[#8C3A12]", border: "border-[#E8C19A]", label: "Partially verified" },
    unsupported:         { bg: "bg-[#FDECE4]", fg: "text-[#8C3A12]", border: "border-[#E8A985]", label: "Unsupported (audited)" },
    pending_audit:       { bg: "bg-[#FAF7F1]", fg: "text-[#5C6470]", border: "border-[#E8E1D2]", label: "Audit pending" },
    uncited:             { bg: "bg-[#FAF7F1]", fg: "text-[#A09680]", border: "border-[#E8E1D2]", label: "No inline citations" },
  };
  const p = map[status];
  if (!p) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] px-1.5 py-[1px] border ${p.bg} ${p.fg} ${p.border}`}
      data-testid={`verification-status-${status}`}
    >
      {p.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ReferencesTitleColumn — right-hand column of the Team Recommendations table.
// Renders exactly what the user asked for on this tab: nothing but a numbered
// list of hyperlinked reference titles. The `n` is the globally-scoped
// bibliography number (built by the `rebuild-bibliography` backend pass), so
// the inline `[N]` markers in the theme + recommendation text on the left
// two columns point at these exact rows. Hyperlink target priority:
// DOI → open-access PDF → source URL. If none are present the title still
// renders as plain text — never a dangling anchor.
// ─────────────────────────────────────────────────────────────────────────────
function ReferencesTitleColumn({ findingRefs, globalRefsByN, testid }) {
  if (!findingRefs || findingRefs.length === 0) {
    return <div className="text-[11.5px] italic text-[#A09680]" data-testid={`${testid}-empty`}>No references.</div>;
  }
  // Sort by n so the list reads in bibliography order — matches the [N]
  // markers on the left as the reader scans down the recommendation.
  const rows = [...findingRefs].sort((a, b) => Number(a.n || 0) - Number(b.n || 0));
  return (
    <ol className="space-y-2" data-testid={testid}>
      {rows.map((r) => {
        const n = Number(r.n);
        const biblio = globalRefsByN.get(n) || {};
        const title = (biblio.title || r.study_citation || r.source_summary || "Untitled reference").trim();
        const doi = biblio.doi;
        const url = doi
          ? `https://doi.org/${doi}`
          : (biblio.open_access_url || biblio.url || r.source_url || "");
        return (
          <li key={n} className="flex items-baseline gap-2 text-[12.5px] leading-snug" data-testid={`${testid}-row-${n}`}>
            <span className="font-mono text-[10px] text-[#C84C21] shrink-0 tabular-nums">[{n}]</span>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0F1825] underline decoration-[#C84C21]/40 decoration-1 underline-offset-[3px] hover:decoration-[#C84C21] hover:text-[#C84C21] transition-colors break-words"
                data-testid={`${testid}-link-${n}`}
              >
                {title}
              </a>
            ) : (
              <span className="text-[#5C6470]">{title}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function FunctionViewsTab({ id, eng, isClient, scrollTo, setScrollTo }) {
  const [findings, setFindings] = useState([]);
  const [teamFilter, setTeamFilter] = useState("ALL");
  // Highlight target — set when a deep-link from Triangulation View arrives.
  // Used to apply a brief ring effect on the matched row so the user's eye
  // lands in the right place after the scroll.
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    (async () => {
      const f = await axios.get(`${API}/console/engagements/${id}/findings`);
      setFindings(f.data.findings || []);
    })();
  }, [id]);

  // Global, deduped bibliography — lives on the engagement doc. Every
  // finding's per-row references[].n is a pointer into this list; the row
  // carries the hyperlinked title used in the References column. Falls
  // back to an empty list on older engagements that pre-date the
  // rebuild-bibliography pass.
  const globalRefs = useMemo(() => eng?.global_references || [], [eng?.global_references]);
  const refsByN = useMemo(() => {
    const m = new Map();
    for (const r of globalRefs) {
      m.set(Number(r.n), r);
    }
    return m;
  }, [globalRefs]);

  // When a scrollTo target arrives (from Triangulation View → FindingCard
  // forward link), wait for findings to load and the rows to render, then
  // scroll the matched row into view + highlight it briefly. The team
  // filter is reset to ALL first so the target row is never filtered out.
  useEffect(() => {
    if (!scrollTo || findings.length === 0) return;
    setTeamFilter("ALL");
    // Wait one tick so the row has mounted, then scroll.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pair-${scrollTo}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(scrollTo);
        // Auto-fade highlight after 2.5s
        setTimeout(() => setHighlightId(null), 2500);
      }
      if (setScrollTo) setScrollTo(null);
    }, 250);
    return () => clearTimeout(t);
  }, [scrollTo, findings, setScrollTo]);

  // Aggregate metrics for the hero header.
  const teamCounts = useMemo(() => {
    const m = new Map();
    for (const f of findings) {
      for (const o of (f.owners || [])) m.set(o, (m.get(o) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [findings]);

  const strengthCounts = useMemo(() => {
    const m = { Confirmed: 0, Emerging: 0, Gap: 0 };
    for (const f of findings) {
      const v = f.confidence || "Emerging";
      if (m[v] !== undefined) m[v] += 1;
    }
    return m;
  }, [findings]);

  const totalSignalCount = useMemo(
    () => findings.reduce((acc, f) => acc + (f.signal_count || 0), 0),
    [findings],
  );

  // Filtered + grouped by V2E theme so the page reads like a briefing deck.
  const filtered = useMemo(() => {
    if (teamFilter === "ALL") return findings;
    return findings.filter((f) => (f.owners || []).includes(teamFilter));
  }, [findings, teamFilter]);

  const byTheme = useMemo(() => {
    const groups = {};
    for (const f of filtered) {
      const theme = f.v2e_theme || (f.v2e_subtheme ? f.v2e_subtheme[0] : "?");
      if (!groups[theme]) groups[theme] = [];
      groups[theme].push(f);
    }
    // Preserve V2E theme order (A → E)
    return V2E_THEMES.map((t) => ({ theme: t, items: groups[t.key] || [] })).filter((g) => g.items.length > 0);
  }, [filtered]);

  return (
    <div data-testid="functions-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Team Recommendations" : "Stage 06 of 06 · Team Recommendations"}
        title="Every patient theme — and the action it produced."
      />

      {/* HERO METRICS — frames the page as a deliverable artifact */}
      {findings.length > 0 && (
        <div className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E8E1D2] bg-white" data-testid="hero-metrics">
          <HeroMetric label="Patient themes" value={findings.length} sub={`${V2E_THEMES.length} V2E themes · 15 sub-themes`} />
          <HeroMetric label="Verified signals" value={totalSignalCount} sub="Across L1-L5" />
          <HeroMetric label="Strong recommendations" value={strengthCounts.Confirmed} sub={`${strengthCounts.Emerging} emerging · ${strengthCounts.Gap} conditional`} accent />
          <HeroMetric label="Teams engaged" value={teamCounts.length} sub="Cross-functional ownership" />
        </div>
      )}

      {/* FILTER BAR — by owning team */}
      {findings.length > 0 && (
        <div className="mb-4 bg-white border border-[#E8E1D2] px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="rec-filters">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] shrink-0">Filter by team</span>
          <FilterPill active={teamFilter === "ALL"} onClick={() => setTeamFilter("ALL")} testid="team-filter-all">All</FilterPill>
          {teamCounts.map(([t, c]) => (
            <FilterPill key={t} active={teamFilter === t} onClick={() => setTeamFilter(t)} testid={`team-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              {t}<span className="ml-1.5 text-[#A09680] font-normal">{c}</span>
            </FilterPill>
          ))}
        </div>
      )}

      <ClientOutput>
        Each row pairs one triangulated patient theme with the single most-leveraged team action it produced — recommendations are scientifically anchored to the evidence on the left, written for leadership-briefing use.
      </ClientOutput>

      {filtered.length === 0 ? (
        <Empty>No themes or recommendations match the current filter.</Empty>
      ) : (
        <div data-testid="theme-rec-table">
          {byTheme.map(({ theme, items }) => (
            <section key={theme.key} className="mb-6 last:mb-0" data-testid={`theme-group-${theme.key}`}>
              {/* V2E theme banner — sticky on scroll, premium */}
              <div
                className="sticky top-0 z-10 flex items-baseline gap-3 px-4 py-2.5 text-white"
                style={{ backgroundColor: theme.accent }}
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold">{theme.key}</span>
                <span className="opacity-80">·</span>
                <span className="text-[13px] font-medium tracking-tight">{theme.label}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">
                  {items.length} {items.length === 1 ? "recommendation" : "recommendations"}
                </span>
              </div>

              {/* Column headers — three columns: theme · recommendation · references */}
              <div className="hidden lg:grid grid-cols-12 gap-0 border-x border-b border-[#E8E1D2] bg-[#FAF7F1]">
                <div className="col-span-4 px-5 py-2.5 border-r border-[#E8E1D2] font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]">Key patient theme</div>
                <div className="col-span-5 px-5 py-2.5 border-r border-[#E8E1D2] font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]">Team recommendation</div>
                <div className="col-span-3 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]">References</div>
              </div>

              {items.map((f) => {
                const sub = f.v2e_subtheme && V2E_SUBTHEME_LOOKUP[f.v2e_subtheme];
                const owners = Array.isArray(f.owners) ? f.owners : [];
                return (
                  <div
                    key={f.id}
                    className={`grid grid-cols-1 lg:grid-cols-12 gap-0 border-x border-b border-[#E8E1D2] bg-white hover:bg-[#FAF7F1]/30 transition-colors ${highlightId === f.id ? "ring-2 ring-[#C84C21] ring-inset" : ""}`}
                    data-testid={`pair-${f.id}`}
                  >
                    {/* COL 1 — Key patient theme (4/12) */}
                    <div
                      className="col-span-1 lg:col-span-4 px-5 py-5 border-r-0 lg:border-r border-b lg:border-b-0 border-[#E8E1D2]"
                      data-testid={`theme-${f.id}`}
                    >
                      {sub && (
                        <div className="mb-2 flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[#A09680]">
                            {sub.key}<span className="mx-1.5">·</span><span className="normal-case tracking-normal text-[11px] text-[#5C6470]">{sub.label}</span>
                          </span>
                        </div>
                      )}
                      <p className="font-serif text-[15px] text-[#0F1825] leading-snug" data-testid={`theme-text-${f.id}`}>
                        <CitedText text={f.theme} referenceIds={f.id} testid={`theme-cited-${f.id}`} />
                      </p>
                      {f.interpretation && (
                        <p
                          className="mt-3 pt-3 border-t border-[#F0EBDC] text-[12.5px] text-[#5C6470] leading-relaxed italic"
                          data-testid={`theme-interpretation-${f.id}`}
                        >
                          <CitedText text={f.interpretation} referenceIds={f.id} testid={`interpretation-cited-${f.id}`} />
                        </p>
                      )}
                    </div>

                    {/* COL 2 — Team recommendation (5/12) */}
                    <div
                      className="col-span-1 lg:col-span-5 px-5 py-5 border-r-0 lg:border-r border-b lg:border-b-0 border-[#E8E1D2]"
                      data-testid={`rec-${f.id}`}
                    >
                      {owners.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`rec-teams-${f.id}`}>
                          {owners.map((o) => (
                            <span
                              key={o}
                              className="font-mono text-[9.5px] uppercase tracking-[0.16em] bg-[#0F1825] text-white px-2 py-[3px]"
                            >
                              {o}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-[14.5px] text-[#0F1825] leading-relaxed" data-testid={`rec-text-${f.id}`}>
                        {f.strategic_recommendation
                          ? <CitedText text={f.strategic_recommendation} referenceIds={f.id} testid={`rec-cited-${f.id}`} />
                          : <span className="italic text-[#A09680]">Recommendation pending finalisation.</span>}
                      </p>
                    </div>

                    {/* COL 3 — References (3/12) — hyperlinked titles only */}
                    <div
                      className="col-span-1 lg:col-span-3 px-5 py-5"
                      data-testid={`refs-${f.id}`}
                    >
                      <ReferencesTitleColumn
                        findingRefs={f.references || []}
                        globalRefsByN={refsByN}
                        testid={`refs-list-${f.id}`}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function HeroMetric({ label, value, sub, accent }) {
  return (
    <div className={`px-5 py-4 border-b lg:border-b-0 lg:border-r border-[#E8E1D2] last:border-r-0 ${accent ? "bg-[#FAF7F1]/40" : ""}`}>
      <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] mb-1">{label}</div>
      <div className={`font-serif text-[28px] leading-none ${accent ? "text-[#C84C21]" : "text-[#0F1825]"}`}>{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-[#5C6470]">{sub}</div>}
    </div>
  );
}

function FilterPill({ children, active, onClick, testid, tone = "default" }) {
  const base = "inline-flex items-center text-[11px] px-2.5 py-1 font-medium transition-colors";
  const palette = {
    default: active ? "bg-[#0F1825] text-white" : "bg-white border border-[#E8E1D2] text-[#5C6470] hover:border-[#0F1825] hover:text-[#0F1825]",
    strong:  active ? "bg-[#C84C21] text-white" : "bg-white border border-[#C84C21]/30 text-[#C84C21] hover:bg-[#C84C21] hover:text-white",
    watch:   active ? "bg-[#5C6470] text-white" : "bg-white border border-[#5C6470]/30 text-[#5C6470] hover:bg-[#5C6470] hover:text-white",
    conditional: active ? "bg-[#A09680] text-white" : "bg-white border border-[#A09680]/30 text-[#A09680] hover:bg-[#A09680] hover:text-white",
  };
  return (
    <button type="button" onClick={onClick} className={`${base} ${palette[tone]}`} data-testid={testid}>
      {children}
    </button>
  );
}

/* ====================== ENGAGEMENT DELIVERABLES ====================== */
function DeliverablesTab({ id, eng, isClient }) {
  const [items, setItems] = useState([]);
  const reload = async () => {
    const { data } = await axios.get(`${API}/console/engagements/${id}/deliverables`);
    setItems(data.deliverables || []);
  };
  useEffect(() => {
    reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id]);

  return (
    <div data-testid="deliverables-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Engagement Deliverables" : "Stage 07 of 08 · Engagement Deliverables"}
        title="Everything from this engagement — in one place."
        lede="The full engagement take-away: the auto-generated leadership and scope reports, plus every team-specific evidence output produced by the Voice2Evidence team. Below the deliverables, see how MarksMan can help operationalise any recommendation."
      />

      <ClientOutput>
        One consolidated take-away zone — auto-generated reports and team-specific evidence outputs, every artefact traceable to its evidence basis.
      </ClientOutput>

      {/* ---------- Engagement reports (auto-generated PDFs) ---------- */}
      <section className="mt-2" data-testid="engagement-reports">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">Engagement reports</div>
        <div className="text-[11.5px] italic text-[#A09680] mb-3">
          Generated live from the workspace. Carries the Voice2Evidence™ wordmark and the illustrative-engagement disclosure.
        </div>
        <div className="bg-white border border-[#E8E1D2] p-5 flex flex-wrap gap-3">
          <PdfDownloadButton
            label="Engagement Brief PDF"
            testid="deliverables-pdf-engagement-brief"
            Icon={FileDown}
            filename={`Voice2Evidence-Engagement-Brief-${slugify(eng?.brand)}.pdf`}
            buildDoc={() => <EngagementBriefReport eng={eng} />}
          />
        </div>
      </section>

      {/* ---------- Team-specific evidence outputs ---------- */}
      <section className="mt-8" data-testid="team-outputs">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-2">Team-specific evidence outputs</div>
        <div className="text-[11.5px] italic text-[#A09680] mb-3">
          Produced by the Voice2Evidence team and attached here for traceable access — one artefact per cross-functional team.
        </div>
        <ul className="space-y-3">
          {items.map((d) => (
            <li key={d.id} className="bg-white border border-[#E8E1D2] p-5" data-testid={`deliverable-${d.id}`}>
              <div className="flex items-baseline justify-between mb-2 gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5C6470]">{d.function}</span>
                <StatusPill value={d.status} />
              </div>
              <h3 className="font-serif text-[1.15rem] text-[#0F1825] leading-snug">{d.title}</h3>
              <p className="mt-2 text-[13.5px] text-[#5C6470] leading-relaxed">{d.summary}</p>

              <div className="mt-3 pt-3 border-t border-[#F0EBDC] grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-[12px]">
                <div>
                  <Label>Methodologist</Label>
                  <Value>{d.methodologist}</Value>
                </div>
                <div>
                  <Label>Evidence basis</Label>
                  <Value>{(d.linked_finding_themes || []).length} linked findings</Value>
                </div>
                <div>
                  <Label>Last updated</Label>
                  <Value>{d.last_updated}</Value>
                </div>
              </div>

              {/* Client PDF — upload / view / replace / remove (operator) or view / download only (client) */}
              <DeliverableFileWidget
                engagementId={id}
                deliverable={d}
                onChange={reload}
                isClient={isClient}
              />
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- Activation support callout ---------- */}
      <section
        className="mt-10 border-l-2 border-[#C84C21] bg-white pl-6 pr-6 py-6"
        data-testid="activation-support"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1.5">Beyond delivery · Activation support</div>
        <h3 className="font-serif text-[1.2rem] text-[#0F1825] leading-snug">
          MarksMan can extend this engagement to help operationalise any recommendation above.
        </h3>
        <p className="mt-3 text-[13.5px] text-[#5C6470] leading-relaxed">
          The engagement deliverable closes the V2E loop. Where teams want hands-on execution support — building MSL discussion modules from a Strong recommendation, commissioning the evidence named in a Conditional recommendation, designing the watch mechanism for a directional thread, or briefing advocacy partners — MarksMan Healthcare can scope a follow-on activation engagement separately from V2E.
        </p>
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px] text-[#0F1825]">
          <li className="flex gap-2">
            <span className="text-[#C84C21] mt-1.5 text-[7px] shrink-0">●</span>
            <span><span className="text-[#0F1825]">Operationalise</span> Strong recommendations into team-specific playbooks.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#C84C21] mt-1.5 text-[7px] shrink-0">●</span>
            <span><span className="text-[#0F1825]">Commission</span> the evidence named in Conditional recommendations.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#C84C21] mt-1.5 text-[7px] shrink-0">●</span>
            <span><span className="text-[#0F1825]">Stand up</span> the watch mechanism for directional threads.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#C84C21] mt-1.5 text-[7px] shrink-0">●</span>
            <span><span className="text-[#0F1825]">Brief</span> patient advocacy and field teams on patient-relevant priorities.</span>
          </li>
        </ul>
        <div className="mt-5 flex flex-wrap gap-3">
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] text-white font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
            data-testid="activation-cta-scope"
          >
            Scope an activation engagement
            <ArrowRight size={13} />
          </a>
          <a
            href="mailto:amit.d@marksmanhealthcare.com?subject=Activation%20support%20—%20follow-on%20to%20V2E%20engagement"
            className="inline-flex items-center gap-2 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#0F1825] font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
            data-testid="activation-cta-methodologist"
          >
            Reach the lead methodologist
          </a>
        </div>
        <p className="mt-4 text-[10.5px] font-mono uppercase tracking-[0.16em] text-[#A09680]">
          Activation engagements are scoped separately from V2E and do not reopen the closed deliverable.
        </p>
      </section>
    </div>
  );
}

/* =========== Deliverable file upload widget =========== */
function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Simulated artefact formats — used to render real-feeling Download buttons
// on deliverable cards that don't have a real attached client PDF. Clicking
// generates a tiny placeholder file in the right MIME and triggers a real
// browser download so the demo click flow feels complete.
const SIMULATED_FORMATS = [
  { ext: "pdf",  label: "PDF",  mime: "application/pdf" },
  { ext: "docx", label: "Word", mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
  { ext: "pptx", label: "PPT",  mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
];

function triggerSimulatedDownload(deliverable, fmt) {
  const filename = `Voice2Evidence-${slugify(deliverable.function)}-${slugify(deliverable.title)}.${fmt.ext}`;
  const stub = [
    "Voice2Evidence(TM) — illustrative evidence-output placeholder",
    "",
    `Deliverable: ${deliverable.title}`,
    `Function: ${deliverable.function}`,
    `Methodologist: ${deliverable.methodologist || ""}`,
    "",
    "This file is a simulated engagement output rendered for demonstration purposes only.",
    "It is not based on a live engagement, client data, confidential information,",
    "or proprietary information from the asset owner.",
  ].join("\n");
  const blob = new Blob([stub], { type: fmt.mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
}

function SimulatedDownloadRow({ deliverable }) {
  return (
    <div data-testid={`deliverable-simulated-${deliverable.id}`}>
      <div className="text-[11.5px] text-[#A09680] mb-2">Download evidence output</div>
      <div className="flex flex-wrap gap-2">
        {SIMULATED_FORMATS.map((fmt) => (
          <button
            key={fmt.ext}
            type="button"
            onClick={() => triggerSimulatedDownload(deliverable, fmt)}
            className="inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21] hover:text-[#C84C21] text-[#0F1825] text-[12px] px-2.5 py-1.5 transition-colors"
            data-testid={`deliverable-download-${fmt.ext}-${deliverable.id}`}
          >
            <Download size={12} className="text-[#C84C21]" />
            {fmt.label}
            <span className="text-[#A09680] text-[10.5px]">.{fmt.ext}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DeliverableFileWidget({ engagementId, deliverable, onChange, isClient }) {
  const d = deliverable;
  const hasFile = !!d.uploaded_pdf_filename;
  const [busy, setBusy] = useState(null); // null | "upload" | "delete"
  const [error, setError] = useState("");
  const inputId = `deliv-file-${d.id}`;

  const uploadFile = async (file) => {
    if (!file) return;
    setError("");
    if (file.type && file.type !== "application/pdf") {
      setError("Only PDF files are accepted.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError("File exceeds the 25 MB limit.");
      return;
    }
    setBusy("upload");
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      await axios.post(
        `${API}/console/engagements/${engagementId}/deliverables/${d.id}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await onChange();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Try again.";
      setError(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setBusy(null);
    }
  };

  const onPick = (e) => uploadFile(e.target.files?.[0]);

  const onView = () => {
    const url = `${API}/console/engagements/${engagementId}/deliverables/${d.id}/file?v=${encodeURIComponent(d.uploaded_pdf_file_id || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onRemove = async () => {
    if (!window.confirm(`Remove "${d.uploaded_pdf_filename}" from this deliverable?`)) return;
    setError("");
    setBusy("delete");
    try {
      await axios.delete(`${API}/console/engagements/${engagementId}/deliverables/${d.id}/file`);
      await onChange();
    } catch {
      setError("Could not remove file.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-[#F0EBDC]" data-testid={`deliverable-file-${d.id}`}>
      {/* Hidden file input — only attached in operator mode (client view never uploads) */}
      {!isClient && (
        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={onPick}
          data-testid={`deliverable-file-input-${d.id}`}
        />
      )}

      {hasFile ? (
        <div className="bg-[#FAF7F1] border border-[#E8E1D2] p-3">
          <div className="flex items-start gap-3">
            <FileText size={16} className="text-[#C84C21] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680] mb-1">
                {isClient ? "Evidence output available" : "Client PDF attached"}
              </div>
              <div className="text-[13.5px] text-[#0F1825] truncate" title={d.uploaded_pdf_filename}>{d.uploaded_pdf_filename}</div>
              <div className="text-[11px] text-[#5C6470] mt-1">
                {formatBytes(d.uploaded_pdf_size)}
                {!isClient && <> · uploaded {d.uploaded_pdf_at?.slice(0, 10)} · {d.uploaded_pdf_by}</>}
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onView}
              className="inline-flex items-center gap-1.5 bg-[#C84C21] hover:bg-[#0F1825] text-white font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors"
              data-testid={`deliverable-view-${d.id}`}
            >
              <Download size={11} />
              {isClient ? "Download PDF" : "View / download"}
            </button>
            {/* Replace / Remove — operator only */}
            {!isClient && (
              <>
                <label
                  htmlFor={inputId}
                  className={`inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#0F1825] font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors cursor-pointer ${busy === "upload" ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid={`deliverable-replace-${d.id}`}
                >
                  <FileText size={11} className="text-[#C84C21]" />
                  {busy === "upload" ? "Uploading…" : "Replace"}
                </label>
                <button
                  type="button"
                  onClick={onRemove}
                  disabled={busy === "delete"}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E8E1D2] hover:border-[#C84C21]/60 text-[#5C6470] font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1.5 transition-colors disabled:opacity-50"
                  data-testid={`deliverable-remove-${d.id}`}
                >
                  {busy === "delete" ? "Removing…" : "Remove"}
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        /* No real client PDF attached yet — render simulated download
           buttons so the deliverable card always has actionable artefacts
           to hand off during the demo. The operator view also keeps the
           "Upload client PDF" composer below for real engagements. */
        isClient ? (
          <SimulatedDownloadRow deliverable={d} />
        ) : (
          <div>
            <SimulatedDownloadRow deliverable={d} />
            <div className="mt-4 pt-4 border-t border-[#F0EBDC]">
              <label
                htmlFor={inputId}
                className={`inline-flex items-center gap-2 bg-white border border-dashed border-[#C84C21]/60 hover:border-[#C84C21] text-[#0F1825] text-[12px] px-3.5 py-2.5 transition-colors cursor-pointer ${busy === "upload" ? "opacity-50 pointer-events-none" : ""}`}
                data-testid={`deliverable-upload-${d.id}`}
              >
                <FileText size={13} className="text-[#C84C21]" />
                {busy === "upload" ? "Uploading…" : "Upload real client PDF"}
              </label>
              <p className="mt-2 text-[11px] text-[#A09680]">PDF only · 25 MB max · replaces the simulated outputs above</p>
            </div>
          </div>
        )
      )}

      {error && !isClient && (
        <p className="mt-2 text-[11.5px] text-[#C84C21]" data-testid={`deliverable-file-error-${d.id}`}>{error}</p>
      )}
    </div>
  );
}

function StatusPill({ value }) {
  const styles = {
    // Deliverable statuses
    "Drafting": "border-[#A09680] text-[#A09680] bg-white",
    "Internal Review": "border-[#5C6470] text-[#5C6470] bg-white",
    "Client Ready": "border-[#3F6D55] text-[#3F6D55] bg-[#F0F4F1]",
    "Issued": "border-[#C84C21] text-[#C84C21] bg-white",
    "Acted On": "border-[#C84C21] text-white bg-[#C84C21]",
    // Action statuses
    "Recommended": "border-[#A09680] text-[#A09680] bg-white",
    "Accepted": "border-[#3F6D55] text-[#3F6D55] bg-[#F0F4F1]",
    "In Progress": "border-[#5C6470] text-[#5C6470] bg-white",
    "Implemented": "border-[#C84C21] text-white bg-[#C84C21]",
    "Monitoring": "border-[#A09680] text-[#A09680] bg-white",
    "Closed": "border-[#5C6470] text-white bg-[#5C6470]",
    // Methodology statuses
    "Applied": "border-[#C84C21] text-[#C84C21] bg-white",
    "In Review": "border-[#5C6470] text-[#5C6470] bg-white",
    "Scoped": "border-[#A09680] text-[#A09680] bg-white",
  };
  const cls = styles[value] || "border-[#A09680] text-[#A09680] bg-white";
  return <span className={`font-mono text-[10px] uppercase tracking-[0.16em] border ${cls} px-2 py-0.5 shrink-0`}>{value}</span>;
}

/* ======================== ACTIONS ======================== */
function ActionsTab({ id, isClient }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/console/engagements/${id}/actions`);
      setItems(data.actions || []);
    })();
  }, [id]);

  const filtered = useMemo(() => {
    if (statusFilter === "All") return items;
    return items.filter((a) => (ACTION_STATUS_MAP[a.status] || a.status) === statusFilter);
  }, [items, statusFilter]);

  return (
    <div data-testid="actions-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Action Tracker" : "Stage 08 of 09 · Action Tracker"}
        title="How insights become action."
        lede="The closed loop. Every evidence-supported insight becomes a recommendation, owned by a function lead. Status and follow-up signal are tracked through the engagement until the loop closes."
      />

      <ClientOutput>
        Recommendations translated into a named owner, action type, status, evidence basis, follow-up signal, and the next review checkpoint.
      </ClientOutput>

      {/* Status filter — grouped by the three Evidence Maturity buckets
          (To do / In progress / Done) so the operational pipeline reads
          consistently with the executive scorecard. Action Tracker keeps
          all 6 granular states; Evidence Maturity rolls them into 3.
          See ACTION_STATUS_MAP for the mapping. */}
      <div className="mb-5 bg-white border border-[#E8E1D2] p-3" data-testid="action-status-filter">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <button
            onClick={() => setStatusFilter("All")}
            className={`text-[12px] tracking-normal border px-2.5 py-1 transition-colors ${
              statusFilter === "All"
                ? "bg-[#0F1825] border-[#0F1825] text-white"
                : "bg-white border-[#E8E1D2] text-[#5C6470] hover:border-[#C84C21]/60"
            }`}
            data-testid="action-status-all"
          >
            All
          </button>
          <ActionStatusGroup
            bucket="To do"
            statuses={["Recommended", "Accepted"]}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
          <ActionStatusGroup
            bucket="In progress"
            statuses={["In Progress", "Monitoring"]}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
          <ActionStatusGroup
            bucket="Done"
            statuses={["Implemented", "Closed"]}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        </div>
        <p className="mt-2.5 text-[11px] text-[#A09680] leading-relaxed">
          Statuses below roll up into the three Evidence Maturity buckets. Click any chip to filter.
        </p>
      </div>

      <div className="space-y-4">
        {filtered.map((a) => {
          const mappedStatus = ACTION_STATUS_MAP[a.status] || a.status;
          const followUp = a.observed_impact === "—" ? "Pending — loop still open." : a.observed_impact;
          return (
            <article key={a.id} className="bg-white border border-[#E8E1D2] p-6" data-testid={`action-${a.id}`}>
              {/* Header row: owner / function / status */}
              <div className="grid grid-cols-12 gap-4 mb-5">
                <div className="col-span-6 md:col-span-3">
                  <Label>Function owner</Label>
                  <Value>{a.owner_function}</Value>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <Label>Owner</Label>
                  <Value>{a.owner_name}</Value>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Label>Assigned</Label>
                  <Value>{a.assigned_at}</Value>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Label>Next review</Label>
                  <Value>Owner-team cadence</Value>
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end items-start">
                  <StatusPill value={mappedStatus} />
                </div>
              </div>

              {/* Flow: Insight → Recommendation → Follow-up Signal */}
              <div className="grid grid-cols-12 gap-6 pt-5 border-t border-[#F0EBDC]">
                <Step label="Insight" body={a.insight} />
                <Arrow />
                <Step label="Recommendation" body={a.recommendation} highlight />
                <Arrow />
                <Step
                  label="Follow-up signal / Observed impact"
                  body={followUp}
                  muted={a.observed_impact === "—"}
                />
              </div>
            </article>
          );
        })}
        {filtered.length === 0 && <Empty>No actions match this filter.</Empty>}
      </div>
    </div>
  );
}

function Step({ label, body, highlight, muted }) {
  return (
    <div className="col-span-12 md:col-span-4 lg:col-span-3">
      <Label>{label}</Label>
      <p className={`mt-1.5 leading-relaxed text-[14px] ${highlight ? "text-[#0F1825]" : muted ? "text-[#A09680] italic" : "text-[#5C6470]"}`}>{body}</p>
    </div>
  );
}
function Arrow() {
  return (
    <div className="hidden md:flex md:col-span-1 items-center justify-center text-[#A09680]">
      <ArrowRight size={16} />
    </div>
  );
}

/* ======================== EVIDENCE TRACEABILITY ======================== */
// Traceability dimensions per the client-workspace product spec — every
// strategic output is linked to source evidence, expert review, and the
// rationale used to interpret it.
const AUDIT_DIMENSIONS = [
  { key: "Source type",          desc: "Each finding is tagged with the source type it draws on — community post, PRO instrument, advisory transcript, regulatory / HTA record, real-world cohort, or peer-reviewed publication." },
  { key: "Evidence stream",      desc: "Each finding is tagged with the PentaVoice™ stream(s) supporting it (L1 Digital & Social Listening, L2 PROs & Surveys, L3 Patient Advisory, L4 RWD / RWE, L5 Regulatory & HTA)." },
  { key: "Concept / domain",     desc: "Each finding is tagged with the patient-experience concept and the domain it sits in (symptom, functional, QoL, treatment burden, access, caregiver, preference)." },
  { key: "Expert review",        desc: "AI-surfaced signals are reviewed by senior methodologists before they advance to interpretation. Reviewer and review date are recorded." },
  { key: "Confidence class",     desc: "Each finding is classified as Supported, Gap, or Emerging based on convergence across streams. Single-stream findings remain Emerging until additional streams agree." },
  { key: "Strategy implication", desc: "Each finding is paired with the strategic implication used to translate it into Medical Affairs, HEOR, Market Access, Regulatory, Commercial, Patient Engagement, or Leadership outputs." },
];

function TraceabilityTab({ id, isClient }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/console/engagements/${id}/methodology`);
      setData(data);
    })();
  }, [id]);

  if (!data) return <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#5C6470]">Loading…</div>;

  return (
    <div data-testid="traceability-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Evidence Traceability" : "Stage 08 of 08 · Evidence Traceability"}
        title="Rigor on the record."
        lede="Every strategic output is linked to source evidence, expert review, and the rationale used to interpret it."
      />

      <ClientOutput>
        Source traceability, expert-review attestation, and the methodology rationale supporting every finding and deliverable.
      </ClientOutput>

      {/* Traceability dimensions */}
      <section className="mb-10" data-testid="audit-dimensions">
        <SectionTitle>Traceability dimensions</SectionTitle>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {AUDIT_DIMENSIONS.map((d) => (
            <div key={d.key} className="bg-white border border-[#E8E1D2] p-5" data-testid={`audit-${d.key.toLowerCase().replace(/[\s/]+/g, '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={15} className="text-[#C84C21]" />
                <span className="text-[13px] text-[#0F1825] font-medium">{d.key}</span>
              </div>
              <p className="text-[13px] text-[#5C6470] leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11.5px] text-[#A09680] leading-relaxed">
          Internal scoring logic, source weighting, AI prompts, and coding rules are part of the Voice2Evidence methodology and are not surfaced in client-facing outputs.
        </p>
      </section>

      {/* Framework reference — operator view only; client view ends at audit dimensions */}
      {!isClient && (
      <section className="space-y-8" data-testid="traceability-framework-reference">
        <div>
          <SectionTitle>Methodology framework reference</SectionTitle>
          <p className="text-[13.5px] text-[#5C6470] leading-relaxed mt-2 max-w-3xl">
            The frameworks below are referenced where applicable in this engagement to anchor methodology rationale. Their inclusion does not imply external certification.
          </p>
        </div>

        {Object.entries(data.by_framework).map(([fw, items]) => (
          <section key={fw} className="bg-white border border-[#E8E1D2]" data-testid={`fw-${fw.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="px-6 py-4 border-b border-[#E8E1D2] flex items-baseline justify-between">
              <h3 className="font-serif text-xl text-[#0F1825]">{fw}</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5C6470]">{items.length} item{items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {items.map((it, idx) => (
                <div key={it.id} className={`p-5 ${idx < items.length - 1 ? "border-b border-[#F0EBDC]" : ""} ${idx % 2 === 0 ? "lg:border-r lg:border-[#F0EBDC]" : ""}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C84C21]">{it.section}</span>
                    <StatusPill value={it.status} />
                  </div>
                  <p className="text-[13.5px] text-[#5C6470] leading-relaxed">{it.notes}</p>
                </div>
              ))}
            </div>
          </section>
        ))}
      </section>
      )}
    </div>
  );
}


/* ============================================================
 * PATIENT ADVISORY BRIEF (PAB) TAB
 *
 * Renders engagement.pab_questions — the Claude-synthesised live-patient
 * advisory board discussion guide. Grouped by V2E parent theme (A-E),
 * filterable by type (Confirm / Gap-probe / Tension), each question
 * shows the sub-themes it probes as a small mono caption (full
 * traceability back to the triangulation row that produced it).
 *
 * Empty state if triangulation has not yet been re-run since the PAB
 * feature shipped — operator can trigger it from the Triangulation
 * View. PDF export uses PatientAdvisoryBriefReport.
 * ============================================================ */

const PAB_TYPE_PALETTE = {
  "Confirm":   { bg: "#E8F0EA", fg: "#2C5C42", border: "#B6D2BC" },
  "Gap-probe": { bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A" },
  "Tension":   { bg: "#F1ECF4", fg: "#5B3C7A", border: "#CCBADB" },
};
const PAB_TYPES = ["Confirm", "Gap-probe", "Tension"];

function PabTypeChip({ type }) {
  const p = PAB_TYPE_PALETTE[type] || PAB_TYPE_PALETTE["Confirm"];
  return (
    <span
      className="font-mono text-[9.5px] uppercase tracking-[0.18em] border px-2 py-0.5"
      style={{ backgroundColor: p.bg, color: p.fg, borderColor: p.border }}
      data-testid={`pab-type-${type.toLowerCase()}`}
    >
      {type}
    </span>
  );
}

function PatientAdvisoryBriefTab({ id, eng, isClient }) {
  const pabQuestions = useMemo(() => eng?.pab_questions || {}, [eng?.pab_questions]);
  const pabComputedAt = eng?.pab_questions_computed_at || null;
  const [filterType, setFilterType] = useState("ALL");
  const [lastRun, setLastRun] = useState(undefined); // undefined = loading, null = never triangulated

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/console/engagements/${id}/triangulation-runs`);
        if (!cancelled) setLastRun((data?.runs || [])[0] || null);
      } catch { if (!cancelled) setLastRun(null); }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  // V2E_THEMES is defined above (line ~2057). Reuse it directly.
  const populatedThemes = useMemo(() => {
    return V2E_THEMES.filter((t) => (pabQuestions[t.key] || []).length > 0);
  }, [pabQuestions]);

  const totalQuestions = useMemo(
    () => populatedThemes.reduce((acc, t) => acc + (pabQuestions[t.key] || []).length, 0),
    [populatedThemes, pabQuestions]
  );

  // Per-type counts across the whole brief — drives the filter chip counts.
  const typeCounts = useMemo(() => {
    const c = { ALL: totalQuestions };
    PAB_TYPES.forEach((t) => { c[t] = 0; });
    populatedThemes.forEach((t) => {
      (pabQuestions[t.key] || []).forEach((q) => {
        if (PAB_TYPES.includes(q.type)) c[q.type] = (c[q.type] || 0) + 1;
      });
    });
    return c;
  }, [populatedThemes, pabQuestions, totalQuestions]);

  const filteredByTheme = useMemo(() => {
    return populatedThemes.map((t) => ({
      theme: t,
      questions: (pabQuestions[t.key] || []).filter(
        (q) => filterType === "ALL" || q.type === filterType
      ),
    })).filter((g) => g.questions.length > 0);
  }, [populatedThemes, pabQuestions, filterType]);

  const empty = populatedThemes.length === 0;

  return (
    <div data-testid="patient-advisory-brief-tab">
      <TabHeader
        eyebrow={isClient ? "Section · Patient Advisory Brief" : "Stage 07 · Patient Advisory Brief"}
        title="Live patient advisory board — discussion guide."
        lede="Triangulated desk evidence translated into a moderator-ready discussion guide. Every question traces back to a V2E sub-theme finding so the live conversation either confirms a finding, closes a named gap, or resolves a contradiction."
      />

      <ClientOutput>
        A moderator-ready discussion guide for the upcoming live patient advisory board. Three question types — Confirm, Gap-probe, Tension — anchored to the V2E sub-themes triangulated in this engagement.
      </ClientOutput>

      {empty ? (
        <div className="bg-white border border-dashed border-[#E8E1D2] p-8 text-center" data-testid="pab-empty-state">
          <ListChecks size={28} className="text-[#A09680] mx-auto mb-3" />
          {lastRun?.status === "running" ? (
            <>
              <div className="font-serif text-[17px] text-[#0F1825] mb-1.5 inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin text-[#B7791F]" /> Triangulation is running — the brief is being written.
              </div>
              <p className="text-[13.5px] text-[#5C6470] leading-relaxed max-w-xl mx-auto" data-testid="pab-empty-running">
                Started {fmtRunDate(lastRun.triggered_at)} · <RunElapsed since={lastRun.triggered_at} /> elapsed. The Patient Advisory Brief is the last phase (after the per-theme insights), so it appears here once the whole pass completes — typically 5–10 minutes. This page checks automatically.
              </p>
            </>
          ) : lastRun?.status === "failed" ? (
            <>
              <div className="font-serif text-[17px] text-[#0F1825] mb-1.5">The last triangulation run did not finish.</div>
              <p className="text-[13.5px] text-[#C84C21] leading-relaxed max-w-xl mx-auto" data-testid="pab-empty-failed">{lastRun.error || "Triangulation failed."}</p>
              <p className="text-[13.5px] text-[#5C6470] leading-relaxed max-w-xl mx-auto mt-2">
                Open <span className="font-mono text-[12px] text-[#C84C21]">Triangulation View</span> and run triangulation again.
              </p>
            </>
          ) : lastRun?.status === "succeeded" ? (
            <>
              <div className="font-serif text-[17px] text-[#0F1825] mb-1.5">Triangulation finished, but no advisory questions were produced.</div>
              <p className="text-[13.5px] text-[#5C6470] leading-relaxed max-w-xl mx-auto" data-testid="pab-empty-no-questions">
                Run of {fmtRunDate(lastRun.completed_at)} · {lastRun.findings_written || 0} findings · PAB synthesis {lastRun.pab_synth_succeeded || 0}/{lastRun.pab_synth_attempted || 0} themes.
                {(lastRun.pab_synth_attempted || 0) === 0
                  ? " No finding had signals attached, so there was nothing to anchor a patient question to — commit signals in the Source Library first, then re-run triangulation."
                  : " The Claude synthesis step returned nothing for every theme — re-run triangulation from the Triangulation View."}
              </p>
            </>
          ) : (
            <>
              <div className="font-serif text-[17px] text-[#0F1825] mb-1.5">No advisory brief yet for this engagement.</div>
              <p className="text-[13.5px] text-[#5C6470] leading-relaxed max-w-xl mx-auto">
                The PAB is generated as part of the triangulation pass. Open the <span className="font-mono text-[12px] text-[#C84C21]">Triangulation View</span> tab and run triangulation — the brief will populate here once the LLM phase completes.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* How-to-use orientation card */}
          <section className="mt-2 bg-white border-l-2 border-[#C84C21] p-5 mb-8" data-testid="pab-orientation">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-3">How to use this brief</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13.5px] text-[#0F1825] leading-relaxed">
              <div>
                <div className="mb-1.5"><PabTypeChip type="Confirm" /></div>
                <span className="text-[#5C6470]">Validate a strong desk finding with patients in the room.</span>
              </div>
              <div>
                <div className="mb-1.5"><PabTypeChip type="Gap-probe" /></div>
                <span className="text-[#5C6470]">Close a named evidence gap with primary patient input.</span>
              </div>
              <div>
                <div className="mb-1.5"><PabTypeChip type="Tension" /></div>
                <span className="text-[#5C6470]">Surface a trade-off where the evidence is split.</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#F0EBDC] text-[12.5px] text-[#A09680] leading-relaxed">
              Probe captions (e.g. <span className="font-mono text-[#C84C21]">Probes · C2 · D1</span>) trace each question to the V2E sub-theme finding it tests.
            </div>
          </section>

          {/* Filter chips + counts */}
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="pab-filter-strip">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mr-2">Filter</span>
            {["ALL", ...PAB_TYPES].map((t) => {
              const isActive = filterType === t;
              const count = typeCounts[t] || 0;
              return (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`font-mono text-[10.5px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                    isActive
                      ? "bg-[#0F1825] text-white border-[#0F1825]"
                      : "bg-white text-[#0F1825] border-[#E8E1D2] hover:border-[#C84C21]/60"
                  }`}
                  data-testid={`pab-filter-${t.toLowerCase()}`}
                >
                  {t === "ALL" ? "All" : t}
                  <span className={`ml-2 ${isActive ? "text-white/70" : "text-[#A09680]"}`}>{count}</span>
                </button>
              );
            })}
            <div className="ml-auto text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#A09680]">
              {populatedThemes.length} theme{populatedThemes.length === 1 ? "" : "s"} · {totalQuestions} questions
              {pabComputedAt ? ` · synthesised ${pabComputedAt.slice(0, 10)}` : ""}
            </div>
          </div>

          {/* Theme groups */}
          <div className="space-y-7">
            {filteredByTheme.map(({ theme, questions }) => (
              <section
                key={theme.key}
                className="bg-white border border-[#E8E1D2]"
                data-testid={`pab-theme-${theme.key}`}
              >
                <div
                  className="flex items-baseline gap-2 px-5 py-2.5 text-white"
                  style={{ backgroundColor: theme.accent }}
                >
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] font-semibold">{theme.key}</span>
                  <span className="opacity-70">·</span>
                  <span className="text-[13px] font-medium tracking-tight">{theme.label}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
                    {questions.length} question{questions.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ol className="divide-y divide-[#F0EBDC]">
                  {questions.map((q, i) => (
                    <li
                      key={q.id || `${theme.key}-${i}`}
                      className="px-5 py-4 flex gap-4 items-start"
                      data-testid={`pab-question-${theme.key}-${i}`}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] shrink-0 pt-1 w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <PabTypeChip type={q.type} />
                          {(q.probes || []).length > 0 && (
                            <span
                              className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]"
                              data-testid={`pab-probes-${theme.key}-${i}`}
                            >
                              Probes · {q.probes.join(" · ")}
                            </span>
                          )}
                        </div>
                        <p className="text-[14.5px] text-[#0F1825] leading-relaxed">
                          <AutoAbbr text={q.question} />
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
            {filteredByTheme.length === 0 && (
              <Empty>No questions match the current filter.</Empty>
            )}
          </div>

          {/* PDF export */}
          <div className="mt-10 flex flex-wrap gap-3" data-testid="pab-exports">
            <PdfDownloadButton
              label="Download Patient Advisory Brief PDF"
              testid="pab-pdf-download"
              filename={`Voice2Evidence-Patient-Advisory-Brief-${slugify(eng?.brand)}.pdf`}
              buildDoc={() => (
                <PatientAdvisoryBriefReport
                  eng={eng}
                  pabQuestions={pabQuestions}
                  themes={V2E_THEMES}
                />
              )}
            />
          </div>
          <p className="mt-3 text-[11.5px] text-[#A09680] font-mono uppercase tracking-[0.16em]">
            One page per V2E theme — easier to print and hand to the moderator chapter-by-chapter.
          </p>
        </>
      )}
    </div>
  );
}


/* ============================================================
 * KOL ADVISORY BRIEF TAB — pre-launch sibling of the PAB.
 *
 * Same data shape (engagement.kol_questions grouped by V2E theme key), same
 * per-theme accordion structure, but a distinct tag palette and orientation
 * copy tuned for KOL / physician audiences. The tags map to real pharma
 * pre-launch decisions:
 *   · Positioning → launch positioning statement, patient-ID story, HTA narrative
 *   · Adoption   → MSL narrative, publication + congress plan, sales training
 *   · Advocacy   → KOL champion roadmap, real-world evidence plan
 *
 * A generate/refresh button lets the operator regenerate the brief in
 * place without re-running triangulation (backend endpoint:
 * POST /api/console/engagements/{id}/generate-kol-brief).
 * ============================================================ */

const KOL_TYPE_PALETTE = {
  "Positioning": { bg: "#E4EEF7", fg: "#22456A", border: "#B7CFE3" },
  "Adoption":    { bg: "#FDF1E6", fg: "#8C3A12", border: "#E8C19A" },
  "Advocacy":    { bg: "#EDE9F4", fg: "#4E378A", border: "#C9BFE0" },
};
const KOL_TYPES = ["Positioning", "Adoption", "Advocacy"];

function KolTypeChip({ type }) {
  const p = KOL_TYPE_PALETTE[type] || KOL_TYPE_PALETTE["Positioning"];
  return (
    <span
      className="font-mono text-[9.5px] uppercase tracking-[0.18em] border px-2 py-0.5"
      style={{ backgroundColor: p.bg, color: p.fg, borderColor: p.border }}
      data-testid={`kol-type-${type.toLowerCase()}`}
    >
      {type}
    </span>
  );
}

function KolAdvisoryBriefTab({ id, eng, isClient }) {
  const [questionsOverride, setQuestionsOverride] = useState(null);
  const [computedAtOverride, setComputedAtOverride] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [genStatus, setGenStatus] = useState("idle");
  const [genError, setGenError] = useState("");

  const kolQuestions = useMemo(
    () => (questionsOverride !== null ? questionsOverride : (eng?.kol_questions || {})),
    [questionsOverride, eng?.kol_questions],
  );
  const kolComputedAt = computedAtOverride || eng?.kol_questions_computed_at || null;

  const populatedThemes = useMemo(
    () => V2E_THEMES.filter((t) => (kolQuestions[t.key] || []).length > 0),
    [kolQuestions],
  );
  const totalQuestions = useMemo(
    () => populatedThemes.reduce((acc, t) => acc + (kolQuestions[t.key] || []).length, 0),
    [populatedThemes, kolQuestions],
  );
  const typeCounts = useMemo(() => {
    const c = { ALL: totalQuestions };
    KOL_TYPES.forEach((t) => { c[t] = 0; });
    populatedThemes.forEach((t) => {
      (kolQuestions[t.key] || []).forEach((q) => {
        if (KOL_TYPES.includes(q.type)) c[q.type] = (c[q.type] || 0) + 1;
      });
    });
    return c;
  }, [populatedThemes, kolQuestions, totalQuestions]);
  const filteredByTheme = useMemo(() => {
    return populatedThemes.map((t) => ({
      theme: t,
      questions: (kolQuestions[t.key] || []).filter(
        (q) => filterType === "ALL" || q.type === filterType,
      ),
    })).filter((g) => g.questions.length > 0);
  }, [populatedThemes, kolQuestions, filterType]);
  const empty = populatedThemes.length === 0;

  const regenerate = async () => {
    setGenStatus("running");
    setGenError("");
    try {
      await axios.post(`${API}/console/engagements/${id}/generate-kol-brief`);
      const engRes = await axios.get(`${API}/console/engagements/${id}`);
      setQuestionsOverride(engRes.data?.kol_questions || {});
      setComputedAtOverride(engRes.data?.kol_questions_computed_at || null);
      setGenStatus("done");
      setTimeout(() => setGenStatus("idle"), 2500);
    } catch (e) {
      setGenError(e?.response?.data?.detail || e?.message || "Generation failed");
      setGenStatus("error");
    }
  };

  const runLabel =
    genStatus === "running" ? "Generating…" :
    genStatus === "done"    ? "Refreshed"    :
    (empty ? "Generate KOL brief" : "Regenerate brief");

  return (
    <div data-testid="kol-advisory-brief-tab">
      <TabHeader
        eyebrow={isClient ? "Section · KOL Advisory Brief" : "Stage 08 · KOL Advisory Brief"}
        title="Pre-launch KOL advisory board — discussion guide."
        lede="Triangulated desk evidence translated into a moderator-ready discussion guide for a senior-KOL pre-launch board. Every question maps to one of three pharma launch decisions: positioning, adoption, or advocacy."
      />
      <ClientOutput>
        A moderator-ready discussion guide for the upcoming pre-launch KOL advisory board. Three question types — Positioning, Adoption, Advocacy — each feeding a specific launch decision.
      </ClientOutput>
      {!isClient && (
        <div className="mt-2 mb-5 flex items-center gap-3 flex-wrap" data-testid="kol-action-bar">
          <button
            type="button"
            onClick={regenerate}
            disabled={genStatus === "running"}
            className={`inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] px-3.5 py-2 border transition-colors ${
              genStatus === "running"
                ? "bg-white border-[#E8E1D2] text-[#A09680] cursor-not-allowed"
                : "bg-[#0F1825] border-[#0F1825] text-white hover:bg-[#C84C21] hover:border-[#C84C21]"
            }`}
            data-testid="kol-generate-run"
          >
            {genStatus === "running" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {runLabel}
          </button>
          {genStatus === "error" && (
            <span className="text-[12px] text-[#B83A28]" data-testid="kol-generate-error">
              <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
              {genError}
            </span>
          )}
          <span className="ml-auto text-[11.5px] font-mono uppercase tracking-[0.16em] text-[#A09680]">
            Runs on top of already-triangulated findings · ~30-90 seconds
          </span>
        </div>
      )}
      {empty ? (
        <div className="bg-white border border-dashed border-[#E8E1D2] p-8 text-center" data-testid="kol-empty-state">
          <ListChecks size={28} className="text-[#A09680] mx-auto mb-3" />
          <div className="font-serif text-[17px] text-[#0F1825] mb-1.5">No KOL brief yet for this engagement.</div>
          <p className="text-[13.5px] text-[#5C6470] leading-relaxed max-w-xl mx-auto">
            The KOL brief is produced from the triangulated findings.
            {isClient
              ? " The methodology team will generate it as part of the pre-launch cycle."
              : " Click Generate KOL brief above to run the LLM synth against the current findings."}
          </p>
        </div>
      ) : (
        <>
          <section className="bg-white border-l-2 border-[#C84C21] p-5 mb-8" data-testid="kol-orientation">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-3">How to use this brief</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13.5px] text-[#0F1825] leading-relaxed">
              <div>
                <div className="mb-1.5"><KolTypeChip type="Positioning" /></div>
                <span className="text-[#5C6470]">Validate where the asset sits, which patient the KOL reaches for, and how payers may frame the value.</span>
              </div>
              <div>
                <div className="mb-1.5"><KolTypeChip type="Adoption" /></div>
                <span className="text-[#5C6470]">Probe what needs to be true — evidence, operational, competitive — for the KOL&apos;s first prescription.</span>
              </div>
              <div>
                <div className="mb-1.5"><KolTypeChip type="Advocacy" /></div>
                <span className="text-[#5C6470]">Surface what would make the KOL a public champion — data, education, symposium roles.</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-[#F0EBDC] text-[12.5px] text-[#A09680] leading-relaxed">
              Probe captions (e.g. <span className="font-mono text-[#C84C21]">Probes · C2 · D1</span>) trace each question to the V2E sub-theme finding it tests. Anchored in the same evidence base as the Patient Advisory Brief.
            </div>
          </section>
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="kol-filter-strip">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] mr-2">Filter</span>
            {["ALL", ...KOL_TYPES].map((t) => {
              const isActive = filterType === t;
              const count = typeCounts[t] || 0;
              return (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`font-mono text-[10.5px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                    isActive
                      ? "bg-[#0F1825] text-white border-[#0F1825]"
                      : "bg-white text-[#0F1825] border-[#E8E1D2] hover:border-[#C84C21]/60"
                  }`}
                  data-testid={`kol-filter-${t.toLowerCase()}`}
                >
                  {t === "ALL" ? "All" : t}
                  <span className={`ml-2 ${isActive ? "text-white/70" : "text-[#A09680]"}`}>{count}</span>
                </button>
              );
            })}
            <div className="ml-auto text-[11.5px] font-mono uppercase tracking-[0.18em] text-[#A09680]">
              {populatedThemes.length} theme{populatedThemes.length === 1 ? "" : "s"} · {totalQuestions} questions
              {kolComputedAt ? ` · synthesised ${kolComputedAt.slice(0, 10)}` : ""}
            </div>
          </div>
          <div className="space-y-7">
            {filteredByTheme.map(({ theme, questions }) => (
              <section
                key={theme.key}
                className="bg-white border border-[#E8E1D2]"
                data-testid={`kol-theme-${theme.key}`}
              >
                <div
                  className="flex items-baseline gap-2 px-5 py-2.5 text-white"
                  style={{ backgroundColor: theme.accent }}
                >
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] font-semibold">{theme.key}</span>
                  <span className="opacity-70">·</span>
                  <span className="text-[13px] font-medium tracking-tight">{theme.label}</span>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
                    {questions.length} question{questions.length === 1 ? "" : "s"}
                  </span>
                </div>
                <ol className="divide-y divide-[#F0EBDC]">
                  {questions.map((q, i) => (
                    <li
                      key={q.id || `${theme.key}-${i}`}
                      className="px-5 py-4 flex gap-4 items-start"
                      data-testid={`kol-question-${theme.key}-${i}`}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680] shrink-0 pt-1 w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <KolTypeChip type={q.type} />
                          {(q.probes || []).length > 0 && (
                            <span
                              className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]"
                              data-testid={`kol-probes-${theme.key}-${i}`}
                            >
                              Probes · {q.probes.join(" · ")}
                            </span>
                          )}
                        </div>
                        <p className="text-[14.5px] text-[#0F1825] leading-relaxed">
                          <AutoAbbr text={q.question} />
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
            {filteredByTheme.length === 0 && (
              <Empty>No questions match the current filter.</Empty>
            )}
          </div>

          {/* PDF export — mirrors the PAB download so Medical Affairs
              can share and print the full brief chapter-by-chapter. */}
          <div className="mt-10 flex flex-wrap gap-3" data-testid="kol-exports">
            <PdfDownloadButton
              label="Download KOL Advisory Brief PDF"
              testid="kol-pdf-download"
              filename={`Voice2Evidence-KOL-Advisory-Brief-${slugify(eng?.brand)}.pdf`}
              buildDoc={() => (
                <KolAdvisoryBriefReport
                  eng={eng}
                  kolQuestions={kolQuestions}
                  themes={V2E_THEMES}
                />
              )}
            />
          </div>
          <p className="mt-3 text-[11.5px] text-[#A09680] font-mono uppercase tracking-[0.16em]">
            One page per V2E theme — easier to print and hand to the moderator chapter-by-chapter.
          </p>
        </>
      )}
    </div>
  );
}